/**
 * FirmTierRepository — ADR-0047 (firm-only auditor tier), v9.8 Track 6.
 *
 * THE ONLY CODE ALLOWED TO NAME A FIRM TABLE (ADR-0047 R3). The table list is
 * `FIRM_CONTROL_TABLES` + `FIRM_TENANT_TABLES` below; scripts/check-firm-tables.mjs
 * reads those two arrays from this file and fails CI on any other source file
 * that names one of them — in an identifier, a string or a SQL literal.
 *
 * Planes (R7):
 *   - control (D1): the roster — firms, firm staff, firm invitations,
 *     engagement members — the engagement row itself and the per-engagement
 *     hash chain (R4).
 *   - tenant (`tenantDb`, D1 or Postgres per `workspaces.data_plane`): firm
 *     content. Firm-only content columns are envelope-encrypted with a per-firm
 *     key (R6, utils/firm-envelope.ts); the D1 → Postgres workspace migration
 *     copies the ciphertext as-is (`FIRM_TENANT_TABLES`).
 *
 * Three entry points, each with its own guard:
 *   - `FirmTierRepository.open(...)` — the firm tier on one engagement. The
 *     engagement id comes from the request PATH; the guard never trusts
 *     `assessorSession` or `membership.role` (R3) and returns FirmNotFoundError
 *     (→ 404) unless ALL of these hold:
 *       · the caller's users row is active and bound to a WorkOS identity that
 *         equals the session's WorkOS subject (R2: identity, never email);
 *       · the engagement belongs to the workspace resolved from the URL slug and
 *         is a firm-tier engagement;
 *       · the caller has an open `engagement_members` row on it with the same
 *         WorkOS identity, and is still accepted staff of that firm (R1);
 *       · the caller holds NO active membership in the client workspace, no
 *         active org membership in its organization and no membership in any of
 *         that organization's workspaces (R2, read time) — a dual role gets 404;
 *       · now is inside the engagement window (engagement_start → assembly
 *         deadline; partner/manager custody to the retention end — R5, R9).
 *     Then MFA (R2): a fresh firm-tier step-up stamp, else FirmMfaRequiredError
 *     (only ever raised AFTER every check above passed, so it reveals nothing).
 *   - `FirmClientTier.open(...)` — the CLIENT's view of one engagement: active
 *     workspace membership, never a roster identity on it. Reads only the
 *     client-visible tier (PBC, populations/selections once requested, issued
 *     exceptions, report STATUS). "Preview as auditor" renders through here.
 *   - `FirmDirectory` — firm-level actions with no workspace: register a firm
 *     bound to a verified WorkOS organization or a platform-verified domain
 *     (R1), manage firm staff, accept / decline a client's invitation.
 *
 * Independence (R2, ADR-0046 T17): `isRosterIdentity` + `recordIndependenceRejection`
 * back every client-hat insertion point (workspace invitation accept, join
 * request, org membership, advisor seat, assignment). A rejection is appended
 * to the firm chain of every engagement the identity is (or within the
 * lookback was) on.
 *
 * Channels (R8): nothing here writes notifications, alerts, findings, audit
 * events, webhooks, SSE, Vectorize or the research pipeline. The only client
 * chain write is the engagement-start marker the client's own invitation
 * produces (engagement id, firm name, period — no firm content, no roster).
 */
import { canonicalJson, generateRandomId, sha256Hex } from '../utils/audit-crypto.js';
import { decodeKek, mintWrappedFirmKey, unwrapFirmKey } from '../utils/firm-envelope.js';
import { compareKeys, getPopulationItems, parsePopulationCsv, populationContentSha256 } from './population-service.js';
import { SAMPLE_METHOD, generateSeed, selectSampleKeys } from './sampling-service.js';
import { subserviceCoverage } from './system-description-service.js';
import { TEMPLATE_REVIEW_STATUS, TEMPLATE_VERSION, bridgeLetter, managementAssertionType2, representationLetter } from '../data/aicpa-report-templates.js';
import { diffLines } from '../utils/text-diff.js';
import { buildStoreZip } from '../utils/zip-store.js';
import { recordSodEvent } from './sod-service.js';
import { getCertificationCycle } from './program-cycle-service.js';
import { resolveRuleSetForPeriod } from './program-rule-set-service.js';
import { ASSESSOR_FIRM_TYPES, ASSESSOR_FIRM_TYPE_SPECS, INDEPENDENCE_ATTESTATION_STATEMENT, assessorTypesForProgram, isAssessorFirmType, previewFamilyAllowed, programAllowsAssessorType } from './assessor-firm-types.js';
import { getProgramManifest, programsForAssessorType } from '../programs/manifests.js';
import { lookupTimeframe } from './certification-service.js';
import { C3paoError, assertRefInPeriodScope } from './c3pao-service.js';
// ---- the firm tables (R3 lint source of truth) ------------------------------------
/** Control-plane (D1) firm tables. */
export const FIRM_CONTROL_TABLES = [
    'audit_firms',
    'audit_firm_staff',
    'firm_engagement_invitations',
    'engagement_members',
    'firm_chain_events',
    'workpaper_templates',
    // 336 / ADR-0067 §6 — advisory engagements (the independence check reads them)
    'advisory_engagements',
];
/**
 * Tenant-plane firm tables. Every one carries `workspace_id`, so the
 * D1 → Postgres workspace migration (R7) copies them, ciphertext as-is.
 */
export const FIRM_TENANT_TABLES = [
    // 241 — persisted PBC (client-visible tier, still repository-only)
    'pbc_requests',
    'pbc_evidence_versions',
    'pbc_messages',
    // 242 — population provenance (client-visible), reconciliation + samples (firm-only)
    'population_provenance',
    'reconciliation_signoffs',
    'sample_lineage',
    // 243 — firm-only working papers (parents before children: the copy order)
    'workpaper_tests',
    'workpaper_attribute_results',
    'workpaper_review_notes',
    'workpaper_signoffs',
    'audit_exceptions',
    'audit_exception_versions',
    'report_status_events',
    // 244 — management responses (client-visible), report documents, export history
    'management_responses',
    'report_documents',
    'firm_exports',
    // 336 / ADR-0067 §7 — assessor RAR / SAP (lead-signed, client read-only)
    'assessor_report_artifacts',
    // 538 / 535b / ADR-0081 §3 (V106-6-5 T6-L3) — CB audit sign-offs, then the decisions that rest on them
    'certification_audit_signoffs',
    'certification_decisions',
];
export const FIRM_TABLES = [...FIRM_CONTROL_TABLES, ...FIRM_TENANT_TABLES];
export const FIRM_ROLES = ['partner', 'manager', 'senior', 'staff'];
/** Firm-tier MFA freshness — matches the advisor step-up policy (12 h). */
export const FIRM_MFA_FRESH_MS = 12 * 60 * 60 * 1000;
/**
 * T6-S1: `POST /firm/session/enter` stamps only when the session's WorkOS
 * authentication (`auth_time`) is at most this old — i.e. the person just
 * completed a fresh sign-in (`prompt=login`). Older or unknown → step up.
 */
export const FIRM_STEP_UP_MAX_AGE_MS = 10 * 60 * 1000;
/** Clock skew tolerated on an `auth_time` from the future. */
const FIRM_STEP_UP_SKEW_MS = 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * V98-7-3 M2: a firm name's comparison key — case, punctuation, spacing,
 * "&"/"and" and a trailing legal suffix don't make a different firm.
 * "Ledger & Co LLP" ≡ "ledger and co., llp" ≡ "LEDGER AND CO".
 */
export function firmNameKey(name) {
    const words = String(name ?? '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean);
    while (words.length > 1 && ['llp', 'llc', 'pllc', 'inc', 'ltd', 'plc', 'pc', 'cpa', 'cpas', 'lp'].includes(words[words.length - 1]))
        words.pop();
    return words.join(' ');
}
export const REPORT_STATUSES = ['fieldwork', 'draft_sent', 'issued'];
const REPORT_STATUS_LABEL = { fieldwork: 'Fieldwork', draft_sent: 'Draft sent', issued: 'Issued' };
export const SOC_OPINION_TYPES = ['unmodified', 'qualified', 'adverse', 'disclaimer'];
export const INDEPENDENCE_LABEL = {
    independent: 'Independent source',
    adjusted_not_independent: 'Adjusted · not independent',
    client_prepared: 'Client-prepared · not independent',
};
/** The independent-source rule: anything adjusted, or prepared by hand, is not independent. */
export function independenceOf(input) {
    if (input.adjusted)
        return 'adjusted_not_independent';
    if (input.extraction_method === 'client_prepared')
        return 'client_prepared';
    return 'independent';
}
export const UNAUDITED_LABEL = 'Unaudited · not covered by the opinion';
export const CLIENT_REPORT_DOCUMENT_KINDS = ['management_assertion', 'representation_letter', 'bridge_letter'];
/**
 * V98-7-3 L9: the manifest seal is an HMAC by the GAC Pro signer service under
 * the CLIENT WORKSPACE's signing key (the same key family as its packages), so
 * it is domain-separated — the signed bytes are `<EXPORT_SIGN_DOMAIN>
` +
 * the canonical manifest — and a manifest seal can never be replayed as (or
 * forged from) any other artifact signed with that key. The label says so.
 */
export const EXPORT_SIGN_DOMAIN = 'gacpro.firm-export-manifest.v1';
export const EXPORT_SIGNER_LABEL = 'Integrity seal by the GAC Pro signer service: HMAC-SHA256 under the client workspace’s signing key, domain-separated (gacpro.firm-export-manifest.v1). Not a signature by the audit firm or by the client.';
// ---- errors -----------------------------------------------------------------------
/** Every guard miss. Routes map it to 404 and never say which check failed. */
export class FirmNotFoundError extends Error {
    constructor() {
        super('Not found');
        this.name = 'FirmNotFoundError';
    }
}
/** A proven roster member whose firm-tier step-up is missing or stale. */
export class FirmMfaRequiredError extends Error {
    constructor() {
        super('Firm-tier access needs a fresh MFA step-up.');
        this.name = 'FirmMfaRequiredError';
    }
}
/**
 * T6-S1: a proven roster member asked to stamp the step-up, but the session's
 * WorkOS authentication is missing, older than FIRM_STEP_UP_MAX_AGE_MS, or an
 * impersonation. The route answers 401 `step_up_required` with a sign-in URL.
 */
export class FirmStepUpRequiredError extends Error {
    constructor() {
        super('Sign in again to open the firm working papers.');
        this.name = 'FirmStepUpRequiredError';
    }
}
/** A proven member (or client member) whose role or phase does not allow the action. */
export class FirmForbiddenError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'FirmForbiddenError';
    }
}
export class FirmValidationError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'FirmValidationError';
    }
}
export class FirmStateError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'FirmStateError';
    }
}
export class AssessorIndependenceError extends Error {
    conflicts;
    lookbackMonths;
    lookbackBasis;
    code = 'independence_refused';
    constructor(conflicts, lookbackMonths, lookbackBasis = 'unknown') {
        super(`Independence: ${conflicts.length} conflict(s) inside the ${lookbackMonths}-month lookback — ${conflicts.slice(0, 5).map((c) => `${c.person} (${c.source.replace('_', ' ')}, ${c.overlap_from.slice(0, 10)} → ${c.overlap_to.slice(0, 10)})`).join('; ')}.`);
        this.conflicts = conflicts;
        this.lookbackMonths = lookbackMonths;
        this.lookbackBasis = lookbackBasis;
        this.name = 'AssessorIndependenceError';
    }
}
/** A client-hat insertion refused because the identity is (or was) on a roster. */
export class IndependenceRejectedError extends Error {
    point;
    constructor(point) {
        super('An audit-firm roster identity can’t hold a client role (ADR-0047).');
        this.point = point;
        this.name = 'IndependenceRejectedError';
    }
}
// ---- pure policy ------------------------------------------------------------------
function endOfDay(date) {
    // A bare YYYY-MM-DD means "through the end of that UTC day".
    return /^\d{4}-\d{2}-\d{2}$/.test(date) ? Date.parse(`${date}T23:59:59.999Z`) : Date.parse(date);
}
function startOfDay(date) {
    return /^\d{4}-\d{2}-\d{2}$/.test(date) ? Date.parse(`${date}T00:00:00.000Z`) : Date.parse(date);
}
/** The engagement scope: period + subsequent events to the report date + the assembly window (R5). */
export function firmWindow(e) {
    const reportDate = e.report_date ?? e.engagement_end;
    const assembly = endOfDay(reportDate) + e.assembly_window_days * DAY_MS;
    const retention = assembly + e.firm_retention_days * DAY_MS;
    return {
        starts_at: new Date(startOfDay(e.engagement_start)).toISOString(),
        report_date: reportDate,
        assembly_deadline: new Date(assembly).toISOString(),
        retention_end: new Date(retention).toISOString(),
    };
}
/**
 * Phase for a role at `now`, or null when the caller has no firm-tier access.
 *   fieldwork  start ≤ now ≤ assembly deadline, report not yet released
 *   assembly   report released, now ≤ assembly deadline (read-only + sign-offs + export)
 *   custody    after the deadline, or after the client revoked (R5): partner /
 *              manager only, read + export, until the retention end
 */
export function firmPhase(e, role, now) {
    const w = firmWindow(e);
    const t = now.getTime();
    if (t < Date.parse(w.starts_at))
        return null;
    if (t > Date.parse(w.retention_end))
        return null;
    const lead = role === 'partner' || role === 'manager';
    if (e.revoked_at || t > Date.parse(w.assembly_deadline))
        return lead ? 'custody' : null;
    return e.report_released_at ? 'assembly' : 'fieldwork';
}
/** When a roster identity stops counting for independence (R2 / R5 lookback). */
export function rosterIdentityUntil(e, member) {
    const w = firmWindow(e);
    const left = member.removed_at ? Math.min(Date.parse(member.removed_at), Date.parse(w.assembly_deadline)) : Date.parse(w.assembly_deadline);
    const until = new Date(left);
    until.setUTCMonth(until.getUTCMonth() + e.independence_lookback_months);
    return until.getTime();
}
export function isLeadRole(role) {
    return role === 'partner' || role === 'manager';
}
// ---- shared helpers ---------------------------------------------------------------
const ENGAGEMENT_COLS = `engagement_id, workspace_id, firm_id, audit_period_id, assessor_firm, engagement_start, engagement_end,
  report_date, report_released_at, revoked_at, assembly_window_days, independence_lookback_months, firm_retention_days, firm_type, assessor_firm_type`;
async function loadEngagement(control, workspaceId, engagementId) {
    const r = await control.query(`SELECT ${ENGAGEMENT_COLS} FROM assessor_engagements
      WHERE engagement_id = ? AND workspace_id = ? AND firm_id IS NOT NULL LIMIT 1`, [engagementId, workspaceId]);
    const e = r.results[0];
    if (!e)
        return null;
    return { ...e, assembly_window_days: Number(e.assembly_window_days), independence_lookback_months: Number(e.independence_lookback_months), firm_retention_days: Number(e.firm_retention_days) };
}
async function loadUser(control, userId) {
    const r = await control.query(`SELECT user_id, email, workos_user_id, is_active FROM users WHERE user_id = ? LIMIT 1`, [userId]);
    return r.results[0] ?? null;
}
/**
 * R2 read-time check: does this identity hold any client hat in the
 * workspace or its organization? (Active workspace membership, active org
 * membership, or membership in any workspace of the same organization.)
 */
async function holdsClientHat(control, workspaceId, userId) {
    const direct = await control.query(`SELECT 1 AS x FROM memberships WHERE workspace_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [workspaceId, userId]);
    if (direct.results.length)
        return true;
    const org = await control.query(`SELECT organization_id FROM workspaces WHERE workspace_id = ? LIMIT 1`, [workspaceId]);
    const orgId = org.results[0]?.organization_id;
    if (!orgId)
        return false;
    const om = await control.query(`SELECT 1 AS x FROM org_memberships WHERE organization_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [orgId, userId]);
    if (om.results.length)
        return true;
    const sib = await control.query(`SELECT 1 AS x FROM memberships m JOIN workspaces w ON w.workspace_id = m.workspace_id
      WHERE w.organization_id = ? AND m.user_id = ? AND m.removed_at IS NULL LIMIT 1`, [orgId, userId]);
    return sib.results.length > 0;
}
async function loadFirmKey(control, kek, firmId) {
    const r = await control.query(`SELECT key_wrapped FROM audit_firms WHERE firm_id = ? LIMIT 1`, [firmId]);
    const row = r.results[0];
    if (!row)
        throw new FirmNotFoundError();
    return unwrapFirmKey(decodeKek(kek), firmId, row.key_wrapped);
}
function cleanText(v, field, min, max) {
    const s = typeof v === 'string' ? v.trim() : '';
    if (s.length < min || s.length > max)
        throw new FirmValidationError(`invalid_${field}`, `${field} must be ${min}–${max} characters.`);
    return s;
}
function isoDate(v, field) {
    if (typeof v !== 'string' || Number.isNaN(Date.parse(v)))
        throw new FirmValidationError(`invalid_${field}`, `${field} must be an ISO date.`);
    return v;
}
/**
 * V106-1-7 / ADR-0078 §6: the latest `issued` report status of any engagement on a SOC 2
 * period of this workspace (the client-visible fields only: event id, opinion type, report
 * date). Used by the SOC 2 report record, which copies the firm's opinion instead of letting
 * the customer enter one. The only read of report_status_events outside the repository
 * class, kept here for ADR-0047 R3.
 */
export async function issuedReportForPeriod(tenant, control, workspaceId, periodId) {
    const engagements = (await control.query(`SELECT engagement_id FROM assessor_engagements WHERE workspace_id = ? AND audit_period_id = ?`, [workspaceId, periodId])).results.map((e) => e.engagement_id);
    let best = null;
    for (const id of engagements) {
        const ev = (await tenant.query(`SELECT status_event_id, opinion_type, report_date, set_at, status FROM report_status_events
        WHERE workspace_id = ? AND engagement_id = ? ORDER BY set_at DESC, status_event_id DESC LIMIT 1`, [workspaceId, id])).results[0];
        if (ev?.status === 'issued' && (!best || ev.set_at > best.set_at))
            best = ev;
    }
    return best ? { status_event_id: best.status_event_id, opinion_type: best.opinion_type, report_date: best.report_date } : null;
}
async function loadReportStatus(tenant, engagementId) {
    const r = await tenant.query(`SELECT status, set_at, opinion_type, report_date FROM report_status_events WHERE engagement_id = ? ORDER BY set_at DESC, status_event_id DESC LIMIT 1`, [engagementId]);
    const row = r.results[0];
    const status = row?.status ?? 'fieldwork';
    const issued = status === 'issued';
    return {
        status, label: REPORT_STATUS_LABEL[status], since: row?.set_at ?? null,
        opinion_type: issued ? row?.opinion_type ?? null : null,
        report_date: issued ? row?.report_date ?? null : null,
    };
}
/**
 * T6-S4: display names for user ids (control plane). An id with no users row
 * renders as "Former member" — the UI never shows a raw id.
 */
export const FORMER_MEMBER = 'Former member';
async function displayNames(control, ids) {
    const uniq = [...new Set(ids.filter((x) => !!x))];
    const map = new Map();
    if (control && uniq.length) {
        for (let i = 0; i < uniq.length; i += 50) {
            const chunk = uniq.slice(i, i + 50);
            // d1-in-list: bounded — batches of 50, no other parameters
            const r = await control.query(`SELECT user_id, display_name, email FROM users WHERE user_id IN (${chunk.map(() => '?').join(', ')})`, chunk);
            for (const u of r.results)
                map.set(u.user_id, (u.display_name ?? '').trim() || (u.email ?? '').split('@')[0] || FORMER_MEMBER);
        }
    }
    return (id) => (id ? map.get(id) ?? FORMER_MEMBER : FORMER_MEMBER);
}
/**
 * PBC requests with versions and threads. `side` decides who is named: the
 * client sees the FIRM for firm-authored rows, never a roster person.
 */
async function loadPbc(tenant, engagementId, side, firmName, requestId, control) {
    const reqs = await tenant.query(`SELECT request_id, ref, title, detail, control_ref, owner_user_id, status, due_at, requested_by, requested_at, latest_version,
            returned_at, return_reason, accepted_at
       FROM pbc_requests WHERE engagement_id = ? ${requestId ? 'AND request_id = ?' : ''} ORDER BY ref ASC`, requestId ? [engagementId, requestId] : [engagementId]);
    const out = [];
    for (const r of reqs.results) {
        const versions = await tenant.query(`SELECT version, items_json, items_sha256, backfill_flag, note, submitted_at, submitted_by FROM pbc_evidence_versions WHERE request_id = ? ORDER BY version ASC`, [r.request_id]);
        const msgs = await tenant.query(`SELECT message_id, author_user_id, author_side, body, posted_at FROM pbc_messages WHERE request_id = ? ORDER BY posted_at ASC, message_id ASC`, [r.request_id]);
        const name = await displayNames(control, [r.owner_user_id, r.requested_by, ...versions.results.map((v) => v.submitted_by), ...msgs.results.map((m) => m.author_user_id)]);
        out.push({
            request_id: r.request_id, ref: r.ref, title: r.title, detail: r.detail, control_ref: r.control_ref, owner_user_id: r.owner_user_id,
            owner_name: r.owner_user_id ? name(r.owner_user_id) : null,
            status: r.status, due_at: r.due_at, requested_at: r.requested_at, requested_by: side === 'firm' ? r.requested_by : firmName,
            requested_by_name: side === 'firm' ? name(r.requested_by) : firmName,
            return_reason: r.return_reason, returned_at: r.returned_at, accepted_at: r.accepted_at,
            versions: versions.results.map((v) => ({
                version: Number(v.version), items: JSON.parse(v.items_json), items_sha256: v.items_sha256,
                backfill_flag: Number(v.backfill_flag) === 1, note: v.note, submitted_at: v.submitted_at, submitted_by: v.submitted_by, submitted_by_name: name(v.submitted_by),
            })),
            thread: msgs.results.map((m) => ({
                message_id: m.message_id, author_side: m.author_side, body: m.body, posted_at: m.posted_at,
                author: m.author_side === 'firm' && side === 'client' ? firmName : name(m.author_user_id),
            })),
        });
    }
    return out;
}
async function loadPbcRow(tenant, engagementId, requestId) {
    const r = await tenant.query(`SELECT * FROM pbc_requests WHERE request_id = ? AND engagement_id = ? LIMIT 1`, [requestId, engagementId]);
    if (!r.results[0])
        throw new FirmNotFoundError();
    return { ...r.results[0], latest_version: Number(r.results[0].latest_version) };
}
async function insertPbcMessage(tenant, ctx, requestId, side, userId, body, at) {
    const text = cleanText(body, 'body', 1, 5000);
    const id = generateRandomId('pbm');
    await tenant.execute(`INSERT INTO pbc_messages (message_id, workspace_id, engagement_id, firm_id, request_id, author_user_id, author_side, body, posted_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`, [id, ctx.workspace_id, ctx.engagement_id, ctx.firm_id, requestId, userId, side, text, at]);
    return id;
}
async function engagementPopulations(tenant, workspaceId, periodId) {
    if (!periodId)
        return [];
    const r = await tenant.query(`SELECT population_id, kind, item_count, content_sha256 FROM populations WHERE workspace_id = ? AND period_id = ? ORDER BY created_at ASC, population_id ASC`, [workspaceId, periodId]);
    return r.results.map((p) => ({ ...p, item_count: Number(p.item_count) }));
}
async function latestProvenance(tenant, engagementId, populationId) {
    const r = await tenant.query(`SELECT provenance_id, source_system, extraction_method, query_or_report, extracted_by, extracted_at, adjusted, adjustment_note, independence, recorded_at
       FROM population_provenance WHERE engagement_id = ? AND population_id = ? ORDER BY recorded_at DESC, provenance_id DESC LIMIT 1`, [engagementId, populationId]);
    const p = r.results[0];
    return p ? { ...p, adjusted: Number(p.adjusted) === 1, independence_label: INDEPENDENCE_LABEL[p.independence] } : null;
}
function toSampleView(r) {
    return {
        lineage_id: r.lineage_id, parent_lineage_id: r.parent_lineage_id, kind: r.kind, method: r.method, seed: r.seed, sample_size: Number(r.sample_size),
        selected_keys: JSON.parse(r.selected_keys_json), rationale: r.rationale, reconciliation_id: r.reconciliation_id,
        created_by: r.created_by, created_at: r.created_at, requested_at: r.requested_at,
    };
}
async function clientPopulations(tenant, e) {
    const out = [];
    for (const p of await engagementPopulations(tenant, e.workspace_id, e.audit_period_id)) {
        const sel = await tenant.query(`SELECT lineage_id, requested_at, selected_keys_json FROM sample_lineage WHERE engagement_id = ? AND population_id = ? AND requested_at IS NOT NULL ORDER BY requested_at ASC`, [e.engagement_id, p.population_id]);
        out.push({
            population_id: p.population_id, kind: p.kind, item_count: p.item_count,
            provenance: await latestProvenance(tenant, e.engagement_id, p.population_id),
            requested_selections: sel.results.map((s) => ({ selection_id: s.lineage_id, requested_at: s.requested_at, item_keys: JSON.parse(s.selected_keys_json) })),
        });
    }
    return out;
}
/** T6-S2: a control-total "source" that is just the population's own count. */
const ECHOED_SOURCE = /^(the\s+)?(population|population\s+count|item\s*count|count|this\s+(list|population)|same|n\/?a|none|-+)\.?$/i;
/**
 * The sealed reconciliation memo: v2 is JSON `{ memo, control_total_source,
 * source_total, difference_explanation }` (T6-S2); a pre-T6-S2 memo is plain
 * text. `reconciledTotal` is the column value (always the item count).
 */
function reconciliationFigures(reconciledTotal, raw) {
    if (raw.startsWith('{')) {
        try {
            const o = JSON.parse(raw);
            if (o && o.v === 2) {
                const sourceTotal = typeof o.source_total === 'number' && Number.isInteger(o.source_total) ? o.source_total : reconciledTotal;
                return {
                    control_total: sourceTotal,
                    agrees: sourceTotal === reconciledTotal,
                    memo: typeof o.memo === 'string' ? o.memo : '',
                    control_total_source: typeof o.control_total_source === 'string' ? o.control_total_source : null,
                    difference_explanation: typeof o.difference_explanation === 'string' ? o.difference_explanation : null,
                };
            }
        }
        catch {
            /* plain text */
        }
    }
    return { control_total: reconciledTotal, agrees: true, memo: raw, control_total_source: null, difference_explanation: null };
}
function cleanAttributes(raw) {
    if (!Array.isArray(raw) || raw.length === 0 || raw.length > 30)
        throw new FirmValidationError('invalid_attributes', 'A template has 1–30 attributes.');
    const out = [];
    for (const a of raw) {
        const key = String(a?.key ?? '').trim();
        if (!/^[a-z][a-z0-9_]{0,63}$/.test(key))
            throw new FirmValidationError('invalid_attributes', 'Attribute keys are lower-case letters, digits and underscores.');
        if (out.some((x) => x.key === key))
            throw new FirmValidationError('invalid_attributes', `Attribute "${key}" is repeated.`);
        out.push({ key, label: cleanText(a?.label, 'label', 2, 300), guidance: a?.guidance ? cleanText(a.guidance, 'guidance', 1, 2000) : null });
    }
    return out;
}
async function currentSystemDescription(tenant, workspaceId, periodId) {
    if (!periodId)
        return null;
    const r = await tenant.query(`SELECT description_id, version FROM system_descriptions WHERE workspace_id = ? AND period_id = ? AND status = 'approved' ORDER BY version DESC LIMIT 1`, [workspaceId, periodId]);
    return r.results[0] ? { description_id: r.results[0].description_id, version: Number(r.results[0].version) } : null;
}
async function activeResponse(tenant, exceptionId) {
    const r = (await tenant.query(`SELECT response_id, exception_version, system_description_id, system_description_version, body, authored_by, authored_at
       FROM management_responses WHERE exception_id = ? AND superseded_at IS NULL ORDER BY authored_at DESC LIMIT 1`, [exceptionId])).results[0];
    if (!r)
        return null;
    return {
        ...r, exception_version: Number(r.exception_version), system_description_version: r.system_description_version === null ? null : Number(r.system_description_version),
        unaudited: true, unaudited_label: UNAUDITED_LABEL,
    };
}
function responseMismatch(controlRef, issuedVersion, resp, sd) {
    if (!resp)
        return [];
    const out = [];
    if (resp.exception_version !== issuedVersion)
        out.push(`${controlRef}: the response answers v${resp.exception_version}; the exception is v${issuedVersion}`);
    if (sd && resp.system_description_version !== sd.version) {
        out.push(`${controlRef}: the response is bound to system description ${resp.system_description_version === null ? '(none)' : `v${resp.system_description_version}`}; the current one is v${sd.version}`);
    }
    return out;
}
/** Section 4 locks while any issued exception's response is bound to a stale version. */
async function section4State(tenant, e) {
    const sd = await currentSystemDescription(tenant, e.workspace_id, e.audit_period_id);
    const ex = await tenant.query(`SELECT exception_id, control_ref, issued_version FROM audit_exceptions WHERE engagement_id = ? AND status = 'issued' ORDER BY control_ref`, [e.engagement_id]);
    const reasons = [];
    const items = [];
    for (const x of ex.results) {
        const resp = await activeResponse(tenant, x.exception_id);
        const issued = Number(x.issued_version);
        for (const reason of responseMismatch(x.control_ref, issued, resp, sd)) {
            reasons.push(reason);
            items.push({
                exception_id: x.exception_id, control_ref: x.control_ref, issued_version: issued, response_version: resp?.exception_version ?? null,
                kind: resp && resp.exception_version !== issued ? 'response_stale' : 'system_description_stale', reason,
            });
        }
    }
    return { locked: reasons.length > 0, reasons, items, current_system_description: sd };
}
/**
 * V115-2-7 (ADR-0109): the monitoring retention job keeps every check a client submitted to an auditor
 * (PBC items `{kind:'control_check', ref_id}`; firm export zips copy them). This is its door to the PBC
 * versions (ADR-0047 R3). Workspace-scoped.
 */
export const FIRM_RETENTION_PIN_SOURCES = [
    { table: 'pbc_evidence_versions', column: 'items_json', target: 'control_checks', how: 'json', note: "a PBC submission to an auditor ({kind:'control_check', ref_id}); firm_exports zips copy these" },
];
export async function pbcSubmittedCheckIds(tenant, workspaceId) {
    const out = new Set();
    const rows = (await tenant.query(`SELECT items_json FROM pbc_evidence_versions WHERE workspace_id = ?`, [workspaceId])).results;
    for (const r of rows) {
        let items;
        try {
            items = JSON.parse(r.items_json);
        }
        catch {
            continue;
        }
        for (const it of Array.isArray(items) ? items : []) {
            const x = it;
            if (x?.kind === 'control_check' && typeof x.ref_id === 'string')
                out.add(x.ref_id);
        }
    }
    return out;
}
export async function firmPeriodSummary(tenant, workspaceId, periodId) {
    if (!periodId)
        return null;
    const r = (await tenant.query(`SELECT p.period_id, p.period_start, p.period_end, p.report_type, cp.program
       FROM audit_periods p LEFT JOIN certification_profiles cp ON cp.profile_id = p.profile_id
      WHERE p.period_id = ? AND p.workspace_id = ? LIMIT 1`, [periodId, workspaceId])).results[0];
    return r ? { period_id: r.period_id, start: r.period_start.slice(0, 10), end: r.period_end.slice(0, 10), report_type: r.report_type, program: r.program } : null;
}
async function clientExceptions(tenant, e, key) {
    const sd = await currentSystemDescription(tenant, e.workspace_id, e.audit_period_id);
    const ex = await tenant.query(`SELECT exception_id, control_ref, issued_version FROM audit_exceptions WHERE engagement_id = ? AND status = 'issued' ORDER BY control_ref, exception_id`, [e.engagement_id]);
    const out = [];
    for (const x of ex.results) {
        const v = (await tenant.query(`SELECT wording_enc, issued_at FROM audit_exception_versions WHERE exception_id = ? AND version = ? AND issued_at IS NOT NULL LIMIT 1`, [x.exception_id, x.issued_version])).results[0];
        if (!v)
            continue;
        const k = await key();
        const response = await activeResponse(tenant, x.exception_id);
        out.push({
            exception_id: x.exception_id, control_ref: x.control_ref, version: Number(x.issued_version), issued_at: v.issued_at,
            wording: (await k.openField([e.engagement_id, 'exception', x.exception_id, `wording:v${x.issued_version}`], v.wording_enc)) ?? '',
            response, needs_response_update: responseMismatch(x.control_ref, Number(x.issued_version), response, sd).length > 0,
        });
    }
    return out;
}
async function periodWindow(tenant, workspaceId, periodId) {
    if (!periodId)
        return null;
    const r = (await tenant.query(`SELECT period_start, period_end FROM audit_periods WHERE period_id = ? AND workspace_id = ? LIMIT 1`, [periodId, workspaceId])).results[0];
    return r ? { start: r.period_start.slice(0, 10), end: r.period_end.slice(0, 10) } : null;
}
function toReportDoc(r, body, reportDate) {
    return {
        doc_id: r.doc_id, kind: r.kind, version: Number(r.version), status: r.status, body, template_version: r.template_version,
        template_review_status: r.template_version === TEMPLATE_VERSION ? TEMPLATE_REVIEW_STATUS : null,
        document_date: r.document_date, covers_from: r.covers_from, covers_through: r.covers_through, system_description_id: r.system_description_id,
        system_description_version: r.system_description_version === null ? null : Number(r.system_description_version), return_comment: r.return_comment,
        stale: r.kind === 'representation_letter' && !!reportDate && r.document_date !== reportDate, created_by: r.created_by, created_at: r.created_at, decided_at: r.decided_at,
    };
}
/** Client-visible report documents: every kind except the opinion draft — never the opinion. */
async function clientReportDocuments(tenant, e) {
    const r = await tenant.query(`SELECT * FROM report_documents WHERE engagement_id = ? AND kind <> 'opinion_draft' ORDER BY kind, version`, [e.engagement_id]);
    return r.results.map((d) => toReportDoc(d, d.body ?? '', e.report_date));
}
async function nextDocVersion(tenant, engagementId, kind) {
    const r = await tenant.query(`SELECT MAX(version) AS v FROM report_documents WHERE engagement_id = ? AND kind = ?`, [engagementId, kind]);
    return Number(r.results[0]?.v ?? 0) + 1;
}
function addDay(d) {
    return new Date(Date.parse(`${d.slice(0, 10)}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
}
const EXPORT_COLS = `export_id, as_of, includes_client_tier, file_count, size_bytes, zip_sha256, manifest_sha256, signing_key_id, signer_label, created_by, created_at`;
function toExportView(r) {
    return { ...r, includes_client_tier: Number(r.includes_client_tier) === 1, file_count: Number(r.file_count), size_bytes: Number(r.size_bytes) };
}
/** The firm-only R2 prefix (R8). Client routes never build keys under it. */
export function firmObjectKey(firmId, engagementId, exportId) {
    return `firm/${firmId}/${engagementId}/exports/${exportId}.zip`;
}
function chainPreimage(e) {
    return canonicalJson({
        engagement_id: e.engagement_id, firm_id: e.firm_id, seq: e.seq, prev_hash: e.prev_hash, action: e.action, tier: e.tier,
        actor_user_id: e.actor_user_id, subject_kind: e.subject_kind, subject_id: e.subject_id, detail_json: e.detail_json,
        content_enc: e.content_enc, at: e.at,
    });
}
/** Append one event to an engagement's chain (control plane). Retries a lost seq race. */
export async function appendFirmChain(control, input) {
    for (let attempt = 0; attempt < 4; attempt++) {
        const last = await control.query(`SELECT seq, event_hash FROM firm_chain_events WHERE engagement_id = ? ORDER BY seq DESC LIMIT 1`, [input.engagement_id]);
        const prev = last.results[0];
        const base = {
            engagement_id: input.engagement_id, firm_id: input.firm_id, seq: prev ? Number(prev.seq) + 1 : 1,
            prev_hash: prev?.event_hash ?? '', action: input.action, tier: input.tier, actor_user_id: input.actor_user_id,
            subject_kind: input.subject_kind ?? null, subject_id: input.subject_id ?? null,
            detail_json: canonicalJson(input.detail ?? {}), content_enc: input.content_enc ?? null, at: input.at,
        };
        const event = { ...base, event_id: generateRandomId('fce'), event_hash: await sha256Hex(chainPreimage(base)) };
        try {
            await control.execute(`INSERT INTO firm_chain_events (event_id, engagement_id, firm_id, seq, prev_hash, event_hash, action, tier, actor_user_id,
           subject_kind, subject_id, detail_json, content_enc, at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [event.event_id, event.engagement_id, event.firm_id, event.seq, event.prev_hash, event.event_hash, event.action, event.tier,
                event.actor_user_id, event.subject_kind, event.subject_id, event.detail_json, event.content_enc, event.at]);
            return event;
        }
        catch (err) {
            if (attempt === 3 || !/UNIQUE|unique|duplicate/.test(String(err?.message ?? err)))
                throw err;
        }
    }
    throw new FirmStateError('chain_contended', 'The firm chain is busy; try again.');
}
/** Recompute every hash in an engagement's chain. */
export async function verifyFirmChain(control, engagementId) {
    const r = await control.query(`SELECT * FROM firm_chain_events WHERE engagement_id = ? ORDER BY seq ASC`, [engagementId]);
    const gaps = [];
    let prevHash = '';
    let expected = 1;
    let verified = 0;
    for (const row of r.results) {
        const seq = Number(row.seq);
        if (seq !== expected)
            gaps.push({ seq, reason: `expected seq ${expected}` });
        if (row.prev_hash !== prevHash)
            gaps.push({ seq, reason: 'prev_hash does not match the previous event' });
        const again = await sha256Hex(chainPreimage({ ...row, seq }));
        if (again !== row.event_hash)
            gaps.push({ seq, reason: 'event_hash mismatch' });
        else
            verified++;
        prevHash = row.event_hash;
        expected = seq + 1;
    }
    return { verified, total: r.results.length, gaps };
}
/**
 * Every engagement on which this identity is — or, within the engagement's
 * lookback, was — a roster member. Identity is `user_id` plus the bound
 * `workos_user_id` (a second users row with the same WorkOS identity still
 * matches); never the email domain.
 */
export async function rosterIdentityHits(control, userId, now = new Date()) {
    const u = await control.query(`SELECT workos_user_id FROM users WHERE user_id = ? LIMIT 1`, [userId]);
    const workos = u.results[0]?.workos_user_id ?? null;
    const rows = await control.query(`SELECT m.member_id, m.user_id, m.workos_user_id, m.removed_at, m.firm_role,
            e.engagement_id, e.workspace_id, e.firm_id, e.audit_period_id, e.assessor_firm, e.engagement_start, e.engagement_end,
            e.report_date, e.report_released_at, e.revoked_at, e.assembly_window_days, e.independence_lookback_months, e.firm_retention_days
       FROM engagement_members m JOIN assessor_engagements e ON e.engagement_id = m.engagement_id
      WHERE m.user_id = ? OR (? IS NOT NULL AND m.workos_user_id = ?)`, [userId, workos, workos]);
    const hits = new Map();
    for (const r of rows.results) {
        const e = { ...r, assembly_window_days: Number(r.assembly_window_days), independence_lookback_months: Number(r.independence_lookback_months), firm_retention_days: Number(r.firm_retention_days) };
        const until = rosterIdentityUntil(e, r);
        if (until < now.getTime())
            continue;
        const current = !r.removed_at;
        const prior = hits.get(r.engagement_id);
        hits.set(r.engagement_id, { engagement_id: r.engagement_id, firm_id: r.firm_id, workspace_id: r.workspace_id, current: current || !!prior?.current });
    }
    return [...hits.values()];
}
/** T17 / R2: true when the identity is on any roster now or within the lookback. */
export async function isRosterIdentity(control, userId, now = new Date()) {
    return (await rosterIdentityHits(control, userId, now)).length > 0;
}
/**
 * Log a refused client-hat insertion to the firm chain of every engagement
 * the identity is (or recently was) on. Content-free: ids and the point.
 */
export async function recordIndependenceRejection(control, input) {
    const now = input.now ?? new Date();
    const hits = await rosterIdentityHits(control, input.user_id, now);
    for (const h of hits) {
        await appendFirmChain(control, {
            engagement_id: h.engagement_id, firm_id: h.firm_id, action: 'independence_rejected', tier: 'independence',
            actor_user_id: input.attempted_by, subject_kind: 'user', subject_id: input.user_id,
            detail: { point: input.point, target_workspace_id: input.target_workspace_id ?? null, target_organization_id: input.target_organization_id ?? null },
            at: now.toISOString(),
        });
    }
    return hits.length;
}
/**
 * The single check every client-hat insertion point calls: refuses (and logs
 * to the firm chain) when the identity is a roster identity.
 */
export async function assertNotRosterIdentity(control, input) {
    if (await isRosterIdentity(control, input.user_id, input.now ?? new Date())) {
        await recordIndependenceRejection(control, input);
        throw new IndependenceRejectedError(input.point);
    }
}
/** By email (directory sync): is any users row with this email a roster identity? */
export async function isRosterIdentityByEmail(control, email, now = new Date()) {
    const r = await control.query(`SELECT user_id FROM users WHERE lower(email) = ?`, [email.trim().toLowerCase()]);
    for (const u of r.results)
        if (await isRosterIdentity(control, u.user_id, now))
            return true;
    return false;
}
/**
 * R5: deleting a workspace needs a completed firm export, or a firm
 * acknowledgement, for every firm-tier engagement on it. True = blocked.
 */
export async function firmCustodyBlocksDeletion(control, workspaceId) {
    const engagements = await control.query(`SELECT engagement_id FROM assessor_engagements WHERE workspace_id = ? AND firm_id IS NOT NULL`, [workspaceId]);
    for (const e of engagements.results) {
        const done = await control.query(`SELECT 1 AS x FROM firm_chain_events WHERE engagement_id = ? AND action IN ('export_completed', 'custody_acknowledged') LIMIT 1`, [e.engagement_id]);
        if (!done.results.length)
            return true;
    }
    return false;
}
/**
 * T6-B1: the accepted partners / managers of a firm — whom to email when a
 * client invites the firm. Used only by the Worker to send that email; never
 * returned to the client.
 */
export async function firmLeadContacts(control, firmId) {
    const r = await control.query(`SELECT u.email, u.display_name, f.name AS firm_name FROM audit_firm_staff s JOIN users u ON u.user_id = s.user_id JOIN audit_firms f ON f.firm_id = s.firm_id
      WHERE s.firm_id = ? AND s.removed_at IS NULL AND s.accepted_at IS NOT NULL AND s.firm_role IN ('partner', 'manager') AND u.is_active = 1`, [firmId]);
    return r.results;
}
export async function firmIdentityForPackage(control, firmId) {
    const r = await control.query(`SELECT firm_id, name, binding_kind, workos_organization_id, verified_domain FROM audit_firms WHERE firm_id = ? LIMIT 1`, [firmId]);
    return r.results[0] ?? null;
}
export async function firmTypesForPackage(control, firmId) {
    const r = await control.query(`SELECT firm_type, cyber_ab_id, authorized_at, revoked_at FROM audit_firm_types WHERE firm_id = ? ORDER BY firm_type`, [firmId]);
    return r.results;
}
export const ASSESSOR_REPORT_SIGN_DOMAIN = 'gacpro.assessor-report.v1';
/** V106-6-4 / ADR-0081 §3: the HMAC domain of a certification decision's manifest. */
export const CERTIFICATION_DECISION_SIGN_DOMAIN = 'gacpro.certification-decision.v1';
export const CERTIFICATION_DECISIONS = ['grant', 'refuse', 'maintain', 'suspend', 'restore', 'withdraw', 'renew', 'expand_scope', 'reduce_scope'];
/** Cycle event kind (iso-certification rules) → the audit kind a sign-off / decision records. Stage 1 is never signed off here. */
export const CERTIFICATION_AUDIT_KIND_BY_EVENT_KIND = Object.freeze({
    stage2: 'initial', surveillance: 'surveillance', recertification: 'recertification',
});
export const ASSESSOR_REPORT_MAX_BYTES = 10 * 1024 * 1024;
const REPORT_COLS = `artifact_id, engagement_id, kind, version, filename, content_type, byte_size, content_sha256, uploaded_by, uploaded_at,
  signed_off_by, signed_off_at, signature_key_id, signature, manifest_json`;
function toReportArtifact(r) {
    return { ...r, version: Number(r.version), byte_size: Number(r.byte_size) };
}
/**
 * V106-6-5 T6-M2: the end an advisory record counts with. For a refuse-mode
 * family an end recorded AFTER the record was created (an open record ended
 * later) counts from max(stated end, when it was recorded), so a back-dated end
 * can't clear a refusal; a record created already ended keeps its stated end.
 * SQL over advisory_engagements columns; `?` = 1 for refuse mode.
 */
const ADVISORY_EFFECTIVE_END = `CASE WHEN ended_at IS NULL THEN NULL
  WHEN ? = 1 AND ended_recorded_at IS NOT NULL AND ended_recorded_at > recorded_at AND ended_recorded_at > ended_at THEN ended_recorded_at
  ELSE ended_at END`;
/** The firm's authorization for an assessor type, when operator-authorized and not revoked. */
export async function activeFirmTypeAuthorization(control, firmId, firmType) {
    const r = await control.query(`SELECT firm_type_id, authorized_at FROM audit_firm_types
      WHERE firm_id = ? AND firm_type = ? AND authorized_at IS NOT NULL AND revoked_at IS NULL LIMIT 1`, [firmId, firmType]);
    return r.results[0] ?? null;
}
/**
 * The CMMC independence lookback for a period: the rule set's
 * `independence_lookback_months` (content, ADR-0059 §5) when present, else 36
 * (32 CFR 170.8(b)(17)(ii)(G)).
 */
export async function cmmcIndependenceLookbackMonths(tenant, control, workspaceId, periodId) {
    try {
        // The program C3PAOs assess comes from the manifest (`assessorTypes`), not a key here.
        const programKey = programsForAssessorType('c3pao')[0];
        const rs = programKey ? await resolveRuleSetForPeriod(tenant, { workspaceId, periodId, programKey, contentDb: control }) : null;
        const v = rs ? JSON.parse(rs.ruleSet.rules_json).independence_lookback_months : undefined;
        if (typeof v === 'number' && Number.isInteger(v) && v >= 12 && v <= 120)
            return v;
    }
    catch {
        /* no rule set yet: the regulation's default */
    }
    return 36;
}
/**
 * V102-7-5 M1: everyone at the firm — accepted staff and engagement roster
 * members, current or ended inside the lookback — who held an active
 * membership in the client workspace or its organization inside the lookback.
 */
export async function assessorIndependenceConflicts(control, input) {
    const now = input.now ?? new Date();
    const cutoff = new Date(now);
    cutoff.setUTCMonth(cutoff.getUTCMonth() - input.lookbackMonths);
    // ADR-0081 §4: a subject-period family (cpa) names its window start instead of a fixed lookback.
    const cutoffIso = input.since ?? cutoff.toISOString();
    const nowIso = now.toISOString();
    const people = await control.query(`SELECT user_id FROM audit_firm_staff WHERE firm_id = ? AND accepted_at IS NOT NULL AND (removed_at IS NULL OR removed_at >= ?)
     UNION
     SELECT user_id FROM engagement_members WHERE firm_id = ? AND (removed_at IS NULL OR removed_at >= ?)`, [input.firmId, cutoffIso, input.firmId, cutoffIso]);
    const org = (await control.query(`SELECT organization_id FROM workspaces WHERE workspace_id = ? LIMIT 1`, [input.workspaceId])).results[0]?.organization_id ?? null;
    const out = [];
    const window = (joined, removed) => ({ overlap_from: joined > cutoffIso ? joined : cutoffIso, overlap_to: removed ?? nowIso });
    for (const p of people.results) {
        const m = await control.query(`SELECT joined_at, removed_at FROM memberships WHERE workspace_id = ? AND user_id = ? AND joined_at <= ? AND (removed_at IS NULL OR removed_at >= ?)`, [input.workspaceId, p.user_id, nowIso, cutoffIso]);
        for (const x of m.results)
            out.push({ user_id: p.user_id, source: 'workspace_membership', ...window(x.joined_at, x.removed_at) });
        if (org) {
            const om = await control.query(`SELECT joined_at, removed_at FROM org_memberships WHERE organization_id = ? AND user_id = ? AND joined_at <= ? AND (removed_at IS NULL OR removed_at >= ?)`, [org, p.user_id, nowIso, cutoffIso]);
            for (const x of om.results)
                out.push({ user_id: p.user_id, source: 'org_membership', ...window(x.joined_at, x.removed_at) });
        }
    }
    const name = await displayNames(control, out.map((c) => c.user_id));
    const conflicts = out.map((c) => ({ ...c, person: name(c.user_id) }));
    // ADR-0067 §6 / FedRAMP REC-IAS-SEP: advisory or consulting services for the offering (or the whole client) inside the lookback.
    const refuse = input.refuseMode ? 1 : 0;
    const adv = await control.query(`SELECT started_at, effective_end FROM (
       SELECT started_at, ${ADVISORY_EFFECTIVE_END} AS effective_end FROM advisory_engagements
        WHERE firm_id = ? AND workspace_id = ? AND (program_key IS NULL OR ? IS NULL OR program_key = ?))
      WHERE started_at <= ? AND (effective_end IS NULL OR effective_end >= ?)
      ORDER BY started_at`, [refuse, input.firmId, input.workspaceId, input.programKey ?? null, input.programKey ?? null, nowIso, cutoffIso]);
    for (const a of adv.results) {
        conflicts.push({ user_id: null, person: 'Advisory engagement', source: 'advisory_engagement', ...window(a.started_at, a.effective_end) });
    }
    // T6-L1: the same client's other workspaces (one organization). Counted here, server-side; the caller learns
    // only that one exists inside the window — never the sibling's record, dates or workspace.
    if (input.organizationScope && org) {
        const sib = await control.query(`SELECT COUNT(*) AS n FROM (
         SELECT a.started_at, ${ADVISORY_EFFECTIVE_END.replace(/\b(ended_at|ended_recorded_at|recorded_at)\b/g, 'a.$1')} AS effective_end
           FROM advisory_engagements a JOIN workspaces w ON w.workspace_id = a.workspace_id
          WHERE a.firm_id = ? AND w.organization_id = ? AND a.workspace_id <> ?
            AND (a.program_key IS NULL OR ? IS NULL OR a.program_key = ?))
        WHERE started_at <= ? AND (effective_end IS NULL OR effective_end >= ?)`, [refuse, input.firmId, org, input.workspaceId, input.programKey ?? null, input.programKey ?? null, nowIso, cutoffIso]);
        if (Number(sib.results[0]?.n ?? 0) > 0) {
            conflicts.push({ user_id: null, person: 'Advisory engagement in your organization', source: 'organization_advisory', overlap_from: cutoffIso, overlap_to: nowIso });
        }
    }
    return conflicts;
}
/** Log a refused assessor engagement on the client's SoD log (content-free: counts, sources, windows). */
async function recordIndependenceRefusal(tenant, input) {
    if (!tenant)
        return;
    await recordSodEvent(tenant, {
        workspace_id: input.workspaceId, transition: 'config', event: input.event ?? 'assessor_independence_refused', actor_user_id: input.actor,
        record_kind: 'audit_firm', record_id: input.firmId, created_at: input.at,
        detail: { point: input.point, firm_type: input.firmType, lookback_months: input.lookbackMonths, lookback_basis: input.basis ?? null,
            conflicts: input.conflicts.map((c) => ({ user_id: c.user_id, source: c.source, overlap_from: c.overlap_from, overlap_to: c.overlap_to })) },
    }).catch(() => undefined);
}
/**
 * ADR-0067 §5: the assessor family of an invitation. An explicit family must
 * be accepted by the period's program manifest (`assessorTypes`) AND held by
 * the firm (operator-authorized, not revoked); with none requested, the one
 * family both the manifest and the firm hold (else a plain firm-tier
 * engagement). `strict` throws on a requested family that fails the gate.
 */
async function resolveAssessorFamily(control, input) {
    if (input.requested) {
        if (!isAssessorFirmType(input.requested))
            throw new FirmValidationError('invalid_assessor_firm_type', `assessor_firm_type must be one of ${ASSESSOR_FIRM_TYPES.join(', ')}.`);
        await assertAssessorFamilyEnabled(control, input.requested);
        if (!programAllowsAssessorType(input.programKey, input.requested)) {
            throw new FirmValidationError('assessor_type_not_allowed', `The ${input.programKey ?? 'unknown'} program does not accept ${ASSESSOR_FIRM_TYPE_SPECS[input.requested].label} assessments.`);
        }
        if (!(await activeFirmTypeAuthorization(control, input.firmId, input.requested))) {
            throw new FirmValidationError('firm_type_not_authorized', `This firm holds no active ${ASSESSOR_FIRM_TYPE_SPECS[input.requested].label} authorization.`);
        }
        return input.requested;
    }
    const held = [];
    for (const t of assessorTypesForProgram(input.programKey)) {
        if ((await assessorFamilyEnabled(control, t)) && (await activeFirmTypeAuthorization(control, input.firmId, t)))
            held.push(t);
    }
    return held.length === 1 ? held[0] : null;
}
/**
 * ADR-0081 §5: migration 533 is the prerequisite of the two new families. The
 * family's `assessor_firm_types` row is read before a family is declared or
 * invited, so code that reaches a database 533 hasn't reached answers 409
 * `assessor_family_not_enabled` (fail closed) instead of a trigger error.
 */
export async function assessorFamilyEnabled(control, family) {
    const r = await control.query(`SELECT 1 AS x FROM assessor_firm_types WHERE firm_type = ? LIMIT 1`, [family]);
    return r.results.length > 0;
}
/** V112-2-1 (T6-L5): a preview family is refused unless the client workspace has the preview enabled. 409. */
async function assertPreviewFamilyAllowed(gate, workspaceId, family) {
    if (!(await previewFamilyAllowed(gate, workspaceId, family))) {
        throw new FirmStateError('assessor_family_in_preview', `${ASSESSOR_FIRM_TYPE_SPECS[family].label} engagements are not open yet.`);
    }
}
async function assertAssessorFamilyEnabled(control, family) {
    if (!(await assessorFamilyEnabled(control, family))) {
        throw new FirmStateError('assessor_family_not_enabled', `${ASSESSOR_FIRM_TYPE_SPECS[family].label} assessments are not enabled on this database yet.`);
    }
}
/**
 * V106-6-5 T6-L6: a family with `singleOpenEngagementPerPeriod` (certification_body) has at most one open
 * engagement per firm × client period. Open = not revoked and not past its end date; a closed one doesn't
 * block (its people still count for separation, c3pao-service). 409 otherwise.
 */
async function assertNoOpenEngagementOfFamily(control, input) {
    if (!ASSESSOR_FIRM_TYPE_SPECS[input.family].singleOpenEngagementPerPeriod)
        return;
    const r = await control.query(`SELECT 1 AS x FROM assessor_engagements
      WHERE workspace_id = ? AND firm_id = ? AND audit_period_id = ? AND assessor_firm_type = ?
        AND revoked_at IS NULL AND substr(engagement_end, 1, 10) >= ? LIMIT 1`, [input.workspaceId, input.firmId, input.periodId, input.family, input.now.toISOString().slice(0, 10)]);
    if (r.results.length) {
        throw new FirmStateError('engagement_already_open', `This firm already has an open ${ASSESSOR_FIRM_TYPE_SPECS[input.family].label} engagement on this period.`);
    }
}
/**
 * ADR-0067 owner decision 3 (2026-10-07): FedRAMP's REC-IAS-SEP period comes
 * from rules content — the CR26 ingest files it as a `certification_timeframes`
 * proposal (`REC-IAS-SEP:all:-:-:-`) and only an APPROVED (active) row is
 * read. Before approval: 2 years, basis `default_pending_rule`.
 */
export async function fedrampAdvisoryLookback(control) {
    const row = await lookupTimeframe(control, 'REC-IAS-SEP', { path: '20x' });
    if (row) {
        const n = Number(row.timeframe_num);
        const months = row.timeframe_type === 'years' ? n * 12 : row.timeframe_type === 'months' ? n
            : Math.ceil((row.timeframe_type === 'weeks' ? n * 7 : row.timeframe_type === 'hours' ? n / 24 : n) / 30.4375);
        if (Number.isFinite(months) && months > 0)
            return { months: Math.min(120, Math.max(12, Math.ceil(months))), basis: `REC-IAS-SEP@${row.source_version ?? 'unknown'}` };
    }
    return { months: 24, basis: 'default_pending_rule' };
}
/** The window of a subject-period family: from the earlier of the period start and now, to now (ADR-0081 §4). */
export function subjectPeriodLookback(family, periodStart, now) {
    const nowIso = now.toISOString();
    const start = periodStart ? new Date(`${periodStart.slice(0, 10)}T00:00:00.000Z`).toISOString() : nowIso;
    const since = start < nowIso ? start : nowIso;
    const months = Math.ceil((now.getTime() - Date.parse(since)) / (30.4375 * 86_400_000));
    return { months: Math.min(120, Math.max(12, months)), basis: ASSESSOR_FIRM_TYPE_SPECS[family].independenceBasis, since };
}
async function familyLookback(family, tenant, control, workspaceId, periodId, now) {
    if (family === 'c3pao') {
        const months = tenant ? await cmmcIndependenceLookbackMonths(tenant, control, workspaceId, periodId) : ASSESSOR_FIRM_TYPE_SPECS.c3pao.independence.defaultLookbackMonths;
        return { months, basis: months === ASSESSOR_FIRM_TYPE_SPECS.c3pao.independence.defaultLookbackMonths ? 'regulation_default' : 'cmmc_rule_set' };
    }
    if (family === 'fedramp_recognized')
        return fedrampAdvisoryLookback(control);
    const spec = ASSESSOR_FIRM_TYPE_SPECS[family];
    if (spec.independence.window === 'subject_period') {
        const period = tenant ? await firmPeriodSummary(tenant, workspaceId, periodId).catch(() => null) : null;
        return subjectPeriodLookback(family, period?.start ?? null, now);
    }
    return { months: spec.independence.defaultLookbackMonths ?? 24, basis: spec.independenceBasis };
}
/** ADR-0067 owner decision 1: GovRAMP's rule is unconfirmed — a conflict is a flag, never a refusal. */
export const GOVRAMP_INDEPENDENCE_FLAG = ASSESSOR_FIRM_TYPE_SPECS.govramp_3pao.flagMessage;
/**
 * M1 / ADR-0067 §6 / ADR-0081 §4: refuse (and log) a conflicted assessor
 * engagement. A `refuse`-mode family (c3pao, fedramp_recognized,
 * certification_body) throws 409; a `flag`-mode family (govramp_3pao, cpa)
 * only logs `assessor_independence_flagged`. Returns the lookback that applied.
 */
async function assertAssessorIndependence(control, tenant, input) {
    const lookback = await familyLookback(input.family, tenant, control, input.workspaceId, input.periodId, input.now);
    const spec = ASSESSOR_FIRM_TYPE_SPECS[input.family];
    const flagOnly = spec.independence.mode === 'flag';
    const conflicts = await assessorIndependenceConflicts(control, {
        workspaceId: input.workspaceId, firmId: input.firmId, lookbackMonths: lookback.months, since: lookback.since ?? null, now: input.now, programKey: input.programKey,
        refuseMode: !flagOnly, organizationScope: spec.independenceScope === 'organization',
    });
    if (conflicts.length) {
        await recordIndependenceRefusal(tenant, { workspaceId: input.workspaceId, firmId: input.firmId, actor: input.actor, point: input.point, firmType: input.family, lookbackMonths: lookback.months, conflicts, at: input.now.toISOString(), event: flagOnly ? 'assessor_independence_flagged' : 'assessor_independence_refused', basis: lookback.basis });
        if (!flagOnly)
            throw new AssessorIndependenceError(conflicts, lookback.months, lookback.basis);
    }
    return lookback;
}
/**
 * The informational flag on a `flag`-mode engagement (GovRAMP owner decision 1;
 * cpa, ADR-0081 §4), computed at read time so advisory recorded later still
 * shows; null for every other engagement. A subject-period family needs the
 * period start (`periodStart`); without it the window is empty (now).
 */
export async function engagementIndependenceFlag(control, e, now = new Date(), periodStart) {
    if (!isAssessorFirmType(e.assessor_firm_type) || !e.firm_id)
        return null;
    const family = e.assessor_firm_type;
    const spec = ASSESSOR_FIRM_TYPE_SPECS[family];
    if (spec.independence.mode !== 'flag' || !spec.flagMessage)
        return null;
    const lookback = spec.independence.window === 'subject_period'
        ? subjectPeriodLookback(family, periodStart ?? null, now)
        : { months: spec.independence.defaultLookbackMonths ?? 24, basis: spec.independenceBasis };
    const programKey = programsForAssessorType(family)[0] ?? null;
    const conflicts = await assessorIndependenceConflicts(control, { workspaceId: e.workspace_id, firmId: e.firm_id, lookbackMonths: lookback.months, since: lookback.since ?? null, now, programKey });
    return conflicts.length ? { code: spec.independenceBasis, message: spec.flagMessage, conflicts: conflicts.length, lookback_months: lookback.months } : null;
}
// ---- firm directory (no workspace) ------------------------------------------------
export class FirmDirectory {
    control;
    userId;
    claims;
    kek;
    now;
    constructor(control, userId, claims, kek, now = () => new Date()) {
        this.control = control;
        this.userId = userId;
        this.claims = claims;
        this.kek = kek;
        this.now = now;
    }
    /** The caller, bound to the session's WorkOS identity — else not found. */
    async me() {
        const u = await loadUser(this.control, this.userId);
        if (!u || Number(u.is_active) !== 1 || !u.workos_user_id || !this.claims.workos_user_id || u.workos_user_id !== this.claims.workos_user_id) {
            throw new FirmNotFoundError();
        }
        return { user_id: u.user_id, email: u.email, workos_user_id: u.workos_user_id };
    }
    async myStaff(firmId) {
        const me = await this.me();
        const r = await this.control.query(`SELECT * FROM audit_firm_staff WHERE firm_id = ? AND user_id = ? AND removed_at IS NULL AND accepted_at IS NOT NULL LIMIT 1`, [firmId, me.user_id]);
        const s = r.results[0];
        return s && s.workos_user_id === me.workos_user_id ? s : null;
    }
    /** Firm lead (partner / manager) of this firm, or not found. */
    async requireLead(firmId) {
        const s = await this.myStaff(firmId);
        if (!s)
            throw new FirmNotFoundError();
        if (!isLeadRole(s.firm_role))
            throw new FirmForbiddenError('firm_lead_required', 'Only a firm partner or manager can do this.');
        return s;
    }
    /**
     * R1: register a firm bound to the WorkOS organization in the caller's
     * signed access token (`org_id`), or — platform operator only (users.role
     * 'admin') — to a domain the operator verified out of band. The caller
     * becomes the firm's founding partner.
     */
    async registerFirm(input) {
        const me = await this.me();
        const name = cleanText(input.name, 'name', 2, 200);
        const at = this.now().toISOString();
        let binding;
        if (input.verified_domain) {
            const role = await this.control.query(`SELECT role FROM users WHERE user_id = ? LIMIT 1`, [me.user_id]);
            if (role.results[0]?.role !== 'admin') {
                throw new FirmForbiddenError('domain_binding_needs_operator', 'Only the platform operator can bind a firm to a verified domain.');
            }
            const domain = String(input.verified_domain).trim().toLowerCase();
            if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(domain))
                throw new FirmValidationError('invalid_domain', 'verified_domain must be a domain name.');
            binding = { kind: 'domain', org: null, domain, verifiedBy: `operator:${me.user_id}` };
        }
        else {
            const org = this.claims.workos_org_id;
            if (!org) {
                throw new FirmValidationError('workos_org_required', 'Sign in through your firm’s WorkOS organization to register the firm.');
            }
            binding = { kind: 'workos_org', org, domain: null, verifiedBy: `workos:${org}` };
        }
        const dupe = await this.control.query(`SELECT firm_id FROM audit_firms WHERE (? IS NOT NULL AND workos_organization_id = ?) OR (? IS NOT NULL AND verified_domain = ?) LIMIT 1`, [binding.org, binding.org, binding.domain, binding.domain]);
        if (dupe.results[0])
            throw new FirmStateError('firm_exists', 'A firm is already registered for this organization or domain.');
        // V98-7-3 M2: no exact, case-insensitive or near-duplicate names (the picker must never show two look-alikes).
        const key0 = firmNameKey(name);
        if (!key0)
            throw new FirmValidationError('invalid_name', 'name must contain letters or digits.');
        const names = await this.control.query(`SELECT name FROM audit_firms`);
        if (names.results.some((f) => firmNameKey(f.name) === key0)) {
            throw new FirmStateError('firm_name_taken', 'A firm with this name (or one that reads the same) is already registered.');
        }
        // A domain binding is made by the platform operator, so it is operator-verified at once.
        const operator = binding.kind === 'domain' ? { at, by: binding.verifiedBy } : { at: null, by: null };
        const firmId = generateRandomId('afm');
        const key = await mintWrappedFirmKey(decodeKek(this.kek), firmId);
        await this.control.batch([
            { sql: `INSERT INTO audit_firms (firm_id, name, binding_kind, workos_organization_id, verified_domain, verified_by, verified_at, key_wrapped, key_kek_id, created_by, created_at,
                                       operator_verified_at, operator_verified_by)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [firmId, name, binding.kind, binding.org, binding.domain, binding.verifiedBy, at, key.key_wrapped, key.key_kek_id, me.user_id, at, operator.at, operator.by] },
            { sql: `INSERT INTO audit_firm_staff (staff_id, firm_id, user_id, workos_user_id, firm_role, added_by, added_at, accepted_at, accepted_proof)
              VALUES (?, ?, ?, ?, 'partner', ?, ?, ?, 'founder')`,
                params: [generateRandomId('afs'), firmId, me.user_id, me.workos_user_id, me.user_id, at, at] },
        ]);
        return (await this.getFirm(firmId));
    }
    async getFirm(firmId) {
        const r = await this.control.query(`SELECT firm_id, name, binding_kind, workos_organization_id, verified_domain, verified_by, verified_at, key_kek_id, created_by, created_at,
              operator_verified_at, operator_verified_by
         FROM audit_firms WHERE firm_id = ? LIMIT 1`, [firmId]);
        return r.results[0] ?? null;
    }
    /**
     * Directory for the client's "invite a firm": id, name, binding kind and
     * what it is bound to (V98-7-3 M2: the verified domain, or the WorkOS
     * organization). Only operator-verified firms are listed.
     */
    async lookup(query) {
        await this.me();
        const q = String(query ?? '').trim().toLowerCase();
        if (q.length < 2)
            return [];
        const r = await this.control.query(`SELECT firm_id, name, binding_kind, verified_domain, workos_organization_id FROM audit_firms
        WHERE operator_verified_at IS NOT NULL AND (lower(name) LIKE ? OR verified_domain = ?) ORDER BY name ASC LIMIT 20`, [`%${q.replace(/[%_]/g, '')}%`, q]);
        return r.results.map(({ workos_organization_id, ...f }) => ({
            ...f, bound_to: f.verified_domain ?? `WorkOS organization ${workos_organization_id ?? ''}`.trim(),
        }));
    }
    /** V98-7-3 M2: the platform operator (users.role 'admin'), bound to this session — else not found. */
    async requireOperator() {
        const me = await this.me();
        const role = await this.control.query(`SELECT role FROM users WHERE user_id = ? LIMIT 1`, [me.user_id]);
        if (role.results[0]?.role !== 'admin')
            throw new FirmNotFoundError();
        return me;
    }
    /** V98-7-3 M2: firms awaiting operator verification (operator only). */
    async listUnverified() {
        await this.requireOperator();
        const r = await this.control.query(`SELECT firm_id, name, binding_kind, workos_organization_id, verified_domain, verified_by, verified_at, key_kek_id, created_by, created_at,
              operator_verified_at, operator_verified_by
         FROM audit_firms WHERE operator_verified_at IS NULL ORDER BY created_at ASC LIMIT 200`);
        return r.results;
    }
    /**
     * V98-7-3 M2: the operator records that a firm was verified out of band
     * (it is a real audit firm and controls the bound organization / domain).
     * Set once; the firm is then listed in lookup and can be invited.
     */
    async verifyFirm(firmId, input = {}) {
        const op = await this.requireOperator();
        const firm = await this.getFirm(firmId);
        if (!firm)
            throw new FirmNotFoundError();
        if (firm.operator_verified_at)
            throw new FirmStateError('already_verified', 'This firm is already verified.');
        const note = input.note ? cleanText(input.note, 'note', 1, 500) : null;
        await this.control.execute(`UPDATE audit_firms SET operator_verified_at = ?, operator_verified_by = ? WHERE firm_id = ? AND operator_verified_at IS NULL`, [this.now().toISOString(), `operator:${op.user_id}${note ? ` — ${note}` : ''}`, firmId]);
        return (await this.getFirm(firmId));
    }
    /**
     * The caller's firms (accepted or pending), and the client invitations their
     * lead roles can decide — each with the client's name and (T6-H10) the
     * period and program when `tenantFor` can reach the client's data plane.
     */
    async mine(opts = {}) {
        const me = await this.me();
        const staff = await this.control.query(`SELECT * FROM audit_firm_staff WHERE user_id = ? AND removed_at IS NULL ORDER BY added_at ASC`, [me.user_id]);
        const firms = [];
        const pending = [];
        const invitations = [];
        for (const s of staff.results) {
            const f = await this.getFirm(s.firm_id);
            if (!f)
                continue;
            if (!s.accepted_at) {
                pending.push({ firm_id: f.firm_id, firm_name: f.name, staff_id: s.staff_id, firm_role: s.firm_role });
                continue;
            }
            firms.push({ ...f, staff: s });
            if (isLeadRole(s.firm_role)) {
                const inv = await this.control.query(`SELECT i.*, w.name AS workspace_name FROM firm_engagement_invitations i JOIN workspaces w ON w.workspace_id = i.workspace_id
            WHERE i.firm_id = ? AND i.status = 'pending' AND w.deleted_at IS NULL ORDER BY i.invited_at ASC`, [f.firm_id]);
                for (const i of inv.results) {
                    const tenant = opts.tenantFor ? await opts.tenantFor(i.workspace_id).catch(() => null) : null;
                    invitations.push({ ...i, firm_name: f.name, period: tenant ? await firmPeriodSummary(tenant, i.workspace_id, i.audit_period_id).catch(() => null) : null });
                }
            }
        }
        return { firms, invitations, pending_staff: pending };
    }
    /**
     * The caller's engagement seats, with the client workspace slug to open them
     * and (T6-H9) where each stands now: `access` open (with its phase), not
     * started (opens at `window.starts_at`) or closed (past retention, or a
     * staff seat after assembly). `period` when `tenantFor` reaches the client's plane.
     */
    async myEngagements(opts = {}) {
        const me = await this.me();
        const r = await this.control.query(`SELECT e.engagement_id, e.workspace_id, w.slug AS workspace_slug, w.name AS workspace_name, e.firm_id, e.assessor_firm AS firm_name, e.assessor_firm, m.firm_role, m.workos_user_id,
              e.audit_period_id, e.engagement_start, e.engagement_end, e.report_date, e.report_released_at, e.revoked_at,
              e.assembly_window_days, e.independence_lookback_months, e.firm_retention_days
         FROM engagement_members m
         JOIN assessor_engagements e ON e.engagement_id = m.engagement_id
         JOIN workspaces w ON w.workspace_id = e.workspace_id
        WHERE m.user_id = ? AND m.removed_at IS NULL AND w.deleted_at IS NULL
        ORDER BY e.engagement_start DESC`, [me.user_id]);
        const now = this.now();
        const out = [];
        for (const x of r.results.filter((row) => row.workos_user_id === me.workos_user_id)) {
            const e = { ...x, assembly_window_days: Number(x.assembly_window_days), independence_lookback_months: Number(x.independence_lookback_months), firm_retention_days: Number(x.firm_retention_days) };
            const window = firmWindow(e);
            const phase = firmPhase(e, x.firm_role, now);
            const access = phase ? 'open' : now.getTime() < Date.parse(window.starts_at) ? 'not_started' : 'closed';
            const tenant = opts.tenantFor ? await opts.tenantFor(x.workspace_id).catch(() => null) : null;
            out.push({
                engagement_id: x.engagement_id, workspace_slug: x.workspace_slug, workspace_name: x.workspace_name, firm_id: x.firm_id, firm_name: x.firm_name, firm_role: x.firm_role,
                engagement_start: x.engagement_start, engagement_end: x.engagement_end, report_date: x.report_date, revoked_at: x.revoked_at,
                access, phase, window, period: tenant ? await firmPeriodSummary(tenant, x.workspace_id, x.audit_period_id).catch(() => null) : null,
            });
        }
        return out;
    }
    /** The firm's staff list (accepted staff of the firm only), with names and emails for the roster picker (T6-B2). */
    async listStaff(firmId) {
        if (!(await this.myStaff(firmId)))
            throw new FirmNotFoundError();
        const r = await this.control.query(`SELECT s.*, u.display_name, u.email FROM audit_firm_staff s JOIN users u ON u.user_id = s.user_id
        WHERE s.firm_id = ? ORDER BY s.removed_at IS NOT NULL, s.firm_role, s.added_at`, [firmId]);
        return r.results;
    }
    /** R1: a firm partner or manager adds staff (a manager adds senior / staff only). Pending until accepted. */
    async addStaff(input) {
        const lead = await this.requireLead(input.firm_id);
        if (!FIRM_ROLES.includes(input.firm_role))
            throw new FirmValidationError('invalid_role', 'firm_role must be partner, manager, senior or staff.');
        if (lead.firm_role === 'manager' && isLeadRole(input.firm_role)) {
            throw new FirmForbiddenError('partner_required', 'Only a partner can add a partner or manager.');
        }
        const target = input.user_id
            ? await loadUser(this.control, input.user_id)
            : (await this.control.query(`SELECT user_id, email, workos_user_id, is_active FROM users WHERE lower(email) = ? AND is_active = 1 ORDER BY workos_user_id IS NULL, user_id LIMIT 1`, [String(input.email ?? '').trim().toLowerCase()])).results[0] ?? null;
        if (!target || Number(target.is_active) !== 1)
            throw new FirmValidationError('unknown_user', 'That person needs a GAC Pro sign-in first.');
        if (!target.workos_user_id)
            throw new FirmValidationError('identity_unbound', 'That person must sign in once so their identity is bound before joining a firm.');
        const open = await this.control.query(`SELECT staff_id FROM audit_firm_staff WHERE firm_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [input.firm_id, target.user_id]);
        if (open.results[0])
            throw new FirmStateError('already_staff', 'That person is already on the firm.');
        const staffId = generateRandomId('afs');
        const at = this.now().toISOString();
        await this.control.execute(`INSERT INTO audit_firm_staff (staff_id, firm_id, user_id, workos_user_id, firm_role, added_by, added_at) VALUES (?, ?, ?, ?, ?, ?, ?)`, [staffId, input.firm_id, target.user_id, target.workos_user_id, input.firm_role, lead.user_id, at]);
        return (await this.control.query(`SELECT * FROM audit_firm_staff WHERE staff_id = ?`, [staffId])).results[0];
    }
    /**
     * The invited person accepts, proving firm affiliation: the signed WorkOS
     * `org_id` equals the firm's organization (workos_org firms), or their bound
     * account email is on the firm's verified domain (domain firms).
     */
    async acceptStaff(input) {
        const me = await this.me();
        const r = await this.control.query(`SELECT * FROM audit_firm_staff WHERE staff_id = ? AND firm_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [input.staff_id, input.firm_id, me.user_id]);
        const s = r.results[0];
        if (!s || s.workos_user_id !== me.workos_user_id)
            throw new FirmNotFoundError();
        if (s.accepted_at)
            return s;
        const firm = (await this.control.query(`SELECT binding_kind, workos_organization_id, verified_domain FROM audit_firms WHERE firm_id = ?`, [input.firm_id])).results[0];
        if (!firm)
            throw new FirmNotFoundError();
        let proof;
        if (firm.binding_kind === 'workos_org') {
            if (!this.claims.workos_org_id || this.claims.workos_org_id !== firm.workos_organization_id) {
                throw new FirmForbiddenError('firm_org_required', 'Sign in through your firm’s organization to accept.');
            }
            proof = 'workos_org';
        }
        else {
            const domain = me.email.split('@')[1]?.toLowerCase();
            if (!domain || domain !== firm.verified_domain)
                throw new FirmForbiddenError('firm_domain_required', 'Accept with your firm account.');
            proof = 'domain';
        }
        const at = this.now().toISOString();
        await this.control.execute(`UPDATE audit_firm_staff SET accepted_at = ?, accepted_proof = ? WHERE staff_id = ? AND accepted_at IS NULL`, [at, proof, s.staff_id]);
        return (await this.control.query(`SELECT * FROM audit_firm_staff WHERE staff_id = ?`, [s.staff_id])).results[0];
    }
    /** T6-B3: the invited person declines a pending staff invitation (the row is ended, never deleted). */
    async declineStaff(input) {
        const me = await this.me();
        const r = await this.control.query(`SELECT * FROM audit_firm_staff WHERE staff_id = ? AND firm_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [input.staff_id, input.firm_id, me.user_id]);
        const s = r.results[0];
        if (!s || s.workos_user_id !== me.workos_user_id)
            throw new FirmNotFoundError();
        if (s.accepted_at)
            throw new FirmStateError('already_accepted', 'You already joined this firm; a partner or manager removes staff.');
        await this.control.execute(`UPDATE audit_firm_staff SET removed_at = ?, removed_by = ?, removed_reason = 'declined' WHERE staff_id = ? AND removed_at IS NULL AND accepted_at IS NULL`, [this.now().toISOString(), me.user_id, s.staff_id]);
    }
    /** T6-B1: who to email about a new staff row (the invitee) — used only by the Worker to send the invitation. */
    async staffInviteContact(staffId) {
        const r = await this.control.query(`SELECT u.email, u.display_name, f.name AS firm_name FROM audit_firm_staff s JOIN users u ON u.user_id = s.user_id JOIN audit_firms f ON f.firm_id = s.firm_id
        WHERE s.staff_id = ? AND s.removed_at IS NULL LIMIT 1`, [staffId]);
        return r.results[0] ?? null;
    }
    /** End a staff row (and that person's open engagement rows for the firm). Never deleted. */
    async removeStaff(input) {
        const lead = await this.requireLead(input.firm_id);
        const reason = cleanText(input.reason, 'reason', 5, 2000);
        const r = await this.control.query(`SELECT * FROM audit_firm_staff WHERE staff_id = ? AND firm_id = ? AND removed_at IS NULL LIMIT 1`, [input.staff_id, input.firm_id]);
        const s = r.results[0];
        if (!s)
            throw new FirmNotFoundError();
        if (lead.firm_role === 'manager' && isLeadRole(s.firm_role))
            throw new FirmForbiddenError('partner_required', 'Only a partner can remove a partner or manager.');
        const at = this.now().toISOString();
        const members = await this.control.query(`SELECT * FROM engagement_members WHERE firm_id = ? AND user_id = ? AND removed_at IS NULL`, [input.firm_id, s.user_id]);
        await this.control.batch([
            { sql: `UPDATE audit_firm_staff SET removed_at = ?, removed_by = ?, removed_reason = ? WHERE staff_id = ? AND removed_at IS NULL`, params: [at, lead.user_id, reason, s.staff_id] },
            ...members.results.map((m) => ({
                sql: `UPDATE engagement_members SET removed_at = ?, removed_by = ?, removed_reason = ? WHERE member_id = ? AND workspace_id = ? AND firm_id = ? AND removed_at IS NULL`,
                params: [at, lead.user_id, `left the firm: ${reason}`, m.member_id, m.workspace_id, input.firm_id],
            })),
        ]);
        for (const m of members.results) {
            await appendFirmChain(this.control, { engagement_id: m.engagement_id, firm_id: m.firm_id, action: 'member_removed', tier: 'roster',
                actor_user_id: lead.user_id, subject_kind: 'engagement_member', subject_id: m.member_id, detail: { reason_kind: 'left_firm' }, at });
        }
    }
    /**
     * A firm partner / manager accepts a client's invitation. Creates the
     * engagement (the FIRM's acceptor is the engagement's first member — the
     * client never chooses people, R1) after the R2 independence check.
     */
    async acceptInvitation(input) {
        const r = await this.control.query(`SELECT * FROM firm_engagement_invitations WHERE invitation_id = ? LIMIT 1`, [input.invitation_id]);
        const inv = r.results[0];
        if (!inv)
            throw new FirmNotFoundError();
        const lead = await this.myStaff(inv.firm_id);
        if (!lead)
            throw new FirmNotFoundError();
        if (!isLeadRole(lead.firm_role))
            throw new FirmForbiddenError('firm_lead_required', 'Only a firm partner or manager can accept an engagement.');
        if (inv.status !== 'pending')
            throw new FirmStateError('invitation_decided', 'This invitation was already decided.');
        const now = this.now();
        if (await holdsClientHat(this.control, inv.workspace_id, lead.user_id)) {
            // R2: never seat someone who holds a hat at the client. Logged where the identity already has a chain.
            await recordIndependenceRejection(this.control, { user_id: lead.user_id, point: 'engagement_member_insert', attempted_by: lead.user_id, target_workspace_id: inv.workspace_id, now });
            throw new IndependenceRejectedError('engagement_member_insert');
        }
        const firm = (await this.getFirm(inv.firm_id));
        // V102-7-5 M1 / ADR-0067 §5–§6: the assessor family is re-gated (manifest × active authorization) and the
        // independence check runs again, inside engagement creation.
        const tenant = input.tenantFor ? await input.tenantFor(inv.workspace_id) : null;
        const period = tenant ? await firmPeriodSummary(tenant, inv.workspace_id, inv.audit_period_id).catch(() => null) : null;
        if (!period && (inv.assessor_firm_type || (await this.firmHoldsAnyAssessorType(inv.firm_id)))) {
            throw new FirmStateError('period_unavailable', 'The client\'s audit period can\'t be read right now; try again.');
        }
        let family;
        try {
            family = await resolveAssessorFamily(this.control, { firmId: inv.firm_id, programKey: period?.program ?? null, requested: inv.assessor_firm_type ?? null });
        }
        catch (err) {
            // ADR-0081 §7: on the firm side a family the period's program doesn't accept, or one this firm isn't
            // authorized for, is not found (cross-firm-type), never a reason code.
            if (err instanceof FirmValidationError)
                throw new FirmNotFoundError();
            throw err;
        }
        if (family)
            await assertPreviewFamilyAllowed(input.previewFamilyGate, inv.workspace_id, family);
        const firmType = family === 'c3pao' ? 'c3pao' : 'audit_firm';
        // V106-6-5 T6-M1 (ADR-0081 open item 4): the accepting lead attests the firm's independence. Required for a
        // `required` family (no acceptance without it), recorded either way for a `recorded` one.
        if (family)
            await assertNoOpenEngagementOfFamily(this.control, { family, workspaceId: inv.workspace_id, firmId: inv.firm_id, periodId: inv.audit_period_id, now });
        const attestationMode = family ? ASSESSOR_FIRM_TYPE_SPECS[family].acceptanceAttestation : null;
        const attested = input.independence_attestation === true;
        if (attestationMode === 'required' && !attested) {
            throw new FirmValidationError('independence_attestation_required', `Accepting a ${ASSESSOR_FIRM_TYPE_SPECS[family].label} engagement needs the lead's independence attestation (independence_attestation: true).`);
        }
        let lookback = 12;
        let lookbackInfo = null;
        if (family) {
            lookbackInfo = await assertAssessorIndependence(this.control, tenant, { family, workspaceId: inv.workspace_id, firmId: inv.firm_id, periodId: inv.audit_period_id, programKey: period?.program ?? null, actor: lead.user_id, point: 'engagement_create', now });
            lookback = lookbackInfo.months;
        }
        const engagementId = generateRandomId('eng');
        const at = now.toISOString();
        const marker = await input.marker({
            workspace_id: inv.workspace_id, engagement_id: engagementId,
            after: { engagement_id: engagementId, assessor_firm: firm.name, audit_period_id: inv.audit_period_id, engagement_start: inv.engagement_start, engagement_end: inv.engagement_end, tier: 'firm' },
        });
        const memberId = generateRandomId('emb');
        await this.control.batch([
            { sql: `INSERT INTO assessor_engagements (engagement_id, workspace_id, snapshot_id, assessor_user_id, assessor_firm, invited_by, invited_at,
                engagement_start, engagement_end, archive_window_days, event_id_started, firm_id, audit_period_id, firm_invitation_id, report_date,
                firm_type, independence_lookback_months, assessor_firm_type)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, 90, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [engagementId, inv.workspace_id, `firm-invitation:${inv.invitation_id}`, lead.user_id, firm.name, inv.invited_by, inv.invited_at,
                    inv.engagement_start, inv.engagement_end, marker.event_id, inv.firm_id, inv.audit_period_id, inv.invitation_id, inv.report_date,
                    firmType, lookback, family] },
            { sql: `INSERT INTO engagement_members (member_id, engagement_id, workspace_id, firm_id, user_id, workos_user_id, firm_role, added_by, added_at)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [memberId, engagementId, inv.workspace_id, inv.firm_id, lead.user_id, lead.workos_user_id, lead.firm_role, lead.user_id, at] },
            { sql: `UPDATE firm_engagement_invitations SET status = 'accepted', decided_by = ?, decided_at = ?, engagement_id = ? WHERE invitation_id = ? AND workspace_id = ? AND firm_id = ? AND status = 'pending'`,
                params: [lead.user_id, at, engagementId, inv.invitation_id, inv.workspace_id, inv.firm_id] },
        ]);
        await appendFirmChain(this.control, { engagement_id: engagementId, firm_id: inv.firm_id, action: 'engagement_accepted', tier: 'roster',
            actor_user_id: lead.user_id, subject_kind: 'engagement_member', subject_id: memberId, detail: { firm_role: lead.firm_role, invitation_id: inv.invitation_id }, at });
        if (family && attestationMode) {
            // Append-only on both sides: the firm chain (hash-linked) and the client's SoD log.
            const detail = {
                firm_type: family, attested, statement: INDEPENDENCE_ATTESTATION_STATEMENT, required: attestationMode === 'required',
                lookback_basis: lookbackInfo?.basis ?? null, lookback_months: lookbackInfo?.months ?? null, window_since: lookbackInfo?.since ?? null,
            };
            await appendFirmChain(this.control, { engagement_id: engagementId, firm_id: inv.firm_id, action: 'independence_attested', tier: 'independence',
                actor_user_id: lead.user_id, subject_kind: 'engagement', subject_id: engagementId, detail, at });
            if (tenant) {
                await recordSodEvent(tenant, {
                    workspace_id: inv.workspace_id, transition: 'config', event: attested ? 'assessor_independence_attested' : 'assessor_independence_not_attested',
                    actor_user_id: lead.user_id, record_kind: 'audit_firm', record_id: inv.firm_id, created_at: at, detail: { engagement_id: engagementId, ...detail },
                });
            }
        }
        return { engagement_id: engagementId, member_id: memberId };
    }
    async firmHoldsAnyAssessorType(firmId) {
        const r = await this.control.query(`SELECT 1 AS x FROM audit_firm_types WHERE firm_id = ? AND firm_type <> 'audit_firm' AND authorized_at IS NOT NULL AND revoked_at IS NULL LIMIT 1`, [firmId]);
        return r.results.length > 0;
    }
    // ---- assessor firm types (ADR-0067 §1): partner / manager declares; the platform operator authorizes ----
    /** The firm's assessor-type authorizations: its accepted staff, or the platform operator; anyone else 404. */
    async listFirmTypes(firmId) {
        if (!(await this.myStaff(firmId)))
            await this.requireOperator();
        if (!(await this.getFirm(firmId)))
            throw new FirmNotFoundError();
        const r = await this.control.query(`SELECT firm_type, cyber_ab_id, external_id, authorized_at, revoked_at, revocation_reason, created_at FROM audit_firm_types WHERE firm_id = ? ORDER BY firm_type`, [firmId]);
        return r.results;
    }
    /** A firm partner / manager declares an assessor family with its authority id (pending until the operator authorizes). */
    async declareFirmType(input) {
        const lead = await this.requireLead(input.firm_id);
        if (!isAssessorFirmType(input.firm_type))
            throw new FirmValidationError('invalid_assessor_firm_type', `firm_type must be one of ${ASSESSOR_FIRM_TYPES.join(', ')}.`);
        const id = cleanText(input.external_id, 'external_id', 3, 100);
        await assertAssessorFamilyEnabled(this.control, input.firm_type);
        const existing = await this.control.query(`SELECT 1 AS x FROM audit_firm_types WHERE firm_id = ? AND firm_type = ? LIMIT 1`, [input.firm_id, input.firm_type]);
        if (existing.results.length)
            throw new FirmStateError('already_declared', 'This firm already declared that assessor type.');
        await this.control.execute(`INSERT INTO audit_firm_types (firm_type_id, firm_id, firm_type, cyber_ab_id, external_id, created_by, created_at) VALUES (?, ?, ?, ?, ?, ?, ?)`, [generateRandomId('aft'), input.firm_id, input.firm_type, input.firm_type === 'c3pao' ? id : null, input.firm_type === 'c3pao' ? null : id, lead.user_id, this.now().toISOString()]);
    }
    /** The platform operator records the out-of-band verification of a declared type. Set once. */
    async authorizeFirmType(firmId, firmType) {
        const op = await this.requireOperator();
        const r = await this.control.query(`SELECT authorized_at, revoked_at FROM audit_firm_types WHERE firm_id = ? AND firm_type = ? LIMIT 1`, [firmId, firmType]);
        const row = r.results[0];
        if (!row)
            throw new FirmNotFoundError();
        if (row.revoked_at)
            throw new FirmStateError('firm_type_revoked', 'This authorization was revoked.');
        if (row.authorized_at)
            throw new FirmStateError('already_authorized', 'This assessor type is already authorized.');
        await this.control.execute(`UPDATE audit_firm_types SET authorized_at = ?, authorized_by = ? WHERE firm_id = ? AND firm_type = ? AND authorized_at IS NULL AND revoked_at IS NULL`, [this.now().toISOString(), `operator:${op.user_id}`, firmId, firmType]);
    }
    /** The platform operator revokes a type (final). Assessor writes stop at once (re-checked per write). */
    async revokeFirmType(firmId, firmType, reason) {
        await this.requireOperator();
        const why = cleanText(reason, 'reason', 5, 500);
        const res = await this.control.execute(`UPDATE audit_firm_types SET revoked_at = ?, revocation_reason = ? WHERE firm_id = ? AND firm_type = ? AND revoked_at IS NULL`, [this.now().toISOString(), why, firmId, firmType]);
        if ((res.meta?.changes ?? 0) === 0)
            throw new FirmNotFoundError();
    }
    async declineInvitation(input) {
        const r = await this.control.query(`SELECT * FROM firm_engagement_invitations WHERE invitation_id = ? LIMIT 1`, [input.invitation_id]);
        const inv = r.results[0];
        if (!inv)
            throw new FirmNotFoundError();
        const lead = await this.requireLead(inv.firm_id);
        if (inv.status !== 'pending')
            throw new FirmStateError('invitation_decided', 'This invitation was already decided.');
        await this.control.execute(`UPDATE firm_engagement_invitations SET status = 'declined', decided_by = ?, decided_at = ? WHERE invitation_id = ? AND workspace_id = ? AND firm_id = ? AND status = 'pending'`, [lead.user_id, this.now().toISOString(), inv.invitation_id, inv.workspace_id, inv.firm_id]);
    }
}
export class FirmTierRepository {
    control;
    tenant;
    ctx;
    kek;
    nowFn;
    keyPromise = null;
    constructor(control, tenant, ctx, kek, nowFn) {
        this.control = control;
        this.tenant = tenant;
        this.ctx = ctx;
        this.kek = kek;
        this.nowFn = nowFn;
    }
    /**
     * The guard (R2, R3, R5, R9). Every miss is FirmNotFoundError; MFA is
     * checked last. `workspaceId` is `c.get('workspace').workspace_id`;
     * `engagementId` is the path param.
     */
    static async open(controlDb, tenantDb, userId, engagementId, workspaceId, opts) {
        const now = opts.now ?? new Date();
        const nf = new FirmNotFoundError();
        const user = await loadUser(controlDb, userId);
        if (!user || Number(user.is_active) !== 1 || !user.workos_user_id)
            throw nf;
        if (!opts.claims.workos_user_id || opts.claims.workos_user_id !== user.workos_user_id)
            throw nf;
        const engagement = await loadEngagement(controlDb, workspaceId, engagementId);
        if (!engagement)
            throw nf;
        const m = await controlDb.query(`SELECT * FROM engagement_members WHERE engagement_id = ? AND user_id = ? AND firm_id = ? AND workspace_id = ? AND removed_at IS NULL LIMIT 1`, [engagementId, userId, engagement.firm_id, workspaceId]);
        const member = m.results[0];
        if (!member || member.workos_user_id !== user.workos_user_id)
            throw nf;
        const staff = await controlDb.query(`SELECT staff_id, workos_user_id FROM audit_firm_staff WHERE firm_id = ? AND user_id = ? AND removed_at IS NULL AND accepted_at IS NOT NULL LIMIT 1`, [engagement.firm_id, userId]);
        if (!staff.results[0] || staff.results[0].workos_user_id !== user.workos_user_id)
            throw nf;
        if (await holdsClientHat(controlDb, workspaceId, userId)) {
            // R2 read time: a dual role gets nothing; the attempt is logged to this engagement's chain.
            await appendFirmChain(controlDb, { engagement_id: engagementId, firm_id: engagement.firm_id, action: 'independence_rejected', tier: 'independence',
                actor_user_id: userId, subject_kind: 'user', subject_id: userId, detail: { point: 'firm_tier_read' }, at: now.toISOString() });
            throw nf;
        }
        const phase = firmPhase(engagement, member.firm_role, now);
        if (!phase)
            throw nf;
        if (!opts.skipMfa) {
            const stamped = member.mfa_verified_at ? Date.parse(member.mfa_verified_at) : NaN;
            if (!Number.isFinite(stamped) || now.getTime() - stamped > FIRM_MFA_FRESH_MS || stamped > now.getTime() + 60_000) {
                throw new FirmMfaRequiredError();
            }
            // V98-7-3 M1: the roster stamp alone isn't enough — THIS session must be the stepped-up authentication (or a
            // later one) and inside the same freshness window, and never an impersonation. Otherwise step up again.
            const authMs = typeof opts.session?.auth_time === 'number' && Number.isFinite(opts.session.auth_time) ? opts.session.auth_time * 1000 : NaN;
            if (!Number.isFinite(authMs) || opts.session.authentication_method === 'Impersonation'
                || now.getTime() - authMs > FIRM_MFA_FRESH_MS || authMs > now.getTime() + FIRM_STEP_UP_SKEW_MS
                || authMs < stamped - FIRM_STEP_UP_SKEW_MS) {
                throw new FirmStepUpRequiredError();
            }
        }
        const firm = (await controlDb.query(`SELECT firm_id, name FROM audit_firms WHERE firm_id = ?`, [engagement.firm_id])).results[0];
        if (!firm)
            throw nf;
        const ws = (await controlDb.query(`SELECT name FROM workspaces WHERE workspace_id = ? LIMIT 1`, [workspaceId])).results[0];
        const period = await firmPeriodSummary(tenantDb, workspaceId, engagement.audit_period_id).catch(() => null);
        return new FirmTierRepository(controlDb, tenantDb, { engagement, member, phase, window: firmWindow(engagement), firm, client: { name: ws?.name ?? 'Client' }, period }, opts.kek, () => opts.now ?? new Date());
    }
    get engagementId() { return this.ctx.engagement.engagement_id; }
    get workspaceId() { return this.ctx.engagement.workspace_id; }
    get firmId() { return this.ctx.engagement.firm_id; }
    get userId() { return this.ctx.member.user_id; }
    get role() { return this.ctx.member.firm_role; }
    now() { return this.nowFn(); }
    async key() {
        if (!this.keyPromise)
            this.keyPromise = loadFirmKey(this.control, this.kek, this.firmId);
        return this.keyPromise;
    }
    /** Writes allowed in fieldwork only (assembly / custody are read + export, plus `allowAssembly` actions). */
    assertWritable(opts = {}) {
        const p = this.ctx.phase;
        if (p === 'custody' || (p === 'assembly' && !opts.allowAssembly)) {
            throw new FirmForbiddenError('engagement_read_only', p === 'custody' ? 'The firm tier is in custody: read and export only.' : 'The report is released: the file is read-only during assembly.');
        }
        if (opts.lead && !isLeadRole(this.role))
            throw new FirmForbiddenError('firm_lead_required', 'Only the engagement partner or manager can do this.');
    }
    /** R5: after the client revokes, client data is no longer readable from the firm tier. */
    assertClientDataReadable() {
        if (this.ctx.engagement.revoked_at)
            throw new FirmForbiddenError('client_revoked', 'The client revoked access: only the firm tier remains.');
    }
    chain(entry) {
        return appendFirmChain(this.control, { ...entry, engagement_id: this.engagementId, firm_id: this.firmId, actor_user_id: this.userId, at: entry.at ?? this.now().toISOString() });
    }
    // ---- overview / session / roster --------------------------------------------------
    overview() {
        return {
            engagement: this.ctx.engagement, window: this.ctx.window, phase: this.ctx.phase,
            me: { member_id: this.ctx.member.member_id, user_id: this.ctx.member.user_id, firm_role: this.role }, firm: this.ctx.firm,
            client: this.ctx.client, period: this.ctx.period,
        };
    }
    /**
     * Stamp the firm-tier step-up (T6-S1). Only when the session's WorkOS
     * authentication — `auth_time`, epoch seconds, from the sealed session
     * envelope — is ≤ FIRM_STEP_UP_MAX_AGE_MS old and not an impersonation;
     * otherwise FirmStepUpRequiredError (→ 401 `step_up_required`). The stamp is
     * the authentication time itself, so the 12 h window counts from the real
     * sign-in, and it is written to the firm chain with the method.
     */
    async stampMfa(session) {
        const now = this.now().getTime();
        const authMs = typeof session.auth_time === 'number' && Number.isFinite(session.auth_time) ? session.auth_time * 1000 : NaN;
        if (!Number.isFinite(authMs) || now - authMs > FIRM_STEP_UP_MAX_AGE_MS || authMs > now + FIRM_STEP_UP_SKEW_MS || session.authentication_method === 'Impersonation') {
            throw new FirmStepUpRequiredError();
        }
        const at = new Date(Math.min(authMs, now)).toISOString();
        await this.control.execute(`UPDATE engagement_members SET mfa_verified_at = ? WHERE member_id = ? AND workspace_id = ? AND removed_at IS NULL`, [at, this.ctx.member.member_id, this.workspaceId]);
        await this.chain({ action: 'mfa_step_up', tier: 'roster', subject_kind: 'engagement_member', subject_id: this.ctx.member.member_id,
            detail: { auth_time: at, authentication_method: session.authentication_method ?? null } });
        return at;
    }
    async listMembers() {
        const r = await this.control.query(`SELECT m.*, u.display_name, u.email FROM engagement_members m JOIN users u ON u.user_id = m.user_id
        WHERE m.engagement_id = ? AND m.workspace_id = ? ORDER BY m.removed_at IS NOT NULL, m.added_at ASC`, [this.engagementId, this.workspaceId]);
        return r.results;
    }
    /**
     * R1: only the engagement's partner or manager adds members, only from the
     * firm's accepted staff, and (R2) never someone holding a client hat.
     */
    async addMember(input) {
        this.assertWritable({ lead: true });
        if (!FIRM_ROLES.includes(input.firm_role))
            throw new FirmValidationError('invalid_role', 'firm_role must be partner, manager, senior or staff.');
        if (this.role === 'manager' && input.firm_role === 'partner')
            throw new FirmForbiddenError('partner_required', 'Only a partner can add a partner.');
        const staff = (await this.control.query(`SELECT * FROM audit_firm_staff WHERE firm_id = ? AND user_id = ? AND removed_at IS NULL AND accepted_at IS NOT NULL LIMIT 1`, [this.firmId, input.user_id])).results[0];
        if (!staff)
            throw new FirmValidationError('not_firm_staff', 'Add people from your firm’s accepted staff.');
        const now = this.now();
        if (await holdsClientHat(this.control, this.workspaceId, input.user_id)) {
            await this.chain({ action: 'independence_rejected', tier: 'independence', subject_kind: 'user', subject_id: input.user_id, detail: { point: 'engagement_member_insert' } });
            throw new IndependenceRejectedError('engagement_member_insert');
        }
        const open = await this.control.query(`SELECT member_id FROM engagement_members WHERE engagement_id = ? AND workspace_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [this.engagementId, this.workspaceId, input.user_id]);
        if (open.results[0])
            throw new FirmStateError('already_member', 'That person is already on this engagement.');
        const memberId = generateRandomId('emb');
        const at = now.toISOString();
        await this.control.execute(`INSERT INTO engagement_members (member_id, engagement_id, workspace_id, firm_id, user_id, workos_user_id, firm_role, added_by, added_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`, [memberId, this.engagementId, this.workspaceId, this.firmId, input.user_id, staff.workos_user_id, input.firm_role, this.userId, at]);
        await this.chain({ action: 'member_added', tier: 'roster', subject_kind: 'engagement_member', subject_id: memberId, detail: { firm_role: input.firm_role } });
        return (await this.control.query(`SELECT * FROM engagement_members WHERE member_id = ? AND workspace_id = ?`, [memberId, this.workspaceId])).results[0];
    }
    async removeMember(input) {
        this.assertWritable({ lead: true });
        const reason = cleanText(input.reason, 'reason', 5, 2000);
        const r = await this.control.query(`SELECT * FROM engagement_members WHERE member_id = ? AND engagement_id = ? AND workspace_id = ? AND removed_at IS NULL LIMIT 1`, [input.member_id, this.engagementId, this.workspaceId]);
        const m = r.results[0];
        if (!m)
            throw new FirmNotFoundError();
        if (m.member_id === this.ctx.member.member_id)
            throw new FirmStateError('cannot_remove_self', 'Ask another partner or manager to remove you.');
        if (this.role === 'manager' && m.firm_role === 'partner')
            throw new FirmForbiddenError('partner_required', 'Only a partner can remove a partner.');
        const at = this.now().toISOString();
        await this.control.execute(`UPDATE engagement_members SET removed_at = ?, removed_by = ?, removed_reason = ? WHERE member_id = ? AND workspace_id = ? AND removed_at IS NULL`, [at, this.userId, reason, m.member_id, this.workspaceId]);
        await this.chain({ action: 'member_removed', tier: 'roster', subject_kind: 'engagement_member', subject_id: m.member_id, detail: { firm_role: m.firm_role } });
    }
    /** Lead: set the report date (subsequent events run to it; R5 assembly window counts from it). */
    async setReportDate(input) {
        this.assertWritable({ lead: true });
        const d = isoDate(input.report_date, 'report_date');
        if (Date.parse(d) < Date.parse(this.ctx.engagement.engagement_start))
            throw new FirmValidationError('invalid_report_date', 'The report date can’t precede the engagement start.');
        await this.control.execute(`UPDATE assessor_engagements SET report_date = ? WHERE engagement_id = ? AND workspace_id = ?`, [d, this.engagementId, this.workspaceId]);
        await this.chain({ action: 'report_date_set', tier: 'firm', subject_kind: 'engagement', subject_id: this.engagementId, detail: { report_date: d } });
    }
    // ---- PBC (V98-6-3) -----------------------------------------------------------------
    async listPbc() {
        return loadPbc(this.tenant, this.engagementId, 'firm', this.ctx.firm.name, undefined, this.control);
    }
    async getPbc(requestId) {
        const r = await loadPbc(this.tenant, this.engagementId, 'firm', this.ctx.firm.name, requestId, this.control);
        if (!r[0])
            throw new FirmNotFoundError();
        return r[0];
    }
    /** Any member, fieldwork: request something from the client. */
    async createPbc(input) {
        this.assertWritable();
        const title = cleanText(input.title, 'title', 3, 300);
        const detail = input.detail ? cleanText(input.detail, 'detail', 1, 5000) : null;
        const due = input.due_at ? isoDate(input.due_at, 'due_at') : null;
        const n = await this.tenant.query(`SELECT COUNT(*) AS n FROM pbc_requests WHERE engagement_id = ?`, [this.engagementId]);
        const ref = `PBC-${String(Number(n.results[0]?.n ?? 0) + 1).padStart(3, '0')}`;
        const id = generateRandomId('pbc');
        const at = this.now().toISOString();
        await this.tenant.execute(`INSERT INTO pbc_requests (request_id, workspace_id, engagement_id, firm_id, ref, title, detail, control_ref, due_at, requested_by, requested_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [id, this.workspaceId, this.engagementId, this.firmId, ref, title, detail, input.control_ref ?? null, due, this.userId, at]);
        await this.chain({ action: 'pbc_requested', tier: 'client', subject_kind: 'pbc_request', subject_id: id, detail: { ref } });
        return this.getPbc(id);
    }
    /** Return a submission to the client with a reason (status `returned`). */
    async returnPbc(requestId, reason) {
        this.assertWritable();
        const why = cleanText(reason, 'reason', 5, 2000);
        const row = await loadPbcRow(this.tenant, this.engagementId, requestId);
        if (row.status !== 'submitted')
            throw new FirmStateError('not_submitted', 'Only a submitted request can be returned.');
        const at = this.now().toISOString();
        await this.tenant.batch([
            { sql: `UPDATE pbc_requests SET status = 'returned', returned_at = ?, return_reason = ? WHERE request_id = ? AND engagement_id = ? AND workspace_id = ? AND status = 'submitted'`, params: [at, why, requestId, this.engagementId, this.workspaceId] },
            { sql: `INSERT INTO pbc_messages (message_id, workspace_id, engagement_id, firm_id, request_id, author_user_id, author_side, body, posted_at) VALUES (?, ?, ?, ?, ?, ?, 'firm', ?, ?)`,
                params: [generateRandomId('pbm'), this.workspaceId, this.engagementId, this.firmId, requestId, this.userId, `Returned: ${why}`, at] },
        ]);
        await this.chain({ action: 'pbc_returned', tier: 'client', subject_kind: 'pbc_request', subject_id: requestId, detail: { version: row.latest_version } });
        return this.getPbc(requestId);
    }
    async acceptPbc(requestId) {
        this.assertWritable();
        const row = await loadPbcRow(this.tenant, this.engagementId, requestId);
        if (row.status !== 'submitted')
            throw new FirmStateError('not_submitted', 'Only a submitted request can be accepted.');
        await this.tenant.execute(`UPDATE pbc_requests SET status = 'accepted', accepted_at = ?, accepted_by = ? WHERE request_id = ? AND engagement_id = ? AND workspace_id = ? AND status = 'submitted'`, [this.now().toISOString(), this.userId, requestId, this.engagementId, this.workspaceId]);
        await this.chain({ action: 'pbc_accepted', tier: 'client', subject_kind: 'pbc_request', subject_id: requestId, detail: { version: row.latest_version } });
        return this.getPbc(requestId);
    }
    async withdrawPbc(requestId) {
        this.assertWritable();
        const row = await loadPbcRow(this.tenant, this.engagementId, requestId);
        if (row.status === 'accepted' || row.status === 'withdrawn')
            throw new FirmStateError('request_final', 'This request is final.');
        await this.tenant.execute(`UPDATE pbc_requests SET status = 'withdrawn', withdrawn_at = ? WHERE request_id = ? AND engagement_id = ? AND workspace_id = ?`, [this.now().toISOString(), requestId, this.engagementId, this.workspaceId]);
        await this.chain({ action: 'pbc_withdrawn', tier: 'client', subject_kind: 'pbc_request', subject_id: requestId });
        return this.getPbc(requestId);
    }
    async postPbcMessage(requestId, body) {
        this.assertWritable({ allowAssembly: true });
        await loadPbcRow(this.tenant, this.engagementId, requestId);
        await insertPbcMessage(this.tenant, { workspace_id: this.workspaceId, engagement_id: this.engagementId, firm_id: this.firmId }, requestId, 'firm', this.userId, body, this.now().toISOString());
        return this.getPbc(requestId);
    }
    // ---- populations, reconciliation, samples (V98-6-4) ---------------------------------
    async engagementPopulation(populationId) {
        this.assertClientDataReadable();
        const pop = (await engagementPopulations(this.tenant, this.workspaceId, this.ctx.engagement.audit_period_id)).find((p) => p.population_id === populationId);
        if (!pop)
            throw new FirmNotFoundError();
        const items = (await getPopulationItems(this.tenant, this.workspaceId, populationId)) ?? [];
        const intact = (await populationContentSha256(items)) === pop.content_sha256 && items.length === pop.item_count;
        return { ...pop, keys: items.map((i) => i.item_key).sort(compareKeys), intact };
    }
    async listPopulations() {
        this.assertClientDataReadable();
        const key = await this.key();
        const out = [];
        for (const p of await engagementPopulations(this.tenant, this.workspaceId, this.ctx.engagement.audit_period_id)) {
            const rec = (await this.tenant.query(`SELECT reconciliation_id, population_sha256, independence, control_total, memo_enc, signed_by, signed_at FROM reconciliation_signoffs
          WHERE engagement_id = ? AND population_id = ? AND superseded_at IS NULL ORDER BY signed_at DESC LIMIT 1`, [this.engagementId, p.population_id])).results[0];
            const samples = await this.tenant.query(`SELECT * FROM sample_lineage WHERE engagement_id = ? AND population_id = ? ORDER BY created_at ASC, lineage_id ASC`, [this.engagementId, p.population_id]);
            out.push({
                ...p,
                provenance: await latestProvenance(this.tenant, this.engagementId, p.population_id),
                reconciliation: rec ? {
                    reconciliation_id: rec.reconciliation_id, current: rec.population_sha256 === p.content_sha256, independence: rec.independence,
                    independence_label: INDEPENDENCE_LABEL[rec.independence],
                    ...reconciliationFigures(Number(rec.control_total), (await key.openField([this.engagementId, 'reconciliation', rec.reconciliation_id, 'memo'], rec.memo_enc)) ?? ''),
                    signed_by: rec.signed_by, signed_by_name: (await displayNames(this.control, [rec.signed_by]))(rec.signed_by), signed_at: rec.signed_at,
                } : null,
                samples: samples.results.map(toSampleView),
            });
        }
        return out;
    }
    /**
     * Reconciliation sign-off memo: the population's count is agreed to an
     * INDEPENDENT control total (T6-S2). The caller names that total's source —
     * a total "from" the population itself is an echo and is refused — and,
     * when the totals differ, explains the difference (otherwise refused). The
     * memo records the provenance it relied on and carries its independence
     * label ("adjusted · not independent" when the client adjusted the list).
     * Supersedes the previous sign-off.
     */
    async reconcilePopulation(populationId, input) {
        this.assertWritable();
        const pop = await this.engagementPopulation(populationId);
        if (!pop.intact)
            throw new FirmStateError('population_tampered', 'The population no longer matches its recorded hash.');
        const prov = await latestProvenance(this.tenant, this.engagementId, populationId);
        if (!prov)
            throw new FirmStateError('no_provenance', 'The client hasn’t recorded where this population came from.');
        const total = typeof input.control_total === 'number' ? input.control_total : NaN;
        if (!Number.isInteger(total) || total < 0)
            throw new FirmValidationError('invalid_control_total', 'Enter the control total as a whole number.');
        const source = typeof input.control_total_source === 'string' ? input.control_total_source.trim() : '';
        if (source.length < 3 || source.length > 300) {
            throw new FirmValidationError('control_total_source_required', 'Name the independent source of the control total (3–300 characters).');
        }
        if (ECHOED_SOURCE.test(source)) {
            throw new FirmValidationError('control_total_not_independent', 'The control total must come from a source other than this population.');
        }
        let difference = null;
        if (total !== pop.item_count) {
            const why = typeof input.difference_explanation === 'string' ? input.difference_explanation.trim() : '';
            if (why.length < 20) {
                throw new FirmValidationError('does_not_reconcile', `The population has ${pop.item_count} items; the control total is ${total}. Explain the difference (at least 20 characters) or correct the total.`);
            }
            difference = cleanText(why, 'difference_explanation', 20, 5000);
        }
        const memoText = cleanText(input.memo, 'memo', 10, 10000);
        // The `control_total` column holds the RECONCILED total (the schema checks
        // it equals the item count); the independent source's raw figure and any
        // explained difference live in the sealed memo, so nothing is overwritten.
        const memo = JSON.stringify({ v: 2, memo: memoText, control_total_source: source, source_total: total, difference_explanation: difference });
        const id = generateRandomId('rcn');
        const at = this.now().toISOString();
        const key = await this.key();
        await this.tenant.batch([
            { sql: `UPDATE reconciliation_signoffs SET superseded_at = ? WHERE engagement_id = ? AND population_id = ? AND superseded_at IS NULL`, params: [at, this.engagementId, populationId] },
            { sql: `INSERT INTO reconciliation_signoffs (reconciliation_id, workspace_id, engagement_id, firm_id, population_id, provenance_id, population_sha256, item_count, control_total,
                independence, memo_enc, signed_by, signed_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [id, this.workspaceId, this.engagementId, this.firmId, populationId, prov.provenance_id, pop.content_sha256, pop.item_count, pop.item_count,
                    prov.independence, await key.sealField([this.engagementId, 'reconciliation', id, 'memo'], memo), this.userId, at] },
        ]);
        await this.chain({ action: 'population_reconciled', tier: 'firm', subject_kind: 'population', subject_id: populationId, detail: { independence: prov.independence, agrees: difference === null } });
        return (await this.listPopulations()).find((p) => p.population_id === populationId);
    }
    /** The current reconciliation, or throw: an unreconciled population can't be sampled. */
    async requireReconciled(pop) {
        if (!pop.intact)
            throw new FirmStateError('population_tampered', 'The population no longer matches its recorded hash.');
        const r = (await this.tenant.query(`SELECT reconciliation_id FROM reconciliation_signoffs WHERE engagement_id = ? AND population_id = ? AND superseded_at IS NULL AND population_sha256 = ? LIMIT 1`, [this.engagementId, pop.population_id, pop.content_sha256])).results[0];
        if (!r)
            throw new FirmStateError('unreconciled', 'Reconcile this population before drawing a sample.');
        return r.reconciliation_id;
    }
    async insertSample(input) {
        const id = generateRandomId('smp');
        const at = this.now().toISOString();
        await this.tenant.execute(`INSERT INTO sample_lineage (lineage_id, workspace_id, engagement_id, firm_id, population_id, population_sha256, reconciliation_id, parent_lineage_id, kind, method, seed,
         sample_size, selected_keys_json, rationale, created_by, created_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [id, this.workspaceId, this.engagementId, this.firmId, input.population.population_id, input.population.content_sha256, input.reconciliation_id, input.parent,
            input.kind, input.method, input.seed, input.keys.length, JSON.stringify(input.keys), input.rationale, this.userId, at]);
        await this.chain({ action: `sample_${input.kind}`, tier: 'firm', subject_kind: 'sample', subject_id: id, detail: { population_id: input.population.population_id, size: input.keys.length, parent: input.parent } });
        return this.getSample(id);
    }
    async getSample(lineageId) {
        const r = (await this.tenant.query(`SELECT * FROM sample_lineage WHERE lineage_id = ? AND engagement_id = ? LIMIT 1`, [lineageId, this.engagementId])).results[0];
        if (!r)
            throw new FirmNotFoundError();
        return toSampleView(r);
    }
    static seedOf(seed) {
        if (seed === undefined || seed === null || seed === '')
            return generateSeed();
        const s = String(seed);
        if (!/^[\x21-\x7e]{1,128}$/.test(s))
            throw new FirmValidationError('invalid_seed', 'seed must be 1–128 printable ASCII characters without spaces.');
        return s;
    }
    /** Original draw (seeded, reproducible). Unreconciled → refused. */
    async drawSample(populationId, input) {
        this.assertWritable();
        const pop = await this.engagementPopulation(populationId);
        const rec = await this.requireReconciled(pop);
        const seed = FirmTierRepository.seedOf(input.seed);
        let keys;
        try {
            keys = await selectSampleKeys(pop.keys, Number(input.sample_size), seed, pop.content_sha256);
        }
        catch (err) {
            throw new FirmValidationError('invalid_sample_size', err.message);
        }
        return this.insertSample({ population: pop, reconciliation_id: rec, parent: null, kind: 'original', method: SAMPLE_METHOD, seed, keys, rationale: null });
    }
    /**
     * Extend: the parent's keys plus `additional` new ones drawn from the rest
     * of the population. Tests on the parent move to the extension, so every
     * result already recorded stays with its item.
     */
    async extendSample(lineageId, input) {
        this.assertWritable();
        const parent = await this.getSample(lineageId);
        const rationale = cleanText(input.rationale, 'rationale', 10, 2000);
        const pop = await this.engagementPopulation((await this.tenant.query(`SELECT population_id FROM sample_lineage WHERE lineage_id = ? AND workspace_id = ?`, [lineageId, this.workspaceId])).results[0].population_id);
        const rec = await this.requireReconciled(pop);
        const taken = new Set(parent.selected_keys);
        const rest = pop.keys.filter((k) => !taken.has(k));
        const seed = FirmTierRepository.seedOf(input.seed);
        let more;
        try {
            more = await selectSampleKeys(rest, Number(input.additional), seed, pop.content_sha256);
        }
        catch (err) {
            throw new FirmValidationError('invalid_sample_size', err.message);
        }
        if (more.length === 0)
            throw new FirmValidationError('invalid_sample_size', 'Extend by at least one item.');
        const child = await this.insertSample({ population: pop, reconciliation_id: rec, parent: lineageId, kind: 'extension', method: SAMPLE_METHOD, seed, keys: [...parent.selected_keys, ...more], rationale });
        await this.tenant.execute(`UPDATE workpaper_tests SET sample_id = ?, updated_at = ? WHERE engagement_id = ? AND sample_id = ?`, [child.lineage_id, this.now().toISOString(), this.engagementId, lineageId]);
        return child;
    }
    /** Redraw: a fresh selection with a rationale; the original and its results stay as they were. */
    async redrawSample(lineageId, input) {
        this.assertWritable();
        await this.getSample(lineageId);
        const rationale = cleanText(input.rationale, 'rationale', 10, 2000);
        const pop = await this.engagementPopulation((await this.tenant.query(`SELECT population_id FROM sample_lineage WHERE lineage_id = ? AND workspace_id = ?`, [lineageId, this.workspaceId])).results[0].population_id);
        const rec = await this.requireReconciled(pop);
        const seed = FirmTierRepository.seedOf(input.seed);
        let keys;
        try {
            keys = await selectSampleKeys(pop.keys, Number(input.sample_size), seed, pop.content_sha256);
        }
        catch (err) {
            throw new FirmValidationError('invalid_sample_size', err.message);
        }
        return this.insertSample({ population: pop, reconciliation_id: rec, parent: lineageId, kind: 'redraw', method: SAMPLE_METHOD, seed, keys, rationale });
    }
    /** Import a selection made elsewhere (CSV with a `key` / `item_key` column). Every key must be in the population. */
    async importSelections(populationId, input) {
        this.assertWritable();
        const rationale = cleanText(input.rationale, 'rationale', 10, 2000);
        const pop = await this.engagementPopulation(populationId);
        const rec = await this.requireReconciled(pop);
        let parsed;
        try {
            parsed = parsePopulationCsv(String(input.csv ?? '')).items.map((i) => i.item_key);
        }
        catch (err) {
            throw new FirmValidationError('invalid_csv', err.message);
        }
        const keys = [...new Set(parsed)];
        if (keys.length === 0)
            throw new FirmValidationError('invalid_csv', 'The CSV selects no items.');
        const inPop = new Set(pop.keys);
        const unknown = keys.filter((k) => !inPop.has(k));
        if (unknown.length)
            throw new FirmValidationError('unknown_keys', `Not in the population: ${unknown.slice(0, 5).join(', ')}${unknown.length > 5 ? '…' : ''}`);
        return this.insertSample({ population: pop, reconciliation_id: rec, parent: null, kind: 'imported', method: 'imported_csv', seed: null, keys: keys.sort(compareKeys), rationale });
    }
    /** Send the selection to the client (it becomes client-visible). One-way. */
    async requestSelection(lineageId) {
        this.assertWritable();
        const s = await this.getSample(lineageId);
        if (s.requested_at)
            return s;
        await this.tenant.execute(`UPDATE sample_lineage SET requested_at = ?, requested_by = ? WHERE lineage_id = ? AND workspace_id = ? AND requested_at IS NULL`, [this.now().toISOString(), this.userId, lineageId, this.workspaceId]);
        await this.chain({ action: 'selection_requested', tier: 'client', subject_kind: 'sample', subject_id: lineageId });
        return this.getSample(lineageId);
    }
    // ---- attribute-level testing (V98-6-5; firm-only) ------------------------------------
    /** Firm-owned templates (control plane, per firm, encrypted). */
    async listTemplates() {
        const key = await this.key();
        const r = await this.control.query(`SELECT template_id, version, title_enc, attributes_enc, created_by, created_at, retired_at FROM workpaper_templates WHERE firm_id = ? ORDER BY created_at ASC`, [this.firmId]);
        const out = [];
        for (const t of r.results) {
            out.push({
                template_id: t.template_id, version: Number(t.version), created_by: t.created_by, created_at: t.created_at, retired_at: t.retired_at,
                title: (await key.openField(['firm', 'template', t.template_id, 'title'], t.title_enc)) ?? '',
                attributes: JSON.parse((await key.openField(['firm', 'template', t.template_id, 'attributes'], t.attributes_enc)) ?? '[]'),
            });
        }
        return out;
    }
    /** A firm lead adds a template for the whole firm. */
    async createTemplate(input) {
        this.assertWritable({ lead: true });
        const title = cleanText(input.title, 'title', 3, 300);
        const attributes = cleanAttributes(input.attributes);
        const key = await this.key();
        const id = generateRandomId('wtp');
        await this.control.execute(`INSERT INTO workpaper_templates (template_id, firm_id, version, title_enc, attributes_enc, created_by, created_at) VALUES (?, ?, 1, ?, ?, ?, ?)`, [id, this.firmId, await key.sealField(['firm', 'template', id, 'title'], title), await key.sealField(['firm', 'template', id, 'attributes'], JSON.stringify(attributes)),
            this.userId, this.now().toISOString()]);
        return (await this.listTemplates()).find((t) => t.template_id === id);
    }
    async retireTemplate(templateId) {
        this.assertWritable({ lead: true });
        const r = await this.control.execute(`UPDATE workpaper_templates SET retired_at = ? WHERE template_id = ? AND firm_id = ? AND retired_at IS NULL`, [this.now().toISOString(), templateId, this.firmId]);
        if ((r.meta?.changes ?? 0) === 0)
            throw new FirmNotFoundError();
    }
    async listTests() {
        const key = await this.key();
        const r = await this.tenant.query(`SELECT t.test_id, t.control_ref, t.title_enc, t.status, t.sample_id, t.content_version, t.created_by,
              (SELECT COUNT(*) FROM workpaper_review_notes n WHERE n.test_id = t.test_id AND n.status <> 'cleared') AS open_notes
         FROM workpaper_tests t WHERE t.engagement_id = ? ORDER BY t.control_ref ASC, t.created_at ASC`, [this.engagementId]);
        const out = [];
        for (const t of r.results) {
            out.push({ test_id: t.test_id, control_ref: t.control_ref, status: t.status, sample_id: t.sample_id, content_version: Number(t.content_version), created_by: t.created_by,
                open_notes: Number(t.open_notes), title: (await key.openField([this.engagementId, 'workpaper_test', t.test_id, 'title'], t.title_enc)) ?? '' });
        }
        return out;
    }
    /**
     * Start a test of a control on a sample from a firm template. The template is
     * frozen into the test. Population / connector values become HINTS on each
     * cell; every result starts empty — a person records each one.
     */
    async createTest(input) {
        this.assertWritable();
        const controlRef = cleanText(input.control_ref, 'control_ref', 1, 100);
        const tpl = (await this.listTemplates()).find((t) => t.template_id === input.template_id && !t.retired_at);
        if (!tpl)
            throw new FirmValidationError('unknown_template', 'Pick an active template of your firm.');
        const sample = await this.getSample(String(input.sample_id ?? ''));
        const id = generateRandomId('wpt');
        const at = this.now().toISOString();
        const key = await this.key();
        // The row already shows the control: the default title is the template's (no "CC6.3 · CC6.3 …").
        const title = input.title ? cleanText(input.title, 'title', 3, 300) : tpl.title;
        // Hints: the population's recorded attribute values for each selected item (e.g. a connector's value).
        this.assertClientDataReadable();
        const popId = (await this.tenant.query(`SELECT population_id FROM sample_lineage WHERE lineage_id = ? AND workspace_id = ?`, [sample.lineage_id, this.workspaceId])).results[0].population_id;
        const items = new Map(((await getPopulationItems(this.tenant, this.workspaceId, popId)) ?? []).map((i) => [i.item_key, i.attributes]));
        const stmts = [{
                sql: `INSERT INTO workpaper_tests (test_id, workspace_id, engagement_id, firm_id, control_ref, template_id, template_version, template_snapshot_enc, title_enc, sample_id, created_by, created_at, updated_at)
            VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [id, this.workspaceId, this.engagementId, this.firmId, controlRef, tpl.template_id, tpl.version,
                    await key.sealField([this.engagementId, 'workpaper_test', id, 'template'], JSON.stringify(tpl.attributes)),
                    await key.sealField([this.engagementId, 'workpaper_test', id, 'title'], title), sample.lineage_id, this.userId, at, at],
            }];
        for (const itemKey of sample.selected_keys) {
            for (const a of tpl.attributes) {
                const v = items.get(itemKey)?.[a.key];
                if (v === undefined || v === null)
                    continue;
                const rid = generateRandomId('war');
                stmts.push({
                    sql: `INSERT INTO workpaper_attribute_results (result_id, workspace_id, engagement_id, firm_id, test_id, item_key, attribute_key, hint_enc) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
                    params: [rid, this.workspaceId, this.engagementId, this.firmId, id, itemKey, a.key, await key.sealField([this.engagementId, 'workpaper_result', `${id}|${itemKey}|${a.key}`, 'hint'], String(v))],
                });
            }
        }
        await this.tenant.batch(stmts);
        await this.chain({ action: 'test_created', tier: 'firm', subject_kind: 'workpaper_test', subject_id: id, detail: { control_ref: controlRef, sample_id: sample.lineage_id } });
        return this.getTest(id);
    }
    async getTest(testId) {
        const t = (await this.tenant.query(`SELECT * FROM workpaper_tests WHERE test_id = ? AND engagement_id = ? LIMIT 1`, [testId, this.engagementId])).results[0];
        if (!t)
            throw new FirmNotFoundError();
        const key = await this.key();
        const attributes = JSON.parse((await key.openField([this.engagementId, 'workpaper_test', testId, 'template'], t.template_snapshot_enc)) ?? '[]');
        const sampleKeys = t.sample_id ? (await this.getSample(t.sample_id)).selected_keys : [];
        const rows = await this.tenant.query(`SELECT item_key, attribute_key, result, note_enc, hint_enc, set_by, set_at FROM workpaper_attribute_results WHERE test_id = ? AND workspace_id = ?`, [testId, this.workspaceId]);
        const byCell = new Map(rows.results.map((r) => [`${r.item_key}|${r.attribute_key}`, r]));
        const cells = [];
        for (const item of sampleKeys) {
            for (const a of attributes) {
                const r = byCell.get(`${item}|${a.key}`);
                cells.push({
                    item_key: item, attribute_key: a.key, result: r?.result ?? null,
                    note: r?.note_enc ? await key.openField([this.engagementId, 'workpaper_result', `${testId}|${item}|${a.key}`, 'note'], r.note_enc) : null,
                    hint: r?.hint_enc ? await key.openField([this.engagementId, 'workpaper_result', `${testId}|${item}|${a.key}`, 'hint'], r.hint_enc) : null,
                    set_by: r?.set_by ?? null, set_at: r?.set_at ?? null,
                });
            }
        }
        const so = await this.tenant.query(`SELECT signoff_id, kind, user_id, firm_role, content_version, signed_at FROM workpaper_signoffs WHERE test_id = ? AND workspace_id = ? AND superseded_at IS NULL ORDER BY signed_at ASC`, [testId, this.workspaceId]);
        return {
            test_id: t.test_id, control_ref: t.control_ref, template_id: t.template_id, template_version: t.template_version === null ? null : Number(t.template_version),
            title: (await key.openField([this.engagementId, 'workpaper_test', testId, 'title'], t.title_enc)) ?? '', attributes, sample_id: t.sample_id, item_keys: sampleKeys,
            status: t.status, content_version: Number(t.content_version), cells,
            signoffs: await (async () => {
                const name = await displayNames(this.control, so.results.map((x) => x.user_id));
                return so.results.map((x) => ({ ...x, content_version: Number(x.content_version), display_name: name(x.user_id) }));
            })(),
            review_notes: await this.reviewNotes(testId), created_by: t.created_by, created_at: t.created_at,
        };
    }
    async reviewNotes(testId) {
        const key = await this.key();
        const r = await this.tenant.query(`SELECT note_id, test_id, body_enc, author_user_id, created_at, status, response_enc, addressed_by, cleared_by FROM workpaper_review_notes WHERE test_id = ? AND workspace_id = ? ORDER BY created_at ASC`, [testId, this.workspaceId]);
        const out = [];
        const name = await displayNames(this.control, r.results.flatMap((n) => [n.author_user_id, n.addressed_by, n.cleared_by]));
        for (const n of r.results) {
            out.push({ note_id: n.note_id, test_id: n.test_id, author_user_id: n.author_user_id, created_at: n.created_at, status: n.status, addressed_by: n.addressed_by, cleared_by: n.cleared_by,
                author_name: name(n.author_user_id), addressed_by_name: n.addressed_by ? name(n.addressed_by) : null, cleared_by_name: n.cleared_by ? name(n.cleared_by) : null,
                body: (await key.openField([this.engagementId, 'review_note', n.note_id, 'body'], n.body_enc)) ?? '',
                response: n.response_enc ? await key.openField([this.engagementId, 'review_note', n.note_id, 'response'], n.response_enc) : null });
        }
        return out;
    }
    /** A change to the working paper: bump the content version and supersede live sign-offs. */
    async touchTest(testId, at) {
        return [
            { sql: `UPDATE workpaper_signoffs SET superseded_at = ? WHERE test_id = ? AND workspace_id = ? AND superseded_at IS NULL`, params: [at, testId, this.workspaceId] },
            { sql: `UPDATE workpaper_tests SET content_version = content_version + 1, status = 'in_progress', updated_at = ? WHERE test_id = ? AND workspace_id = ?`, params: [at, testId, this.workspaceId] },
        ];
    }
    /** A person records one result (pass / fail / n/a) with an optional note. */
    async recordResult(testId, input) {
        this.assertWritable();
        const t = await this.getTest(testId);
        if (!t.item_keys.includes(input.item_key))
            throw new FirmValidationError('unknown_item', 'That item is not in this test’s sample.');
        if (!t.attributes.some((a) => a.key === input.attribute_key))
            throw new FirmValidationError('unknown_attribute', 'That attribute is not in this test’s template.');
        if (!['pass', 'fail', 'na'].includes(input.result))
            throw new FirmValidationError('invalid_result', 'result must be pass, fail or na.');
        const key = await this.key();
        const at = this.now().toISOString();
        const cellId = `${testId}|${input.item_key}|${input.attribute_key}`;
        const note = input.note ? await key.sealField([this.engagementId, 'workpaper_result', cellId, 'note'], cleanText(input.note, 'note', 1, 2000)) : null;
        const existing = (await this.tenant.query(`SELECT result_id FROM workpaper_attribute_results WHERE test_id = ? AND workspace_id = ? AND item_key = ? AND attribute_key = ? LIMIT 1`, [testId, this.workspaceId, input.item_key, input.attribute_key])).results[0];
        const write = existing
            ? { sql: `UPDATE workpaper_attribute_results SET result = ?, note_enc = ?, set_by = ?, set_at = ? WHERE result_id = ? AND workspace_id = ?`, params: [input.result, note, this.userId, at, existing.result_id, this.workspaceId] }
            : { sql: `INSERT INTO workpaper_attribute_results (result_id, workspace_id, engagement_id, firm_id, test_id, item_key, attribute_key, result, note_enc, set_by, set_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [generateRandomId('war'), this.workspaceId, this.engagementId, this.firmId, testId, input.item_key, input.attribute_key, input.result, note, this.userId, at] };
        await this.tenant.batch([write, ...(await this.touchTest(testId, at))]);
        return this.getTest(testId);
    }
    async contentHash(t) {
        return sha256Hex(canonicalJson({ test_id: t.test_id, content_version: t.content_version, cells: t.cells.map((c) => [c.item_key, c.attribute_key, c.result, c.note]) }));
    }
    /**
     * Preparer sign-off: every cell has a result. Reviewer sign-off: senior or
     * above, not the preparer, a live preparer sign-off on the same content
     * version, and no review note left uncleared. Allowed during assembly.
     */
    async signoff(testId, kind) {
        this.assertWritable({ allowAssembly: true });
        const t = await this.getTest(testId);
        if (kind !== 'preparer' && kind !== 'reviewer')
            throw new FirmValidationError('invalid_kind', 'kind must be preparer or reviewer.');
        if (t.cells.length === 0 || t.cells.some((c) => c.result === null))
            throw new FirmStateError('incomplete', 'Record a result for every item and attribute first.');
        const preparer = t.signoffs.find((s) => s.kind === 'preparer');
        if (kind === 'preparer' && preparer)
            throw new FirmStateError('already_prepared', 'Already signed off as prepared.');
        if (kind === 'reviewer') {
            if (this.role === 'staff')
                throw new FirmForbiddenError('reviewer_role', 'A reviewer is a senior, manager or partner.');
            if (!preparer)
                throw new FirmStateError('not_prepared', 'The preparer signs off first.');
            if (preparer.user_id === this.userId)
                throw new FirmForbiddenError('reviewer_is_preparer', 'The reviewer can’t be the preparer.');
            if (t.review_notes.some((n) => n.status !== 'cleared'))
                throw new FirmStateError('open_review_notes', 'Clear every review note first.');
            if (t.signoffs.some((s) => s.kind === 'reviewer'))
                throw new FirmStateError('already_reviewed', 'Already reviewed.');
        }
        const at = this.now().toISOString();
        await this.tenant.batch([
            { sql: `INSERT INTO workpaper_signoffs (signoff_id, workspace_id, engagement_id, firm_id, test_id, kind, user_id, firm_role, content_version, content_sha256, signed_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [generateRandomId('wso'), this.workspaceId, this.engagementId, this.firmId, testId, kind, this.userId, this.role, t.content_version, await this.contentHash(t), at] },
            { sql: `UPDATE workpaper_tests SET status = ?, updated_at = ? WHERE test_id = ? AND workspace_id = ?`, params: [kind === 'preparer' ? 'prepared' : 'reviewed', at, testId, this.workspaceId] },
        ]);
        await this.chain({ action: `signoff_${kind}`, tier: 'firm', subject_kind: 'workpaper_test', subject_id: testId, detail: { content_version: t.content_version } });
        return this.getTest(testId);
    }
    /** Review notes are raised by seniors and above. */
    async addReviewNote(testId, body) {
        this.assertWritable({ allowAssembly: true });
        if (this.role === 'staff')
            throw new FirmForbiddenError('reviewer_role', 'Review notes come from a senior, manager or partner.');
        await this.getTest(testId);
        const key = await this.key();
        const id = generateRandomId('wrn');
        await this.tenant.execute(`INSERT INTO workpaper_review_notes (note_id, workspace_id, engagement_id, firm_id, test_id, body_enc, author_user_id, created_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`, [id, this.workspaceId, this.engagementId, this.firmId, testId, await key.sealField([this.engagementId, 'review_note', id, 'body'], cleanText(body, 'body', 3, 5000)), this.userId, this.now().toISOString()]);
        await this.chain({ action: 'review_note_added', tier: 'firm', subject_kind: 'review_note', subject_id: id });
        return this.getTest(testId);
    }
    async addressReviewNote(noteId, response) {
        this.assertWritable({ allowAssembly: true });
        const n = (await this.tenant.query(`SELECT test_id, status FROM workpaper_review_notes WHERE note_id = ? AND engagement_id = ? LIMIT 1`, [noteId, this.engagementId])).results[0];
        if (!n)
            throw new FirmNotFoundError();
        if (n.status !== 'open')
            throw new FirmStateError('note_not_open', 'Only an open note can be addressed.');
        const key = await this.key();
        await this.tenant.execute(`UPDATE workpaper_review_notes SET status = 'addressed', response_enc = ?, addressed_by = ?, addressed_at = ? WHERE note_id = ? AND workspace_id = ? AND status = 'open'`, [await key.sealField([this.engagementId, 'review_note', noteId, 'response'], cleanText(response, 'response', 2, 5000)), this.userId, this.now().toISOString(), noteId, this.workspaceId]);
        return this.getTest(n.test_id);
    }
    /** The note's author (or a lead) clears it. */
    async clearReviewNote(noteId) {
        this.assertWritable({ allowAssembly: true });
        const n = (await this.tenant.query(`SELECT test_id, status, author_user_id FROM workpaper_review_notes WHERE note_id = ? AND engagement_id = ? LIMIT 1`, [noteId, this.engagementId])).results[0];
        if (!n)
            throw new FirmNotFoundError();
        if (n.author_user_id !== this.userId && !isLeadRole(this.role))
            throw new FirmForbiddenError('note_author_required', 'The note’s author or the engagement lead clears it.');
        if (n.status === 'cleared')
            throw new FirmStateError('note_cleared', 'Already cleared.');
        await this.tenant.execute(`UPDATE workpaper_review_notes SET status = 'cleared', cleared_by = ?, cleared_at = ? WHERE note_id = ? AND workspace_id = ?`, [this.userId, this.now().toISOString(), noteId, this.workspaceId]);
        await this.chain({ action: 'review_note_cleared', tier: 'firm', subject_kind: 'review_note', subject_id: noteId });
        return this.getTest(n.test_id);
    }
    /**
     * Reviewer queue for the caller: prepared tests awaiting review (not their
     * own preparation) and addressed notes they raised, awaiting clearing.
     */
    async reviewerQueue() {
        const key = await this.key();
        const awaiting = this.role === 'staff' ? { results: [] } : await this.tenant.query(`SELECT t.test_id, t.control_ref, t.title_enc, s.user_id AS prepared_by, s.signed_at AS prepared_at FROM workpaper_tests t
         JOIN workpaper_signoffs s ON s.test_id = t.test_id AND s.kind = 'preparer' AND s.superseded_at IS NULL
        WHERE t.engagement_id = ? AND t.status = 'prepared' AND s.user_id <> ? ORDER BY s.signed_at ASC`, [this.engagementId, this.userId]);
        const notes = await this.tenant.query(`SELECT note_id, test_id, body_enc, response_enc FROM workpaper_review_notes WHERE engagement_id = ? AND author_user_id = ? AND status = 'addressed' ORDER BY created_at ASC`, [this.engagementId, this.userId]);
        return {
            awaiting_review: await Promise.all(awaiting.results.map(async (a) => ({ test_id: a.test_id, control_ref: a.control_ref, prepared_by: a.prepared_by, prepared_by_name: (await displayNames(this.control, [a.prepared_by]))(a.prepared_by), prepared_at: a.prepared_at,
                title: (await key.openField([this.engagementId, 'workpaper_test', a.test_id, 'title'], a.title_enc)) ?? '' }))),
            notes_to_clear: await Promise.all(notes.results.map(async (n) => ({ note_id: n.note_id, test_id: n.test_id,
                body: (await key.openField([this.engagementId, 'review_note', n.note_id, 'body'], n.body_enc)) ?? '',
                response: n.response_enc ? await key.openField([this.engagementId, 'review_note', n.note_id, 'response'], n.response_enc) : null }))),
        };
    }
    // ---- auditor exceptions (V98-6-6) ----------------------------------------------------
    async listExceptions() {
        const key = await this.key();
        const sd = await currentSystemDescription(this.tenant, this.workspaceId, this.ctx.engagement.audit_period_id);
        const ex = await this.tenant.query(`SELECT exception_id, control_ref, test_id, status, latest_version, issued_version FROM audit_exceptions WHERE engagement_id = ? ORDER BY control_ref, created_at`, [this.engagementId]);
        const out = [];
        for (const x of ex.results) {
            const vs = await this.tenant.query(`SELECT version, wording_enc, created_by, created_at, issued_at FROM audit_exception_versions WHERE exception_id = ? AND workspace_id = ? ORDER BY version ASC`, [x.exception_id, this.workspaceId]);
            const versions = [];
            for (const v of vs.results) {
                versions.push({ version: Number(v.version), created_by: v.created_by, created_at: v.created_at, issued_at: v.issued_at,
                    wording: (await key.openField([this.engagementId, 'exception', x.exception_id, `wording:v${v.version}`], v.wording_enc)) ?? '' });
            }
            const response = await activeResponse(this.tenant, x.exception_id);
            out.push({
                exception_id: x.exception_id, control_ref: x.control_ref, test_id: x.test_id, status: x.status, latest_version: Number(x.latest_version),
                issued_version: x.issued_version === null ? null : Number(x.issued_version), versions, response,
                mismatch: x.status === 'issued' ? responseMismatch(x.control_ref, Number(x.issued_version), response, sd) : [],
            });
        }
        return out;
    }
    async exceptionRow(exceptionId) {
        const x = (await this.tenant.query(`SELECT exception_id, control_ref, status, latest_version, issued_version FROM audit_exceptions WHERE exception_id = ? AND engagement_id = ? LIMIT 1`, [exceptionId, this.engagementId])).results[0];
        if (!x)
            throw new FirmNotFoundError();
        return { ...x, latest_version: Number(x.latest_version) };
    }
    async versionStatement(exceptionId, version, wording) {
        const key = await this.key();
        const text = cleanText(wording, 'wording', 10, 10000);
        return {
            sql: `INSERT INTO audit_exception_versions (version_id, workspace_id, engagement_id, firm_id, exception_id, version, wording_enc, wording_sha256, created_by, created_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            params: [generateRandomId('aev'), this.workspaceId, this.engagementId, this.firmId, exceptionId, version,
                await key.sealField([this.engagementId, 'exception', exceptionId, `wording:v${version}`], text), await sha256Hex(text), this.userId, this.now().toISOString()],
        };
    }
    /** Draft an exception (v1). Drafts are firm-only. */
    async createException(input) {
        this.assertWritable();
        const controlRef = cleanText(input.control_ref, 'control_ref', 1, 100);
        // V106-1-9 / ADR-0078 §8: on an assessor engagement (cpa on SOC 2), the exception names a
        // criterion or point of focus in the period's scope; another category or program is 422.
        // Display only: the exception never makes an objective Not met (the determination does).
        if (this.assessorType() && this.ctx.engagement.audit_period_id) {
            try {
                await assertRefInPeriodScope(this.tenant, this.control, this.workspaceId, this.ctx.engagement.audit_period_id, controlRef);
            }
            catch (e) {
                if (e instanceof C3paoError && (e.code === 'invalid' || e.code === 'not_found'))
                    throw new FirmValidationError('out_of_scope', e.message);
                throw e;
            }
        }
        if (input.test_id)
            await this.getTest(input.test_id);
        const id = generateRandomId('aex');
        await this.tenant.batch([
            { sql: `INSERT INTO audit_exceptions (exception_id, workspace_id, engagement_id, firm_id, test_id, control_ref, created_by, created_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [id, this.workspaceId, this.engagementId, this.firmId, input.test_id ?? null, controlRef, this.userId, this.now().toISOString()] },
            await this.versionStatement(id, 1, input.wording),
        ]);
        await this.chain({ action: 'exception_drafted', tier: 'firm', subject_kind: 'exception', subject_id: id, detail: { version: 1 } });
        return (await this.listExceptions()).find((e) => e.exception_id === id);
    }
    /** A new wording version. An issued exception keeps its issued version until re-issued. */
    async reviseException(exceptionId, wording) {
        this.assertWritable();
        const x = await this.exceptionRow(exceptionId);
        if (x.status === 'withdrawn')
            throw new FirmStateError('exception_withdrawn', 'This exception was withdrawn.');
        const next = x.latest_version + 1;
        await this.tenant.batch([
            await this.versionStatement(exceptionId, next, wording),
            { sql: `UPDATE audit_exceptions SET latest_version = ? WHERE exception_id = ? AND workspace_id = ? AND latest_version = ?`, params: [next, exceptionId, this.workspaceId, x.latest_version] },
        ]);
        await this.chain({ action: 'exception_revised', tier: 'firm', subject_kind: 'exception', subject_id: exceptionId, detail: { version: next } });
        return (await this.listExceptions()).find((e) => e.exception_id === exceptionId);
    }
    /** Lead: issue the latest version to the client. */
    async issueException(exceptionId) {
        this.assertWritable({ lead: true });
        const x = await this.exceptionRow(exceptionId);
        if (x.status === 'withdrawn')
            throw new FirmStateError('exception_withdrawn', 'This exception was withdrawn.');
        if (x.issued_version === x.latest_version)
            throw new FirmStateError('already_issued', `v${x.latest_version} is already issued.`);
        const at = this.now().toISOString();
        await this.tenant.batch([
            { sql: `UPDATE audit_exception_versions SET issued_by = ?, issued_at = ? WHERE exception_id = ? AND workspace_id = ? AND version = ? AND issued_at IS NULL`, params: [this.userId, at, exceptionId, this.workspaceId, x.latest_version] },
            { sql: `UPDATE audit_exceptions SET status = 'issued', issued_version = ? WHERE exception_id = ? AND workspace_id = ?`, params: [x.latest_version, exceptionId, this.workspaceId] },
        ]);
        await this.chain({ action: 'exception_issued', tier: 'client', subject_kind: 'exception', subject_id: exceptionId, detail: { version: x.latest_version } });
        return (await this.listExceptions()).find((e) => e.exception_id === exceptionId);
    }
    async withdrawException(exceptionId) {
        this.assertWritable({ lead: true });
        const x = await this.exceptionRow(exceptionId);
        if (x.status === 'withdrawn')
            throw new FirmStateError('exception_withdrawn', 'Already withdrawn.');
        await this.tenant.execute(`UPDATE audit_exceptions SET status = 'withdrawn', withdrawn_by = ?, withdrawn_at = ? WHERE exception_id = ? AND workspace_id = ?`, [this.userId, this.now().toISOString(), exceptionId, this.workspaceId]);
        await this.chain({ action: 'exception_withdrawn', tier: 'client', subject_kind: 'exception', subject_id: exceptionId });
        return (await this.listExceptions()).find((e) => e.exception_id === exceptionId);
    }
    /** Section 4: locked while any response answers a stale exception or system-description version. */
    async section4() {
        return section4State(this.tenant, this.ctx.engagement);
    }
    // ---- report documents (V98-6-7) -------------------------------------------------------
    /** Every report document, the opinion draft included (firm-only). */
    async listReportDocuments() {
        const key = await this.key();
        const r = await this.tenant.query(`SELECT * FROM report_documents WHERE engagement_id = ? ORDER BY kind, version`, [this.engagementId]);
        const out = [];
        for (const d of r.results) {
            const body = d.kind === 'opinion_draft' ? (await key.openField([this.engagementId, 'report_document', d.doc_id, 'body'], d.body_enc)) ?? '' : d.body ?? '';
            out.push(toReportDoc(d, body, this.ctx.engagement.report_date));
        }
        return out;
    }
    /** Lead: a new version of the opinion draft. Firm-only, encrypted; the client never reads it. */
    async saveOpinionDraft(body) {
        this.assertWritable({ lead: true });
        const text = cleanText(body, 'body', 10, 50000);
        const key = await this.key();
        const id = generateRandomId('rdc');
        const version = await nextDocVersion(this.tenant, this.engagementId, 'opinion_draft');
        await this.tenant.execute(`INSERT INTO report_documents (doc_id, workspace_id, engagement_id, firm_id, kind, version, status, body_enc, created_by, created_at) VALUES (?, ?, ?, ?, 'opinion_draft', ?, 'draft', ?, ?, ?)`, [id, this.workspaceId, this.engagementId, this.firmId, version, await key.sealField([this.engagementId, 'report_document', id, 'body'], text), this.userId, this.now().toISOString()]);
        await this.chain({ action: 'opinion_draft_saved', tier: 'firm', subject_kind: 'report_document', subject_id: id, detail: { version } });
        return (await this.listReportDocuments()).find((d) => d.doc_id === id);
    }
    /** The period's system-description versions with the firm's latest review of each. */
    async systemDescriptionVersions() {
        this.assertClientDataReadable();
        if (!this.ctx.engagement.audit_period_id)
            return [];
        const r = await this.tenant.query(`SELECT description_id, version, status, created_at, approved_at FROM system_descriptions WHERE workspace_id = ? AND period_id = ? ORDER BY version ASC`, [this.workspaceId, this.ctx.engagement.audit_period_id]);
        const out = [];
        for (const d of r.results) {
            const rev = (await this.tenant.query(`SELECT status, return_comment, decided_at FROM report_documents WHERE engagement_id = ? AND kind = 'system_description_review' AND system_description_id = ? ORDER BY version DESC LIMIT 1`, [this.engagementId, d.description_id])).results[0];
            out.push({ ...d, version: Number(d.version), review: rev ? { status: rev.status, comment: rev.return_comment, reviewed_at: rev.decided_at } : null });
        }
        return out;
    }
    /** Section-by-section line diff between two system-description versions of the period. */
    async diffSystemDescriptions(fromId, toId) {
        this.assertClientDataReadable();
        const load = async (id) => {
            const r = (await this.tenant.query(`SELECT version, sections_json, period_id FROM system_descriptions WHERE description_id = ? AND workspace_id = ? LIMIT 1`, [id, this.workspaceId])).results[0];
            if (!r || r.period_id !== this.ctx.engagement.audit_period_id)
                throw new FirmNotFoundError();
            return { version: Number(r.version), sections: JSON.parse(r.sections_json) };
        };
        const a = await load(fromId);
        const b = await load(toId);
        const keys = [...new Set([...a.sections.map((s) => s.key), ...b.sections.map((s) => s.key)])];
        const sections = keys.map((k) => {
            const x = a.sections.find((s) => s.key === k);
            const y = b.sections.find((s) => s.key === k);
            const lines = diffLines(x?.body_markdown ?? '', y?.body_markdown ?? '');
            const change = !x ? 'added' : !y ? 'removed' : lines.every((l) => l.op === 'same') ? 'unchanged' : 'changed';
            return { key: k, title: (y ?? x).title, change, lines };
        });
        return { from_version: a.version, to_version: b.version, sections };
    }
    /** Accept a system-description version, or return it to the client with a comment. */
    async reviewSystemDescription(descriptionId, input) {
        this.assertWritable();
        const d = (await this.systemDescriptionVersions()).find((x) => x.description_id === descriptionId);
        if (!d)
            throw new FirmNotFoundError();
        if (input.action !== 'return' && input.action !== 'accept')
            throw new FirmValidationError('invalid_action', 'action must be return or accept.');
        const comment = input.action === 'return' ? cleanText(input.comment, 'comment', 5, 5000) : null;
        const at = this.now().toISOString();
        await this.tenant.execute(`INSERT INTO report_documents (doc_id, workspace_id, engagement_id, firm_id, kind, version, status, body, system_description_id, system_description_version, return_comment, decided_by, decided_at, created_by, created_at)
       VALUES (?, ?, ?, ?, 'system_description_review', ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [generateRandomId('rdc'), this.workspaceId, this.engagementId, this.firmId, await nextDocVersion(this.tenant, this.engagementId, 'system_description_review'),
            input.action === 'return' ? 'returned' : 'accepted', comment ?? `Accepted system description v${d.version}.`, descriptionId, d.version, comment, this.userId, at, this.userId, at]);
        await this.chain({ action: `system_description_${input.action === 'return' ? 'returned' : 'accepted'}`, tier: 'client', subject_kind: 'system_description', subject_id: descriptionId, detail: { version: d.version } });
        return this.systemDescriptionVersions();
    }
    /** Return a submitted client document with a comment, or accept it. */
    async decideReportDocument(docId, input) {
        this.assertWritable();
        const d = (await this.tenant.query(`SELECT * FROM report_documents WHERE doc_id = ? AND engagement_id = ? LIMIT 1`, [docId, this.engagementId])).results[0];
        if (!d || !CLIENT_REPORT_DOCUMENT_KINDS.includes(d.kind))
            throw new FirmNotFoundError();
        if (d.status !== 'submitted')
            throw new FirmStateError('not_submitted', 'Only a submitted document can be returned or accepted.');
        if (input.action === 'accept' && d.kind === 'representation_letter' && d.document_date !== this.ctx.engagement.report_date) {
            throw new FirmStateError('letter_not_dated_to_report', 'The representation letter must be dated as of the report date.');
        }
        const at = this.now().toISOString();
        if (input.action === 'return') {
            await this.tenant.execute(`UPDATE report_documents SET status = 'returned', return_comment = ?, decided_by = ?, decided_at = ? WHERE doc_id = ? AND workspace_id = ? AND status = 'submitted'`, [cleanText(input.comment, 'comment', 5, 5000), this.userId, at, docId, this.workspaceId]);
        }
        else if (input.action === 'accept') {
            await this.tenant.execute(`UPDATE report_documents SET status = 'accepted', decided_by = ?, decided_at = ? WHERE doc_id = ? AND workspace_id = ? AND status = 'submitted'`, [this.userId, at, docId, this.workspaceId]);
        }
        else {
            throw new FirmValidationError('invalid_action', 'action must be return or accept.');
        }
        await this.chain({ action: `report_document_${input.action === 'return' ? 'returned' : 'accepted'}`, tier: 'client', subject_kind: 'report_document', subject_id: docId, detail: { kind: d.kind, version: Number(d.version) } });
        return (await this.listReportDocuments()).find((x) => x.doc_id === docId);
    }
    /** Subservice coverage for the period, from the coverage documents on file. */
    async subserviceCoverage() {
        this.assertClientDataReadable();
        const p = await periodWindow(this.tenant, this.workspaceId, this.ctx.engagement.audit_period_id);
        return subserviceCoverage(this.tenant, this.workspaceId, p);
    }
    // ---- export to workpapers (V98-6-8) -------------------------------------------------
    async listExports() {
        const r = await this.tenant.query(`SELECT ${EXPORT_COLS} FROM firm_exports WHERE engagement_id = ? ORDER BY created_at DESC`, [this.engagementId]);
        return r.results.map(toExportView);
    }
    /**
     * Build the indexed workpaper zip as of now: engagement + roster + chain,
     * the firm tier (samples, reconciliations, tests, review notes, sign-offs,
     * exceptions, report documents incl. the opinion draft, Section 4) and —
     * unless the client revoked access (R5) — the client tier (PBC with threads
     * and evidence versions, population provenance, the SoD event log and
     * delegation log). A signed manifest indexes every file. The zip is sealed
     * with the per-firm key and stored under the firm-only prefix (R6, R8).
     */
    async createExport(deps) {
        const asOf = this.now().toISOString();
        const exportId = generateRandomId('fex');
        const enc = new TextEncoder();
        const files = [];
        const add = (path, value) => files.push({ path, data: enc.encode(typeof value === 'string' ? value : JSON.stringify(value, null, 2)) });
        const omitted = [];
        const clientReadable = !this.ctx.engagement.revoked_at;
        add('01-engagement/engagement.json', this.overview());
        add('01-engagement/roster.json', (await this.listMembers()).map((m) => ({ member_id: m.member_id, user_id: m.user_id, name: m.display_name, firm_role: m.firm_role, added_at: m.added_at, removed_at: m.removed_at, removed_reason: m.removed_reason })));
        add('01-engagement/firm-chain.json', { events: await this.chainEvents(), verification: await this.verifyChain() });
        if (clientReadable) {
            for (const r of await this.listPbc())
                add(`02-pbc/${r.ref}.json`, r);
            for (const p of await this.listPopulations())
                add(`03-populations/${p.population_id}.json`, p);
            for (const [name, sql] of [
                ['delegation-log', `SELECT * FROM delegations WHERE workspace_id = ? ORDER BY created_at`],
                ['sod-events', `SELECT * FROM sod_events WHERE workspace_id = ? ORDER BY created_at`],
            ]) {
                try {
                    add(`07-segregation-of-duties/${name}.json`, (await this.tenant.query(sql, [this.workspaceId])).results);
                }
                catch {
                    omitted.push(`07-segregation-of-duties/${name}.json (not available on this data plane)`);
                }
            }
        }
        else {
            omitted.push('02-pbc, 03-populations, 07-segregation-of-duties (client revoked access — client data is no longer readable; ADR-0047 R5)');
        }
        add('04-workpapers/templates.json', await this.listTemplates());
        for (const t of await this.listTests())
            add(`04-workpapers/tests/${t.control_ref.replace(/[^A-Za-z0-9._-]/g, '_')}-${t.test_id}.json`, await this.getTest(t.test_id));
        add('04-workpapers/review-queue.json', await this.reviewerQueue());
        add('05-exceptions/exceptions.json', await this.listExceptions());
        add('05-exceptions/section-4.json', await this.section4());
        add('06-report/status.json', await this.reportStatus());
        add('06-report/documents.json', await this.listReportDocuments());
        add('06-report/export-history.json', await this.listExports());
        const manifest = {
            export_id: exportId, engagement_id: this.engagementId, workspace_id: this.workspaceId, firm: this.ctx.firm, as_of: asOf, generated_by: this.userId,
            includes_client_tier: clientReadable, omitted,
            files: await Promise.all(files.map(async (f) => ({ path: f.path, sha256: await sha256Hex(f.data), bytes: f.data.length }))),
            signer: { label: EXPORT_SIGNER_LABEL, algorithm: 'HMAC-SHA256', key_id: '', domain: EXPORT_SIGN_DOMAIN },
        };
        const signed = await deps.sign(`${EXPORT_SIGN_DOMAIN}
${canonicalJson({ ...manifest, signer: { ...manifest.signer, key_id: null } })}`);
        manifest.signer.key_id = signed.key_id;
        manifest.signature = signed.signature;
        const manifestBytes = enc.encode(JSON.stringify(manifest, null, 2));
        const zip = buildStoreZip([{ path: '00-manifest.json', data: manifestBytes }, ...files], new Date(asOf));
        const key = await this.key();
        const r2Key = firmObjectKey(this.firmId, this.engagementId, exportId);
        await deps.put(r2Key, await key.sealBytes([this.engagementId, 'export', exportId, 'zip'], zip));
        await this.tenant.execute(`INSERT INTO firm_exports (export_id, workspace_id, engagement_id, firm_id, as_of, r2_key, includes_client_tier, file_count, size_bytes, zip_sha256, manifest_sha256,
         signature, signing_key_id, signer_label, created_by, created_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [exportId, this.workspaceId, this.engagementId, this.firmId, asOf, r2Key, clientReadable ? 1 : 0, files.length + 1, zip.length, await sha256Hex(zip),
            await sha256Hex(manifestBytes), signed.signature, signed.key_id, EXPORT_SIGNER_LABEL, this.userId, this.now().toISOString()]);
        await this.chain({ action: 'export_completed', tier: 'firm', subject_kind: 'export', subject_id: exportId, detail: { files: files.length + 1, includes_client_tier: clientReadable } });
        return (await this.listExports()).find((e) => e.export_id === exportId);
    }
    /** Re-download any prior export: read the sealed object, verify, return the zip. */
    async openExport(exportId, get) {
        const r = (await this.tenant.query(`SELECT r2_key, zip_sha256, as_of FROM firm_exports WHERE export_id = ? AND engagement_id = ? LIMIT 1`, [exportId, this.engagementId])).results[0];
        if (!r || r.r2_key !== firmObjectKey(this.firmId, this.engagementId, exportId))
            throw new FirmNotFoundError();
        const sealed = await get(r.r2_key);
        if (!sealed)
            throw new FirmStateError('export_missing', 'The stored export is missing.');
        const zip = await (await this.key()).openBytes([this.engagementId, 'export', exportId, 'zip'], sealed);
        if ((await sha256Hex(zip)) !== r.zip_sha256)
            throw new FirmStateError('export_tampered', 'The stored export does not match its recorded hash.');
        await this.chain({ action: 'export_downloaded', tier: 'firm', subject_kind: 'export', subject_id: exportId });
        return { filename: `workpapers-${this.engagementId}-${r.as_of.slice(0, 19).replace(/[:T]/g, '-')}.zip`, bytes: zip };
    }
    /** R5: the firm acknowledges custody (lets the client delete the workspace without a completed export). */
    async acknowledgeCustody() {
        if (!isLeadRole(this.role))
            throw new FirmForbiddenError('firm_lead_required', 'Only the engagement partner or manager can do this.');
        await this.chain({ action: 'custody_acknowledged', tier: 'firm', subject_kind: 'engagement', subject_id: this.engagementId });
    }
    // ---- report status (client-visible: status only, never an opinion) ----------------
    async reportStatus() {
        return loadReportStatus(this.tenant, this.engagementId);
    }
    /**
     * Lead: move the client-visible report status. `issued` releases the report:
     * the engagement enters the read-only assembly window (R5) and can't go back.
     * ADR-0078 §6: `issued` records the opinion type and the report date (both required, and
     * refused on any other status). A reissued or corrected report is a newer `issued` event
     * (`reissue: true`) that supersedes the earlier one by recency.
     */
    async setReportStatus(input) {
        if (input.status === 'issued' && input.reissue) {
            // The reissue comes after the release, inside the read-only assembly window: lead only.
            if (!isLeadRole(this.role))
                throw new FirmForbiddenError('firm_lead_required', 'Only the engagement partner or manager can do this.');
        }
        else {
            this.assertWritable({ lead: true });
        }
        if (!REPORT_STATUSES.includes(input.status))
            throw new FirmValidationError('invalid_status', 'status must be fieldwork, draft_sent or issued.');
        let opinion = null;
        let reportDate = null;
        if (input.status === 'issued') {
            if (typeof input.opinion_type !== 'string' || !SOC_OPINION_TYPES.includes(input.opinion_type)) {
                throw new FirmValidationError('invalid_opinion_type', 'An issued report needs opinion_type: unmodified, qualified, adverse or disclaimer.');
            }
            if (typeof input.report_date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(input.report_date) || Number.isNaN(Date.parse(input.report_date))) {
                throw new FirmValidationError('invalid_report_date', 'An issued report needs report_date (YYYY-MM-DD).');
            }
            if (input.report_date > this.now().toISOString().slice(0, 10))
                throw new FirmValidationError('invalid_report_date', 'report_date cannot be in the future.');
            opinion = input.opinion_type;
            reportDate = input.report_date;
        }
        else if (input.opinion_type !== undefined || input.report_date !== undefined) {
            throw new FirmValidationError('invalid_opinion_type', 'Only an issued report records an opinion type and report date.');
        }
        const current = await this.reportStatus();
        if (input.reissue && current.status !== 'issued')
            throw new FirmStateError('not_issued', 'Only an issued report can be reissued.');
        if (current.status === 'issued' && !(input.status === 'issued' && input.reissue))
            throw new FirmStateError('report_issued', 'The report is issued; its status is final (a corrected report is a reissue).');
        if (REPORT_STATUSES.indexOf(input.status) < REPORT_STATUSES.indexOf(current.status)) {
            throw new FirmStateError('status_backwards', 'Report status only moves forward.');
        }
        if (input.status === 'issued') {
            const s4 = await this.section4();
            if (s4.locked && !input.reissue)
                throw new FirmStateError('section4_locked', `Section 4 is locked: ${s4.reasons.join('; ')}`);
        }
        const at = this.now().toISOString();
        await this.tenant.execute(`INSERT INTO report_status_events (status_event_id, workspace_id, engagement_id, firm_id, status, set_by, set_at, opinion_type, report_date) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`, [generateRandomId('rse'), this.workspaceId, this.engagementId, this.firmId, input.status, this.userId, at, opinion, reportDate]);
        if (input.status === 'issued') {
            await this.control.execute(`UPDATE assessor_engagements SET report_released_at = ? WHERE engagement_id = ? AND workspace_id = ? AND report_released_at IS NULL`, [at, this.engagementId, this.workspaceId]);
        }
        await this.chain({ action: 'report_status_set', tier: 'firm', subject_kind: 'engagement', subject_id: this.engagementId, detail: { status: input.status, ...(opinion ? { opinion_type: opinion, report_date: reportDate, reissue: !!input.reissue } : {}) } });
        return this.reportStatus();
    }
    // ---- assessor engagements (V102-7-5 H1 / M5; ADR-0059) ------------------------------
    /** The engagement's assessor family, or null for a plain firm-tier engagement. */
    assessorType() {
        const e = this.ctx.engagement;
        if (isAssessorFirmType(e.assessor_firm_type))
            return e.assessor_firm_type;
        return e.firm_type === 'c3pao' ? 'c3pao' : null;
    }
    /**
     * The verified assessor context the workpaper service needs (H1). Not an
     * assessor engagement → not found. Reads need client data readable (R5:
     * not revoked). Writes ALSO need: fieldwork, not revoked, now inside
     * [engagement_start, engagement_end], and the firm's authorization for the
     * type still active (re-read on every call, never cached).
     */
    async assessorEngagement(opts) {
        const type = this.assessorType();
        const e = this.ctx.engagement;
        if (!type || !e.audit_period_id)
            throw new FirmNotFoundError();
        // ADR-0081 §7: the period's program must (still) accept this family (manifest `assessorTypes`). A family the
        // program doesn't take is cross-firm-type: not found, on reads and writes alike.
        if (!programAllowsAssessorType(this.ctx.period?.program ?? null, type))
            throw new FirmNotFoundError();
        this.assertClientDataReadable();
        if (opts.write) {
            this.assertWritable();
            const t = this.now().getTime();
            if (t < startOfDay(e.engagement_start) || t > endOfDay(e.engagement_end)) {
                throw new FirmForbiddenError('outside_engagement_window', 'Assessor work is recorded only inside the engagement window.');
            }
            if (!(await activeFirmTypeAuthorization(this.control, e.firm_id, type))) {
                throw new FirmForbiddenError('firm_type_not_authorized', 'The firm\'s authorization for this assessment type is not active.');
            }
        }
        // V106-6-5 T6-L6: the firm's engagements on this client period (separation runs over all of them).
        const siblings = await this.control.query(`SELECT engagement_id FROM assessor_engagements WHERE workspace_id = ? AND firm_id = ? AND audit_period_id = ? ORDER BY engagement_id`, [e.workspace_id, e.firm_id, e.audit_period_id]);
        const separationScopeEngagementIds = [...new Set([e.engagement_id, ...siblings.results.map((r) => r.engagement_id)])];
        return { workspaceId: e.workspace_id, engagementId: e.engagement_id, firmId: e.firm_id, periodId: e.audit_period_id, type, separationScopeEngagementIds };
    }
    /** The informational independence flag of a flag-mode family (GovRAMP owner decision 1; cpa, ADR-0081 §4). */
    independenceFlag() {
        return engagementIndependenceFlag(this.control, this.ctx.engagement, this.now(), this.ctx.period?.start ?? null);
    }
    /** Only the engagement partner / manager issues or ends assessor roles (H1: never the client). */
    assertAssessorRoleIssuer() {
        if (!isLeadRole(this.role))
            throw new FirmForbiddenError('firm_lead_required', 'Only the engagement partner or manager assigns assessor roles.');
    }
    /**
     * H1: an assessor role goes only to someone on THIS engagement's roster who
     * is still accepted staff of the firm and holds no client hat (R2).
     */
    async assertAssignableRosterMember(userId) {
        const m = await this.control.query(`SELECT member_id FROM engagement_members WHERE engagement_id = ? AND workspace_id = ? AND user_id = ? AND firm_id = ? AND removed_at IS NULL LIMIT 1`, [this.engagementId, this.workspaceId, userId, this.firmId]);
        const staff = await this.control.query(`SELECT staff_id FROM audit_firm_staff WHERE firm_id = ? AND user_id = ? AND accepted_at IS NOT NULL AND removed_at IS NULL LIMIT 1`, [this.firmId, userId]);
        if (!m.results[0] || !staff.results[0])
            throw new FirmValidationError('not_on_roster', 'Assessor roles go only to members of this engagement\'s roster.');
        if (await holdsClientHat(this.control, this.workspaceId, userId)) {
            await this.chain({ action: 'independence_rejected', tier: 'independence', subject_kind: 'user', subject_id: userId, detail: { point: 'assessor_role' } });
            throw new IndependenceRejectedError('assignment');
        }
    }
    /** H1 / runbook I: every assessor write and refusal is on the firm chain and the client's SoD log (ids only). */
    async logAssessorEvent(action, detail, subject) {
        const at = this.now().toISOString();
        await this.chain({ action, tier: 'firm', subject_kind: subject?.kind ?? 'engagement', subject_id: subject?.id ?? this.engagementId, detail, at });
        await recordSodEvent(this.tenant, {
            workspace_id: this.workspaceId, transition: 'config', event: action, actor_user_id: this.userId,
            record_kind: subject?.kind ?? 'assessor_engagement', record_id: subject?.id ?? this.engagementId, created_at: at,
            detail: { engagement_id: this.engagementId, ...detail },
        });
    }
    // ---- assessor report artifacts (ADR-0067 §7; GovRAMP RAR / SAP) ----------------------
    // The lead-role check is the caller's (assessor-tier-service reads the tenant roles).
    async listReportArtifacts() {
        await this.assessorEngagement({ write: false });
        const r = await this.tenant.query(`SELECT ${REPORT_COLS} FROM assessor_report_artifacts WHERE workspace_id = ? AND engagement_id = ? ORDER BY kind, version`, [this.workspaceId, this.engagementId]);
        return r.results.map(toReportArtifact);
    }
    /** Upload a new version of the RAR or SAP (bytes to R2 first; the row only after the object exists). */
    async insertReportArtifact(input) {
        const e = await this.assessorEngagement({ write: true });
        if (input.kind !== 'rar' && input.kind !== 'sap')
            throw new FirmValidationError('invalid_kind', 'kind must be rar or sap.');
        // V106-6-5 T6-L2: RAR / SAP are FedRAMP / GovRAMP artifacts; other families upload none until they define their own.
        if (!ASSESSOR_FIRM_TYPE_SPECS[e.type].reportKinds.includes(input.kind)) {
            throw new FirmValidationError('report_kind_not_allowed', `A ${ASSESSOR_FIRM_TYPE_SPECS[e.type].label} engagement doesn't upload ${input.kind} reports.`);
        }
        const filename = cleanText(input.filename, 'filename', 1, 200);
        if (/[\\/\u0000-\u001f]/.test(filename))
            throw new FirmValidationError('invalid_filename', 'filename must be a plain file name.');
        const contentType = cleanText(input.content_type, 'content_type', 3, 100);
        if (input.bytes.length === 0 || input.bytes.length > ASSESSOR_REPORT_MAX_BYTES)
            throw new FirmValidationError('invalid_size', 'A report is 1 byte to 10 MB.');
        const last = await this.tenant.query(`SELECT MAX(version) AS v FROM assessor_report_artifacts WHERE engagement_id = ? AND kind = ?`, [this.engagementId, input.kind]);
        const version = Number(last.results[0]?.v ?? 0) + 1;
        const id = generateRandomId('arp');
        const key = `assessor-reports/${e.workspaceId}/${this.engagementId}/${id}`;
        const sha = await sha256Hex(input.bytes);
        const at = this.now().toISOString();
        await input.put(key, input.bytes);
        await this.tenant.execute(`INSERT INTO assessor_report_artifacts (artifact_id, workspace_id, engagement_id, firm_id, kind, version, filename, content_type, byte_size, content_sha256, storage_key, uploaded_by, uploaded_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [id, e.workspaceId, this.engagementId, this.firmId, input.kind, version, filename, contentType, input.bytes.length, sha, key, this.userId, at]);
        return (await this.listReportArtifacts()).find((a) => a.artifact_id === id);
    }
    /** The lead's sign-off: HMAC over a canonical manifest with the workspace key. Final. */
    async signOffReportArtifact(artifactId, sign) {
        await this.assessorEngagement({ write: true });
        const a = (await this.listReportArtifacts()).find((x) => x.artifact_id === artifactId);
        if (!a)
            throw new FirmNotFoundError();
        if (a.signed_off_at)
            throw new FirmStateError('already_signed', 'This report version is already signed off.');
        const at = this.now().toISOString();
        const manifest = canonicalJson({
            domain: ASSESSOR_REPORT_SIGN_DOMAIN, artifact_id: a.artifact_id, workspace_id: this.workspaceId, engagement_id: this.engagementId,
            firm_id: this.firmId, assessor_firm_type: this.assessorType(), kind: a.kind, version: a.version, filename: a.filename,
            byte_size: a.byte_size, content_sha256: a.content_sha256, uploaded_by: a.uploaded_by, uploaded_at: a.uploaded_at,
            signed_off_by: this.userId, signed_off_at: at,
        });
        const sig = await sign(manifest);
        const res = await this.tenant.execute(`UPDATE assessor_report_artifacts SET signed_off_by = ?, signed_off_at = ?, manifest_json = ?, signature_key_id = ?, signature = ?
        WHERE artifact_id = ? AND workspace_id = ? AND engagement_id = ? AND signed_off_at IS NULL`, [this.userId, at, manifest, sig.key_id, sig.signature, artifactId, this.workspaceId, this.engagementId]);
        if ((res.meta?.changes ?? 0) === 0)
            throw new FirmStateError('already_signed', 'This report version is already signed off.');
        return (await this.listReportArtifacts()).find((x) => x.artifact_id === artifactId);
    }
    // ---- certification-body audits (V106-6-4 / ADR-0081 §3; ISO/IEC 17021-1 §9.5) ----------
    // Only a certification_body engagement (any other family: 404, cross-firm-type). The lead auditor signs off
    // one audit of the program's iso-certification cycle; the technical reviewer then records the decision,
    // which rests on that recorded sign-off (538 / 539 triggers hold the same rules). The client has no write.
    async certificationEngagement(write) {
        const e = await this.assessorEngagement({ write });
        if (e.type !== 'certification_body')
            throw new FirmNotFoundError();
        const prof = (await this.tenant.query(`SELECT profile_id FROM audit_periods WHERE period_id = ? AND workspace_id = ? LIMIT 1`, [e.periodId, e.workspaceId])).results[0];
        if (!prof)
            throw new FirmNotFoundError();
        return { workspaceId: e.workspaceId, engagementId: e.engagementId, firmId: e.firmId, periodId: e.periodId, profileId: prof.profile_id };
    }
    async myOpenRole(engagementId, atIso) {
        const r = await this.tenant.query(`SELECT role FROM assessor_engagement_roles WHERE workspace_id = ? AND engagement_id = ? AND user_id = ?
          AND role_start <= ? AND (role_end IS NULL OR role_end > ?) LIMIT 1`, [this.workspaceId, engagementId, this.userId, atIso, atIso]);
        return r.results[0]?.role ?? null;
    }
    /** The audits of the current cycle the lead may sign off, with this engagement's sign-offs and decisions. */
    async certificationOverview() {
        const e = await this.certificationEngagement(false);
        const view = await getCertificationCycle(this.tenant, { workspaceId: e.workspaceId, profileId: e.profileId, contentDb: this.control, now: this.now() });
        const cycle = view.schedule ? {
            cycle_no: view.schedule.cycle_no,
            rule_set_version: view.rule_set?.version ?? null,
            audits: view.schedule.events.filter((x) => CERTIFICATION_AUDIT_KIND_BY_EVENT_KIND[x.kind]).map((x) => ({
                key: x.key, label: x.label, kind: x.kind, audit_kind: CERTIFICATION_AUDIT_KIND_BY_EVENT_KIND[x.kind], status: x.status, due_on: x.due_on, done_on: x.done_on,
            })),
        } : null;
        const signoffs = await this.tenant.query(`SELECT * FROM certification_audit_signoffs WHERE workspace_id = ? AND engagement_id = ? ORDER BY signed_off_at, signoff_id`, [e.workspaceId, e.engagementId]);
        const decisions = await this.tenant.query(`SELECT * FROM certification_decisions WHERE workspace_id = ? AND engagement_id = ? ORDER BY decided_at, decision_id`, [e.workspaceId, e.engagementId]);
        return { cycle, signoffs: signoffs.results, decisions: decisions.results };
    }
    /**
     * The lead auditor signs off one audit of the current cycle (stage 2, a surveillance or the
     * recertification audit). Cycle number and audit kind come from the approved iso-certification rules
     * and the recorded milestones, never from the request. Once per engagement × cycle × audit.
     */
    async signOffCertificationAudit(input) {
        const e = await this.certificationEngagement(true);
        const at = this.now().toISOString();
        if ((await this.myOpenRole(e.engagementId, at)) !== 'lead_assessor') {
            throw new FirmForbiddenError('role_required', 'Only the engagement\'s lead auditor signs off an audit.');
        }
        const view = await getCertificationCycle(this.tenant, { workspaceId: e.workspaceId, profileId: e.profileId, contentDb: this.control, now: this.now() });
        if (!view.schedule)
            throw new FirmStateError('cycle_rules_not_approved', 'The program\'s certification cycle rules are not approved yet.');
        const key = String(input.cycle_event_key ?? '').trim();
        const ev = view.schedule.events.find((x) => x.key === key);
        const auditKind = ev ? CERTIFICATION_AUDIT_KIND_BY_EVENT_KIND[ev.kind] : undefined;
        if (!ev || !auditKind) {
            throw new FirmValidationError('invalid_cycle_event', 'cycle_event_key must name a stage 2, surveillance or recertification audit of the current cycle.');
        }
        const id = generateRandomId('cas');
        try {
            await this.tenant.execute(`INSERT INTO certification_audit_signoffs (signoff_id, workspace_id, period_id, engagement_id, firm_id, audit_kind, signed_off_by, signed_off_at, cycle_no, cycle_event_key)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [id, e.workspaceId, e.periodId, e.engagementId, e.firmId, auditKind, this.userId, at, view.schedule.cycle_no, key]);
        }
        catch (err) {
            if (/UNIQUE|unique/.test(String(err?.message ?? err)))
                throw new FirmStateError('already_signed_off', 'This audit is already signed off on this engagement.');
            throw err;
        }
        return (await this.tenant.query(`SELECT * FROM certification_audit_signoffs WHERE signoff_id = ? AND workspace_id = ?`, [id, e.workspaceId])).results[0];
    }
    /**
     * The technical reviewer records the certification decision (ISO/IEC 17021-1 §9.5.1.1). The lead
     * sign-off it rests on is read from the recorded row (signer, time, audit, cycle), never the request;
     * a superseded decision must be this client period's and this firm's. HMAC-signed; immutable.
     */
    async recordCertificationDecision(input, sign) {
        const e = await this.certificationEngagement(true);
        const at = this.now().toISOString();
        if ((await this.myOpenRole(e.engagementId, at)) !== 'technical_reviewer') {
            throw new FirmForbiddenError('role_required', 'Only the engagement\'s technical reviewer records a certification decision.');
        }
        const so = (await this.tenant.query(`SELECT * FROM certification_audit_signoffs WHERE signoff_id = ? AND workspace_id = ? AND engagement_id = ? LIMIT 1`, [String(input.signoff_id ?? ''), e.workspaceId, e.engagementId])).results[0];
        if (!so)
            throw new FirmNotFoundError();
        const decision = String(input.decision ?? '');
        if (!CERTIFICATION_DECISIONS.includes(decision))
            throw new FirmValidationError('invalid_decision', `decision must be one of ${CERTIFICATION_DECISIONS.join(', ')}.`);
        const scopeText = cleanText(input.scope_text, 'scope_text', 1, 4000);
        const effective = isoDate(input.effective_date, 'effective_date');
        const expires = input.expires_on ? isoDate(input.expires_on, 'expires_on') : null;
        const rationale = input.rationale ? cleanText(input.rationale, 'rationale', 1, 4000) : null;
        if ((decision === 'grant' || decision === 'renew') && !expires)
            throw new FirmValidationError('expires_on_required', 'A grant or renewal names the certificate\'s expiry (expires_on).');
        if (['refuse', 'suspend', 'withdraw', 'reduce_scope'].includes(decision) && !rationale)
            throw new FirmValidationError('rationale_required', 'This decision needs a rationale.');
        let supersedes = null;
        if (input.supersedes_id) {
            const prev = (await this.tenant.query(`SELECT decision_id FROM certification_decisions WHERE decision_id = ? AND workspace_id = ? AND period_id = ? AND firm_id = ? LIMIT 1`, [String(input.supersedes_id), e.workspaceId, e.periodId, e.firmId])).results[0];
            if (!prev)
                throw new FirmNotFoundError();
            supersedes = prev.decision_id;
        }
        const id = generateRandomId('cdc');
        const manifest = canonicalJson({
            domain: CERTIFICATION_DECISION_SIGN_DOMAIN, decision_id: id, workspace_id: e.workspaceId, period_id: e.periodId,
            engagement_id: e.engagementId, firm_id: e.firmId, assessor_firm_type: 'certification_body',
            audit_kind: so.audit_kind, cycle_no: so.cycle_no, cycle_event_key: so.cycle_event_key, decision, scope_text: scopeText,
            effective_date: effective, expires_on: expires, rationale, lead_signoff_id: so.signoff_id, lead_signoff_by: so.signed_off_by,
            lead_signoff_at: so.signed_off_at, decided_by: this.userId, decided_at: at, supersedes_id: supersedes,
        });
        const sig = await sign(manifest);
        await this.tenant.execute(`INSERT INTO certification_decisions (decision_id, workspace_id, period_id, engagement_id, firm_id, audit_kind, decision, scope_text, effective_date, expires_on, rationale,
          lead_signoff_by, lead_signoff_at, decided_by, decided_at, manifest_json, signature_key_id, signature, supersedes_id, lead_signoff_id, cycle_no, cycle_event_key)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [id, e.workspaceId, e.periodId, e.engagementId, e.firmId, so.audit_kind, decision, scopeText, effective, expires, rationale,
            so.signed_off_by, so.signed_off_at, this.userId, at, manifest, sig.key_id, sig.signature, supersedes, so.signoff_id, so.cycle_no, so.cycle_event_key]);
        return (await this.tenant.query(`SELECT * FROM certification_decisions WHERE decision_id = ? AND workspace_id = ?`, [id, e.workspaceId])).results[0];
    }
    async chainEvents() {
        const r = await this.control.query(`SELECT * FROM firm_chain_events WHERE engagement_id = ? ORDER BY seq ASC`, [this.engagementId]);
        return r.results;
    }
    verifyChain() {
        return verifyFirmChain(this.control, this.engagementId);
    }
}
export class FirmClientTier {
    control;
    tenant;
    workspaceId;
    userId;
    role;
    nowFn;
    kek;
    constructor(control, tenant, workspaceId, userId, role, nowFn, kek) {
        this.control = control;
        this.tenant = tenant;
        this.workspaceId = workspaceId;
        this.userId = userId;
        this.role = role;
        this.nowFn = nowFn;
        this.kek = kek;
    }
    /**
     * Client guard: an active workspace membership (re-read here, never taken
     * from the caller) and — R2 — never a roster identity.
     */
    static async open(controlDb, tenantDb, userId, workspaceId, opts = {}) {
        const m = await controlDb.query(`SELECT role FROM memberships WHERE workspace_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [workspaceId, userId]);
        const role = m.results[0]?.role;
        if (!role)
            throw new FirmNotFoundError();
        if (await isRosterIdentity(controlDb, userId, opts.now ?? new Date()))
            throw new FirmNotFoundError();
        return new FirmClientTier(controlDb, tenantDb, workspaceId, userId, role, () => opts.now ?? new Date(), opts.kek);
    }
    now() { return this.nowFn(); }
    requireAdmin() {
        if (this.role !== 'owner' && this.role !== 'admin')
            throw new FirmForbiddenError('owner_admin_required', 'Requires role: owner, admin.');
    }
    /** The engagement as the client may see it: never the roster, never firm content. */
    async engagement(engagementId) {
        const e = await loadEngagement(this.control, this.workspaceId, engagementId);
        if (!e)
            throw new FirmNotFoundError();
        return e;
    }
    /** R1: the client invites a FIRM (never a person) for one of its audit periods. */
    async inviteFirm(input) {
        this.requireAdmin();
        // V98-7-3 M2: only an operator-verified firm can be invited (an unverified one reads as unknown).
        const firm = await this.control.query(`SELECT firm_id FROM audit_firms WHERE firm_id = ? AND operator_verified_at IS NOT NULL LIMIT 1`, [input.firm_id]);
        if (!firm.results[0])
            throw new FirmValidationError('unknown_firm', 'No such audit firm.');
        const period = await this.tenant.query(`SELECT period_id FROM audit_periods WHERE period_id = ? AND workspace_id = ? LIMIT 1`, [input.audit_period_id, this.workspaceId]);
        if (!period.results[0])
            throw new FirmValidationError('unknown_period', 'No such audit period in this workspace.');
        const start = isoDate(input.engagement_start, 'engagement_start');
        const end = isoDate(input.engagement_end, 'engagement_end');
        if (Date.parse(end) <= Date.parse(start))
            throw new FirmValidationError('invalid_window', 'engagement_end must be after engagement_start.');
        const report = input.report_date ? isoDate(input.report_date, 'report_date') : null;
        const pending = await this.control.query(`SELECT invitation_id FROM firm_engagement_invitations WHERE workspace_id = ? AND firm_id = ? AND audit_period_id = ? AND status = 'pending' LIMIT 1`, [this.workspaceId, input.firm_id, input.audit_period_id]);
        if (pending.results[0])
            throw new FirmStateError('invitation_pending', 'That firm already has a pending invitation for this period.');
        // ADR-0067 §5: the family is gated by the program manifest × the firm's active authorization;
        // V102-7-5 M1 / ADR-0067 §6: a conflicted assessor is refused here too (and again at acceptance).
        const periodSummary = await firmPeriodSummary(this.tenant, this.workspaceId, input.audit_period_id);
        const family = await resolveAssessorFamily(this.control, { firmId: input.firm_id, programKey: periodSummary?.program ?? null, requested: input.assessor_firm_type ?? null });
        if (family) {
            await assertPreviewFamilyAllowed(input.previewFamilyGate, this.workspaceId, family);
            await assertNoOpenEngagementOfFamily(this.control, { family, workspaceId: this.workspaceId, firmId: input.firm_id, periodId: input.audit_period_id, now: this.now() });
            await assertAssessorIndependence(this.control, this.tenant, { family, workspaceId: this.workspaceId, firmId: input.firm_id, periodId: input.audit_period_id, programKey: periodSummary?.program ?? null, actor: this.userId, point: 'invitation', now: this.now() });
        }
        const id = generateRandomId('fei');
        await this.control.execute(`INSERT INTO firm_engagement_invitations (invitation_id, workspace_id, firm_id, audit_period_id, engagement_start, engagement_end, report_date, invited_by, invited_at, assessor_firm_type)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [id, this.workspaceId, input.firm_id, input.audit_period_id, start, end, report, this.userId, this.now().toISOString(), family]);
        return (await this.control.query(`SELECT * FROM firm_engagement_invitations WHERE invitation_id = ? AND workspace_id = ?`, [id, this.workspaceId])).results[0];
    }
    async withdrawInvitation(invitationId) {
        this.requireAdmin();
        const r = await this.control.execute(`UPDATE firm_engagement_invitations SET status = 'withdrawn', decided_by = ?, decided_at = ? WHERE invitation_id = ? AND workspace_id = ? AND status = 'pending'`, [this.userId, this.now().toISOString(), invitationId, this.workspaceId]);
        if ((r.meta?.changes ?? 0) === 0)
            throw new FirmNotFoundError();
    }
    /**
     * The client-visible tier of one engagement (ADR-0047 §2) — what "Preview as
     * auditor" renders. Allow-listed fields only; no roster, no working papers,
     * no drafts, no review notes, no sign-offs, no opinion.
     */
    async clientTierView(engagementId) {
        const e = await this.engagement(engagementId);
        return {
            engagement: {
                engagement_id: e.engagement_id,
                firm_name: e.assessor_firm,
                audit_period_id: e.audit_period_id,
                engagement_start: e.engagement_start,
                engagement_end: e.engagement_end,
                report_date: e.report_date,
                client_access: e.revoked_at ? 'revoked' : 'active',
            },
            report_status: await loadReportStatus(this.tenant, e.engagement_id),
            requests: await loadPbc(this.tenant, e.engagement_id, 'client', e.assessor_firm, undefined, this.control),
            populations: await clientPopulations(this.tenant, e),
            exceptions: await clientExceptions(this.tenant, e, () => this.firmKey(e.firm_id)),
            section4: await section4State(this.tenant, e),
            documents: await clientReportDocuments(this.tenant, e),
        };
    }
    // ---- PBC: the client side (V98-6-3) -------------------------------------------------
    async openEngagement(engagementId) {
        const e = await this.engagement(engagementId);
        if (e.revoked_at)
            throw new FirmForbiddenError('engagement_revoked', 'Client access to this engagement was revoked.');
        return e;
    }
    /** Owner/admin: who on the client side owns a request (an active member, never a roster identity). */
    async setPbcOwner(engagementId, requestId, ownerUserId) {
        this.requireAdmin();
        const e = await this.openEngagement(engagementId);
        const row = await loadPbcRow(this.tenant, e.engagement_id, requestId);
        if (row.status === 'accepted' || row.status === 'withdrawn')
            throw new FirmStateError('request_final', 'This request is final.');
        const m = await this.control.query(`SELECT 1 AS x FROM memberships WHERE workspace_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [this.workspaceId, ownerUserId]);
        if (!m.results[0])
            throw new FirmValidationError('not_member', 'The owner must be an active member of this workspace.');
        if (await isRosterIdentity(this.control, ownerUserId, this.now()))
            throw new IndependenceRejectedError('assignment');
        await this.tenant.execute(`UPDATE pbc_requests SET owner_user_id = ? WHERE request_id = ? AND engagement_id = ? AND workspace_id = ?`, [ownerUserId, requestId, e.engagement_id, this.workspaceId]);
        return (await loadPbc(this.tenant, e.engagement_id, 'client', e.assessor_firm, requestId, this.control))[0];
    }
    /**
     * Submit evidence (the request's owner, or owner/admin). Every submission is
     * a NEW immutable version — a replacement never overwrites. Each item is
     * snapshotted with its system-of-record creation time; anything created
     * after the request carries the backfill flag.
     */
    async submitPbc(engagementId, requestId, input) {
        const e = await this.openEngagement(engagementId);
        const row = await loadPbcRow(this.tenant, e.engagement_id, requestId);
        if (row.owner_user_id !== this.userId && this.role !== 'owner' && this.role !== 'admin') {
            throw new FirmForbiddenError('pbc_owner_required', 'Only the request’s owner, or an owner/admin, can submit.');
        }
        if (row.status !== 'requested' && row.status !== 'returned') {
            throw new FirmStateError('not_open', row.status === 'submitted' ? 'Already submitted — wait for the auditor or a return.' : 'This request is final.');
        }
        const raw = Array.isArray(input.items) ? input.items : [];
        if (raw.length === 0 || raw.length > 50)
            throw new FirmValidationError('invalid_items', 'Submit between 1 and 50 evidence items.');
        const requestedAt = Date.parse(row.requested_at);
        const items = [];
        for (const it of raw) {
            const id = String(it?.id ?? '');
            if (it?.kind === 'evidence_artifact') {
                const a = (await this.tenant.query(`SELECT ea.artifact_id, ea.title, ea.type, ea.artifact_ref_kind, ea.version_label, ea.captured_at, ea.uploaded_at, ea.updated_at
             FROM evidence_artifacts ea JOIN customer_profiles cp ON cp.profile_id = ea.profile_id
            WHERE ea.artifact_id = ? AND cp.workspace_id = ? LIMIT 1`, [id, this.workspaceId])).results[0];
                if (!a)
                    throw new FirmValidationError('unknown_evidence', `No evidence ${id} in this workspace.`);
                const created = a.captured_at ?? a.uploaded_at;
                items.push({
                    kind: 'evidence_artifact', ref_id: a.artifact_id, title: a.title, detail: [a.type, a.version_label].filter(Boolean).join(' · ') || null,
                    content_sha256: await sha256Hex(canonicalJson({ artifact_id: a.artifact_id, kind: a.artifact_ref_kind, version_label: a.version_label, updated_at: a.updated_at })),
                    source_created_at: created, backfilled: Date.parse(created) > requestedAt,
                });
            }
            else if (it?.kind === 'control_check') {
                const k = (await this.tenant.query(`SELECT check_id, control_id, status, evidence_hash, observed_at FROM control_checks WHERE check_id = ? AND workspace_id = ? LIMIT 1`, [id, this.workspaceId])).results[0];
                if (!k)
                    throw new FirmValidationError('unknown_evidence', `No check ${id} in this workspace.`);
                items.push({
                    kind: 'control_check', ref_id: k.check_id, title: `${k.control_id} check`, detail: k.status,
                    content_sha256: k.evidence_hash, source_created_at: k.observed_at, backfilled: Date.parse(k.observed_at) > requestedAt,
                });
            }
            else {
                throw new FirmValidationError('invalid_items', 'Each item is an evidence_artifact or a control_check.');
            }
        }
        const version = row.latest_version + 1;
        const at = this.now().toISOString();
        const itemsJson = JSON.stringify(items);
        const backfill = items.some((i) => i.backfilled) ? 1 : 0;
        await this.tenant.batch([
            { sql: `INSERT INTO pbc_evidence_versions (version_id, workspace_id, engagement_id, firm_id, request_id, version, items_json, items_sha256, backfill_flag, note, submitted_by, submitted_at)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [generateRandomId('pev'), this.workspaceId, e.engagement_id, e.firm_id, requestId, version, itemsJson, await sha256Hex(itemsJson), backfill,
                    input.note ? cleanText(input.note, 'note', 1, 2000) : null, this.userId, at] },
            { sql: `UPDATE pbc_requests SET status = 'submitted', latest_version = ?, submitted_at = ? WHERE request_id = ? AND workspace_id = ? AND status IN ('requested', 'returned') AND latest_version = ?`,
                params: [version, at, requestId, this.workspaceId, row.latest_version] },
        ]);
        await appendFirmChain(this.control, { engagement_id: e.engagement_id, firm_id: e.firm_id, action: 'pbc_submitted', tier: 'client', actor_user_id: this.userId,
            subject_kind: 'pbc_request', subject_id: requestId, detail: { version, backfill: backfill === 1 }, at });
        return (await loadPbc(this.tenant, e.engagement_id, 'client', e.assessor_firm, requestId, this.control))[0];
    }
    async postPbcMessage(engagementId, requestId, body) {
        const e = await this.openEngagement(engagementId);
        const row = await loadPbcRow(this.tenant, e.engagement_id, requestId);
        if (this.role === 'viewer' && row.owner_user_id !== this.userId) {
            throw new FirmForbiddenError('viewer_read_only', 'Viewers read the thread; the request’s owner or an editor replies.');
        }
        await insertPbcMessage(this.tenant, { workspace_id: this.workspaceId, engagement_id: e.engagement_id, firm_id: e.firm_id }, requestId, 'client', this.userId, body, this.now().toISOString());
        return (await loadPbc(this.tenant, e.engagement_id, 'client', e.assessor_firm, requestId, this.control))[0];
    }
    // ---- population provenance: the client side (V98-6-4) -------------------------------
    /**
     * Owner/admin/editor: record where a population came from. Append-only; the
     * latest row is current. The independent-source label is derived here, never
     * chosen by the caller.
     */
    async recordProvenance(engagementId, populationId, input) {
        if (this.role === 'viewer' || this.role === 'advisor')
            throw new FirmForbiddenError('editor_required', 'Requires role: owner, admin, editor.');
        const e = await this.openEngagement(engagementId);
        const pop = (await engagementPopulations(this.tenant, this.workspaceId, e.audit_period_id)).find((p) => p.population_id === populationId);
        if (!pop)
            throw new FirmNotFoundError();
        const methods = ['system_query', 'system_report', 'connector', 'client_prepared'];
        if (!methods.includes(input.extraction_method))
            throw new FirmValidationError('invalid_method', 'extraction_method must be system_query, system_report, connector or client_prepared.');
        const method = input.extraction_method;
        const adjusted = input.adjusted === true;
        const note = adjusted ? cleanText(input.adjustment_note, 'adjustment_note', 5, 2000) : null;
        const id = generateRandomId('ppv');
        // The latest row is current and ids are random, so a second write in the same millisecond must still sort
        // last: recorded_at is at least 1 ms after the population's latest row.
        const latest = (await this.tenant.query(`SELECT MAX(recorded_at) AS recorded_at FROM population_provenance WHERE workspace_id = ? AND engagement_id = ? AND population_id = ?`, [this.workspaceId, e.engagement_id, populationId])).results[0]?.recorded_at;
        const recordedAt = new Date(Math.max(this.now().getTime(), latest ? Date.parse(latest) + 1 : 0)).toISOString();
        await this.tenant.execute(`INSERT INTO population_provenance (provenance_id, workspace_id, engagement_id, firm_id, population_id, source_system, extraction_method, query_or_report,
         extracted_by, extracted_at, adjusted, adjustment_note, independence, recorded_by, recorded_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [id, this.workspaceId, e.engagement_id, e.firm_id, populationId, cleanText(input.source_system, 'source_system', 2, 200), method,
            input.query_or_report ? cleanText(input.query_or_report, 'query_or_report', 1, 5000) : null, input.extracted_by ? cleanText(input.extracted_by, 'extracted_by', 1, 200) : this.userId,
            isoDate(input.extracted_at, 'extracted_at'), adjusted ? 1 : 0, note, independenceOf({ extraction_method: method, adjusted }), this.userId, recordedAt]);
        await appendFirmChain(this.control, { engagement_id: e.engagement_id, firm_id: e.firm_id, action: 'provenance_recorded', tier: 'client', actor_user_id: this.userId,
            subject_kind: 'population', subject_id: populationId, detail: { adjusted }, at: this.now().toISOString() });
        return (await clientPopulations(this.tenant, e)).find((p) => p.population_id === populationId);
    }
    // ---- management responses (V98-6-6) -------------------------------------------------
    /**
     * Owner/admin: management's response to an ISSUED exception, bound to the
     * version it answers (must be the current issued version) and to the
     * current approved system description. Always unaudited. A new response
     * supersedes the previous one.
     */
    async respondToException(engagementId, exceptionId, input) {
        this.requireAdmin();
        const e = await this.openEngagement(engagementId);
        const x = (await this.tenant.query(`SELECT status, issued_version FROM audit_exceptions WHERE exception_id = ? AND engagement_id = ? LIMIT 1`, [exceptionId, e.engagement_id])).results[0];
        if (!x || x.status !== 'issued')
            throw new FirmNotFoundError();
        if (Number(input.exception_version) !== Number(x.issued_version)) {
            throw new FirmStateError('stale_version', `Respond to the current version (v${x.issued_version}).`);
        }
        const sd = await currentSystemDescription(this.tenant, this.workspaceId, e.audit_period_id);
        const at = this.now().toISOString();
        const id = generateRandomId('mrs');
        await this.tenant.batch([
            { sql: `UPDATE management_responses SET superseded_at = ? WHERE exception_id = ? AND workspace_id = ? AND superseded_at IS NULL`, params: [at, exceptionId, this.workspaceId] },
            { sql: `INSERT INTO management_responses (response_id, workspace_id, engagement_id, firm_id, exception_id, exception_version, system_description_id, system_description_version, body, authored_by, authored_at)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [id, this.workspaceId, e.engagement_id, e.firm_id, exceptionId, Number(x.issued_version), sd?.description_id ?? null, sd?.version ?? null,
                    cleanText(input.body, 'body', 10, 10000), this.userId, at] },
        ]);
        await appendFirmChain(this.control, { engagement_id: e.engagement_id, firm_id: e.firm_id, action: 'management_response', tier: 'client', actor_user_id: this.userId,
            subject_kind: 'exception', subject_id: exceptionId, detail: { exception_version: Number(x.issued_version), system_description_version: sd?.version ?? null }, at });
        return (await clientExceptions(this.tenant, e, () => this.firmKey(e.firm_id))).find((c) => c.exception_id === exceptionId);
    }
    /** Section 4 lock state, for the client's report page. */
    async section4(engagementId) {
        return section4State(this.tenant, await this.engagement(engagementId));
    }
    keyCache = new Map();
    /** Issued exception wording is encrypted with the firm key; the client reads ISSUED versions only. */
    firmKey(firmId) {
        if (!this.keyCache.has(firmId))
            this.keyCache.set(firmId, loadFirmKey(this.control, this.kek, firmId));
        return this.keyCache.get(firmId);
    }
    // ---- report documents: the client side (V98-6-7) -------------------------------------
    async assertionFacts(e, systemName) {
        const p = await periodWindow(this.tenant, this.workspaceId, e.audit_period_id);
        if (!p)
            throw new FirmStateError('no_period', 'This engagement has no audit period.');
        const ws = (await this.control.query(`SELECT name FROM workspaces WHERE workspace_id = ? LIMIT 1`, [this.workspaceId])).results[0];
        const org = ws?.name ?? 'The Company';
        const subs = await subserviceCoverage(this.tenant, this.workspaceId, p);
        const cuecs = await this.tenant.query(`SELECT COUNT(*) AS n FROM cuecs WHERE workspace_id = ?`, [this.workspaceId]);
        return {
            service_organization: org, system_name: systemName?.trim() || `${org} Platform`, period_start: p.start, period_end: p.end, categories: ['security'],
            carve_outs: subs.filter((s) => s.method === 'carve_out').map((s) => ({ name: s.name, services: s.services })),
            inclusive: subs.filter((s) => s.method === 'inclusive').map((s) => ({ name: s.name, services: s.services })),
            has_cuecs: Number(cuecs.results[0]?.n ?? 0) > 0,
        };
    }
    /** A pre-filled draft of a client document from the AICPA-structured template (review pending). */
    async reportTemplate(engagementId, kind, opts = {}) {
        const e = await this.openEngagement(engagementId);
        if (!CLIENT_REPORT_DOCUMENT_KINDS.includes(kind))
            throw new FirmValidationError('invalid_kind', 'kind must be management_assertion, representation_letter or bridge_letter.');
        const facts = await this.assertionFacts(e, opts.system_name);
        const base = { kind: kind, template_version: TEMPLATE_VERSION, template_review_status: TEMPLATE_REVIEW_STATUS, document_date: null, covers_from: null, covers_through: null };
        if (kind === 'management_assertion')
            return { ...base, body: managementAssertionType2(facts) };
        if (kind === 'representation_letter') {
            if (!e.report_date)
                throw new FirmStateError('report_date_unset', 'The auditor hasn’t set the report date yet; the letter is dated to it.');
            return { ...base, document_date: e.report_date, body: representationLetter({ service_organization: facts.service_organization, system_name: facts.system_name, firm_name: e.assessor_firm, period_start: facts.period_start, period_end: facts.period_end, report_date: e.report_date }) };
        }
        const from = addDay(facts.period_end);
        const through = opts.covers_through ? isoDate(opts.covers_through, 'covers_through').slice(0, 10) : this.now().toISOString().slice(0, 10);
        return { ...base, covers_from: from, covers_through: through, body: bridgeLetter({ service_organization: facts.service_organization, system_name: facts.system_name, report_period_end: facts.period_end, covers_from: from, covers_through: through }) };
    }
    /**
     * Owner/admin: write (or replace) a client report document. Each save is a
     * new version; `submit` sends it to the auditor. The representation letter
     * is dated to the report date; a bridge letter starts the day after the
     * period ends.
     */
    async saveReportDocument(engagementId, input) {
        this.requireAdmin();
        const e = await this.openEngagement(engagementId);
        const tpl = await this.reportTemplate(engagementId, input.kind, { system_name: input.system_name, covers_through: input.covers_through });
        if (tpl.kind === 'bridge_letter' && tpl.covers_through < tpl.covers_from)
            throw new FirmValidationError('invalid_covers_through', 'A bridge letter covers days after the period end.');
        const body = input.body ? cleanText(input.body, 'body', 20, 50000) : tpl.body;
        const at = this.now().toISOString();
        const id = generateRandomId('rdc');
        await this.tenant.batch([
            { sql: `UPDATE report_documents SET status = 'superseded' WHERE engagement_id = ? AND kind = ? AND status IN ('draft', 'submitted', 'returned')`, params: [e.engagement_id, tpl.kind] },
            { sql: `INSERT INTO report_documents (doc_id, workspace_id, engagement_id, firm_id, kind, version, status, body, template_id, template_version, document_date, covers_from, covers_through, created_by, created_at)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [id, this.workspaceId, e.engagement_id, e.firm_id, tpl.kind, await nextDocVersion(this.tenant, e.engagement_id, tpl.kind), input.submit ? 'submitted' : 'draft', body,
                    tpl.kind, tpl.template_version, tpl.document_date, tpl.covers_from, tpl.covers_through, this.userId, at] },
        ]);
        await appendFirmChain(this.control, { engagement_id: e.engagement_id, firm_id: e.firm_id, action: input.submit ? 'report_document_submitted' : 'report_document_saved', tier: 'client',
            actor_user_id: this.userId, subject_kind: 'report_document', subject_id: id, detail: { kind: tpl.kind }, at });
        return (await clientReportDocuments(this.tenant, e)).find((d) => d.doc_id === id);
    }
    // ---- advisory engagements (ADR-0067 §6; any member reads, owner/admin records) ------
    async listAdvisoryEngagements() {
        const r = await this.control.query(`SELECT a.advisory_id, a.firm_id, f.name AS firm_name, a.program_key, a.profile_id, a.description, a.started_at, a.ended_at, a.recorded_by, a.recorded_at,
              a.ended_recorded_at
         FROM advisory_engagements a JOIN audit_firms f ON f.firm_id = a.firm_id
        WHERE a.workspace_id = ? ORDER BY a.started_at DESC, a.advisory_id`, [this.workspaceId]);
        return r.results;
    }
    /** Record that a firm advised this workspace (optionally one offering). The independence check reads it. */
    async recordAdvisoryEngagement(input) {
        this.requireAdmin();
        const firm = await this.control.query(`SELECT firm_id FROM audit_firms WHERE firm_id = ? LIMIT 1`, [input.firm_id]);
        if (!firm.results[0])
            throw new FirmValidationError('unknown_firm', 'No such audit firm.');
        const programKey = input.program_key ? String(input.program_key) : null;
        if (programKey && !getProgramManifest(programKey))
            throw new FirmValidationError('unknown_program', 'No such program.');
        let profileId = null;
        if (input.profile_id) {
            const p = await this.tenant.query(`SELECT program FROM certification_profiles WHERE profile_id = ? AND workspace_id = ? LIMIT 1`, [input.profile_id, this.workspaceId]);
            if (!p.results[0])
                throw new FirmNotFoundError();
            if (programKey && p.results[0].program !== programKey)
                throw new FirmValidationError('profile_program_mismatch', 'The profile is for another program.');
            profileId = String(input.profile_id);
        }
        const started = isoDate(input.started_at, 'started_at');
        const ended = input.ended_at ? isoDate(input.ended_at, 'ended_at') : null;
        if (ended && Date.parse(ended) < Date.parse(started))
            throw new FirmValidationError('invalid_window', 'ended_at must not be before started_at.');
        const description = cleanText(input.description, 'description', 5, 2000);
        const id = generateRandomId('adv');
        const recordedAt = this.now().toISOString();
        await this.control.execute(`INSERT INTO advisory_engagements (advisory_id, firm_id, workspace_id, program_key, profile_id, description, started_at, ended_at, ended_recorded_by, recorded_by, recorded_at, ended_recorded_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [id, input.firm_id, this.workspaceId, programKey, profileId, description, started, ended, ended ? this.userId : null, this.userId, recordedAt, ended ? recordedAt : null]);
        return (await this.listAdvisoryEngagements()).find((a) => a.advisory_id === id);
    }
    async endAdvisoryEngagement(advisoryId, endedAt) {
        this.requireAdmin();
        const ended = isoDate(endedAt, 'ended_at');
        const row = (await this.listAdvisoryEngagements()).find((a) => a.advisory_id === advisoryId);
        if (!row)
            throw new FirmNotFoundError();
        if (row.ended_at)
            throw new FirmStateError('already_ended', 'This advisory engagement already ended.');
        if (Date.parse(ended) < Date.parse(row.started_at))
            throw new FirmValidationError('invalid_window', 'ended_at must not be before started_at.');
        // T6-M2: the end keeps when it was recorded (refuse-mode independence counts from max(end, recorded)), and is logged.
        const recordedAt = this.now().toISOString();
        const res = await this.control.execute(`UPDATE advisory_engagements SET ended_at = ?, ended_recorded_by = ?, ended_recorded_at = ? WHERE advisory_id = ? AND workspace_id = ? AND ended_at IS NULL`, [ended, this.userId, recordedAt, advisoryId, this.workspaceId]);
        if ((res.meta?.changes ?? 0) === 0)
            throw new FirmStateError('already_ended', 'This advisory engagement already ended.');
        await recordSodEvent(this.tenant, {
            workspace_id: this.workspaceId, transition: 'config', event: 'advisory_engagement_ended', actor_user_id: this.userId,
            record_kind: 'advisory_engagement', record_id: advisoryId, created_at: recordedAt,
            detail: { firm_id: row.firm_id, program_key: row.program_key, ended_at: ended, ended_recorded_at: recordedAt,
                backdated_days: Math.max(0, Math.floor((Date.parse(recordedAt) - Date.parse(ended)) / 86_400_000)) },
        });
        return (await this.listAdvisoryEngagements()).find((a) => a.advisory_id === advisoryId);
    }
    // ---- assessor reports, client side (ADR-0067 §7): signed only, read-only -------------
    /** V106-6-4: the certification body's signed decisions on this engagement, read-only; no firm people (ADR-0047 §2). */
    async listCertificationDecisions(engagementId) {
        const e = await this.openEngagement(engagementId);
        const r = await this.tenant.query(`SELECT * FROM certification_decisions WHERE workspace_id = ? AND engagement_id = ? ORDER BY decided_at, decision_id`, [this.workspaceId, e.engagement_id]);
        return r.results.map(({ lead_signoff_by: _l, decided_by: _d, lead_signoff_id: _s, ...rest }) => rest);
    }
    async listAssessorReports(engagementId) {
        const e = await this.openEngagement(engagementId);
        const r = await this.tenant.query(`SELECT ${REPORT_COLS} FROM assessor_report_artifacts
        WHERE workspace_id = ? AND engagement_id = ? AND signed_off_at IS NOT NULL ORDER BY kind, version`, [this.workspaceId, e.engagement_id]);
        // The client sees the firm, never roster people (ADR-0047 §2).
        return r.results.map(toReportArtifact).map(({ uploaded_by: _u, signed_off_by: _s, ...rest }) => rest);
    }
    async openAssessorReport(engagementId, artifactId, get) {
        const e = await this.openEngagement(engagementId);
        const r = (await this.tenant.query(`SELECT filename, content_type, storage_key, content_sha256 FROM assessor_report_artifacts
        WHERE artifact_id = ? AND workspace_id = ? AND engagement_id = ? AND signed_off_at IS NOT NULL LIMIT 1`, [artifactId, this.workspaceId, e.engagement_id])).results[0];
        if (!r)
            throw new FirmNotFoundError();
        const bytes = await get(r.storage_key);
        if (!bytes || (await sha256Hex(bytes)) !== r.content_sha256)
            throw new FirmStateError('report_integrity', 'The stored report does not match its recorded hash.');
        return { filename: r.filename, content_type: r.content_type, bytes, content_sha256: r.content_sha256 };
    }
    /** Firm invitations and firm-tier engagements on this workspace (client-visible fields only). */
    async listEngagements() {
        const inv = await this.control.query(`SELECT i.*, f.name AS firm_name FROM firm_engagement_invitations i JOIN audit_firms f ON f.firm_id = i.firm_id
        WHERE i.workspace_id = ? ORDER BY i.invited_at DESC`, [this.workspaceId]);
        const eng = await this.control.query(`SELECT engagement_id, assessor_firm AS firm_name, audit_period_id, engagement_start, engagement_end, report_date, revoked_at, firm_id, firm_type, assessor_firm_type
         FROM assessor_engagements WHERE workspace_id = ? AND firm_id IS NOT NULL ORDER BY engagement_start DESC`, [this.workspaceId]);
        const engagements = [];
        for (const { firm_id, firm_type, ...e } of eng.results) {
            const subjectPeriod = isAssessorFirmType(e.assessor_firm_type) && ASSESSOR_FIRM_TYPE_SPECS[e.assessor_firm_type].independence.window === 'subject_period';
            const periodStart = subjectPeriod && this.tenant ? (await firmPeriodSummary(this.tenant, this.workspaceId, e.audit_period_id).catch(() => null))?.start ?? null : null;
            const flag = await engagementIndependenceFlag(this.control, { workspace_id: this.workspaceId, firm_id, firm_type, assessor_firm_type: e.assessor_firm_type }, this.now(), periodStart);
            engagements.push({ ...e, ...(flag ? { independence_flag: flag } : {}) });
        }
        return { invitations: inv.results, engagements };
    }
}
/**
 * V106-1-8 / V106-5-6: the client-visible firm facts of one period for the program screens —
 * the engaged firm's name and the latest report status per engagement (status word, the
 * ISSUED opinion + report date only; ADR-0078 §6 / ADR-0047 amendment). Nothing firm-only.
 */
export async function periodFirmFacts(tenant, control, workspaceId, periodId) {
    const engagements = (await control.query(`SELECT e.engagement_id, COALESCE(f.name, e.assessor_firm) AS firm_name, e.revoked_at FROM assessor_engagements e LEFT JOIN audit_firms f ON f.firm_id = e.firm_id
      WHERE e.workspace_id = ? AND e.audit_period_id = ? ORDER BY e.invited_at DESC`, [workspaceId, periodId]).catch(() => ({ results: [] }))).results;
    let issued = null;
    let draft = null;
    for (const e of engagements) {
        const ev = (await tenant.query(`SELECT status, set_at, opinion_type, report_date FROM report_status_events WHERE workspace_id = ? AND engagement_id = ? ORDER BY set_at DESC, status_event_id DESC LIMIT 1`, [workspaceId, e.engagement_id])).results[0];
        if (ev?.status === 'issued' && (!issued || ev.set_at > issued.set_at))
            issued = { opinion_type: ev.opinion_type, report_date: ev.report_date, firm_name: e.firm_name, set_at: ev.set_at };
        if (ev?.status === 'draft_sent' && (!draft || ev.set_at > draft.set_at))
            draft = { firm_name: e.firm_name, set_at: ev.set_at };
    }
    const active = engagements.find((e) => !e.revoked_at) ?? null;
    return { firm_name: active?.firm_name ?? null, issued, draft_sent: issued ? null : draft };
}
//# sourceMappingURL=firm-tier-repository.js.map